/**
 * The two-step checkout wizard (WP-18, PRD §1.5.2). One instance per `[data-anudan-checkout]`
 * container — the modal and the full-page lane share this exact class, per the dual-mode
 * requirement; only the container's own size and the trigger wiring around it differ.
 */

import * as api from './api.js';
import { buildStitch, updateStitch, setStepTotal, frequencyBadge } from './progress-stitch.js';
import { buildPhoneField } from './phone-field.js';
import { keepAlignedToScreenPixels } from './pixel-align.js';
import { createFocusTrap } from './focus-trap.js';
import { loadGateway } from './gateway-loader.js';
import { stripeElementsOptions, paypalButtonStyle, paymentElementOptions } from '../shared/brand.js';
import { attachEmailSuggestion, COMMON_DOMAINS } from '../shared/email-suggest.js';
import { getToken as getTurnstileToken, spendToken as spendTurnstileToken, lastError as turnstileLastError } from './turnstile.js';
import { digitsOnly } from './digits.js';
import { saveDraft, loadDraft, clearDraft } from './draft-storage.js';
import { saveGatewaySelection, loadGatewaySelection } from './gateway-session.js';
import { createWalletController, splitPayerName } from './wallet.js';
import { trackBeginCheckout, trackAddPaymentInfo } from './analytics.js';
import { declinedRecently, rememberDecline } from './monthly-prompt.js';
import { EntriesUi } from './entries.js';
import { ItemsUi } from './items.js';
import { MonthsUi } from './months.js';

const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

/**
 * D1 (0.9.41-rc3): marks the "Monthly" badge's words inside the thank-you sentence; never shown or read out.
 */
const BADGE_MARK = '\u0001';

const TOTAL_STEPS = 2;

/**
 * Q1 (0.9.40): the steps the donor sees. The wizard's own second step holds the details and, once the gift is made, the
 * payment; the payment appearing is the third ({@see Wizard#displayStep}).
 */
const SHOWN_STEPS = 3;

/** How long the "few seconds" wording stays before the longer-wait message replaces it. */
const CONFIRMING_PATIENCE_MS = 90000;

/** How long Stripe's own confirmation may go unanswered before the donor is told (0.9.1). */
const STRIPE_CONFIRM_PATIENCE_MS = 45000;

/**
 * How long each step after Give may take before the checkout stops waiting, tells the donor and lets Give be pressed again
 * (0.9.38, GATE-PROGRESS.md Check 18). Each is that step's own slowest legitimate path, with room:
 * - `security_check` 20 s: the script's own 8-second limit, then a widget that solves without asking the donor anything. While
 *   Turnstile asks the donor to act, the limit waits (its before- and after-interactive callbacks).
 * - `connection_check` 10 s: the "does the site answer" request gives up after 4 s.
 * - `intent` 75 s: the server checks the security token (5 s limit) and calls the payment provider, 20 s a call for Stripe and
 *   for PayPal, up to three calls for a monthly gift (65 s).
 * - `payment_script` 15 s: the provider's script gives up after 8 s.
 * - `payment_fields` 15 s: mounting the fields is synchronous once the script is there.
 */
const BUSY_LIMITS_MS = {
	security_check: 20000,
	connection_check: 10000,
	intent: 75000,
	payment_script: 15000,
	payment_fields: 15000,
};

/**
 * 0.9.41-rc1: how long Continue on the gift step waits for the server's total before it says "Still working…"
 * ({@see Wizard.whenQuoteSettled}). It goes on waiting, unavailable, until the total arrives or a message says why it cannot; the
 * details step never opens on a total the server has not given (decisions log, 2026-10-29).
 */
const QUOTE_WAIT_MS = 4000;

/**
 * A Stripe error's code for the event log: its decline or error code, else its type. A short slug
 * only, never Stripe's sentence, which the donor sees and the server does not need.
 *
 * @param {{type?: string, code?: string, decline_code?: string}} error
 * @return {string}
 */
function stripeErrorCode( error ) {
	const code = error.decline_code || error.code || error.type || 'unknown';

	return /^[a-z0-9_]{1,60}$/.test( code ) ? code : 'unknown';
}

let wizardInstances = 0;

/**
 * Generates a value for Turnstile's `cdata` binding — client-generated because, per
 * `05-API-CONTRACT.md` §5.4, "the widget renders before this request exists": nothing on the
 * server can hand this out in time.
 *
 * **This value is a bearer secret, not a label**, the same way it always has been — even though
 * WP-24c narrowed what it actually gates. It used to be the same value `Rest\CheckoutController
 * ::status_body()` checked a later poll against; as of WP-24c that check is against a
 * server-generated token delivered in an `HttpOnly` cookie instead (`intent()`'s own response),
 * which this module never reads. What this function's output still gates is Turnstile's own
 * verification (`Security\Turnstile::verify()` compares it against Cloudflare's echoed `cdata`)
 * — guessable here would let a caller forge that binding, which is why the unguessability
 * requirement below is unchanged even though the *consequence* of a guess changed.
 *
 * **`crypto.randomUUID()` first** (122 bits, secure-context only), **`crypto.getRandomValues()`
 * as the fallback** (128 bits from 16 raw bytes) — `getRandomValues()` is available in
 * contexts `randomUUID()` is not (a plain-HTTP admin preview, an older browser that shipped
 * WebCrypto's random source before the convenience wrapper), so it is a real fallback rather
 * than a formality.
 *
 * **No third tier.** Issue 0014 recorded what the previous fallback
 * (`` `${Date.now()}-${Math.random().toString(36)}` ``) actually was: `Math.random()` is not
 * cryptographically secure and its internal state is recoverable from a handful of observed
 * outputs, so an attacker who starts several checkouts to sample it could predict a future
 * session id and use it — together with a leaked `donation_uid`, which travels in URLs far
 * more easily than a session id should — to pull another donor's PII through the identical,
 * otherwise-correct `status()` endpoint. There is no weaker generator worth falling back to;
 * a browser with neither WebCrypto function does not get a session id, and per this function's
 * only caller, does not get a checkout.
 *
 * @return {string|null} Null when no cryptographically secure generator is available.
 */
function generateSessionId() {
	if ( window.crypto && 'function' === typeof window.crypto.randomUUID ) {
		return window.crypto.randomUUID();
	}

	if ( window.crypto && 'function' === typeof window.crypto.getRandomValues ) {
		const bytes = new Uint8Array( 16 );

		window.crypto.getRandomValues( bytes );

		/*
		 * Formatted as an RFC 4122 version-4 UUID, not bare hex: the same value is the intent's idempotency
		 * key, and `Donations\Idempotency::validate()` refuses anything that is not a canonical UUIDv4. The
		 * bare 32-hex form this used to return was refused by the server, so on every plain-HTTP page and
		 * every browser without `randomUUID()` no gift could be started.
		 */
		bytes[ 6 ] = ( bytes[ 6 ] & 0x0f ) | 0x40;
		bytes[ 8 ] = ( bytes[ 8 ] & 0x3f ) | 0x80;

		const hex = Array.from( bytes, ( byte ) => byte.toString( 16 ).padStart( 2, '0' ) ).join( '' );

		return `${ hex.slice( 0, 8 ) }-${ hex.slice( 8, 12 ) }-${ hex.slice( 12, 16 ) }-${ hex.slice( 16, 20 ) }-${ hex.slice( 20 ) }`;
	}

	return null;
}

/**
 * Whether a Stripe confirmation error leaves it unknown whether the money moved: Stripe could not
 * be reached or failed on its own side, or the intent had already succeeded. A decline or a
 * validation error is a clear "no" (release gate 0.5.3, adversarial review round 11, LOW-1).
 *
 * @param {{type?: string, code?: string}} error
 * @return {boolean}
 */
function isUnclearStripeError( error ) {
	return 'api_connection_error' === error.type
		|| 'api_error' === error.type
		|| 'payment_intent_unexpected_state' === error.code;
}

/**
 * The nearest ancestor that scrolls its own content (the checkout dialog), or null when the page
 * itself scrolls.
 *
 * @param {HTMLElement} element
 * @return {HTMLElement|null}
 */
function scrollingAncestor( element ) {
	for ( let node = element.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement ) {
		const overflow = window.getComputedStyle( node ).overflowY;

		if ( ( 'auto' === overflow || 'scroll' === overflow ) && node.scrollHeight > node.clientHeight ) {
			return node;
		}
	}

	return null;
}

export class Wizard {
	/**
	 * @param {HTMLElement} container The `[data-anudan-checkout]` element.
	 */
	constructor( container ) {
		this.container = container;
		this.fundId = Number( container.getAttribute( 'data-anudan-fund-id' ) || 0 );
		this.mode = container.getAttribute( 'data-anudan-mode' ) || 'page';
		// 0.9.0 tablet mode: a shared device at an event. Nothing a donor types is kept on it.
		this.tablet = null !== container.closest( '[data-anudan-tablet]' );
		// 0.9.0 gift cart: several funds, one payment. No drafts, no wallets, no monthly offer.
		this.cart = container.hasAttribute( 'data-anudan-cart' );
		this.cartFundIds = container.getAttribute( 'data-anudan-cart-funds' ) || '';
		this.cartLines = [];
		// Turnstile's own cdata binding only (WP-24c) — generated here because the widget
		// renders before intent() exists to hand out anything server-side. It is sent to
		// intent() in the POST body and never used for anything after that: the *later*
		// polling credential (status()/confirm()/the receipt link) is the HttpOnly cookie
		// intent()'s own response sets, which this class never reads.
		this.sessionId = null;
		this.form = null;
		this.step = 1;
		this.selectedTierId = null;
		this.customAmount = null;
		// 0.9.12: a gift moved from PayPal, from its personal link, and the intent that scheduled it.
		this.restart = null;
		this.scheduledIntent = null;
		// WP-37 (M-MONTHS): the donor's own chosen period keys, in catalog order — never the
		// order they were clicked in, so the chip row and the frozen donation always agree
		// (`Funds\Modules\MonthsModule::clean_selection()`'s own reasoning, applied client-side).
		this.selectedPeriods = [];
		// WP-42 (M-ANON): whether the donor asked to be listed anonymously — not sensitive, so
		// (unlike the M-DEDICATE fields just below) this is persisted to the draft.
		this.isAnonymous = false;
		// WP-42 (M-DEDICATE): the donor's own tribute, if any — `dedicateFields`, the control
		// getters `collectDedicateAnswer()` reads, the same shape `this.customFields` already
		// uses. Deliberately never written to the draft (`saveDraftNow()`'s own docblock): a
		// dedication names a third party, the identical reasoning that already keeps
		// `collectFieldAnswers()`'s own M-SERVICE answers out of `localStorage`.
		this.dedicateFields = {};
		this.coverFees = false;
		this.fields = {};
		// WP-38: the fund's own custom-field controls, as value getters — see `buildField()`.
		this.customFields = {};
		// Unique per instance, so a modal and an inline form for the same fund on one page never
		// share an element id.
		this.idPrefix = `anudan-checkout-${ ++wizardInstances }`;
		this.phoneField = null;
		this.quoteToken = 0;
		// Item 3 after rc5: counts every change but the fee box's, so the last answer's figures for the box are known to be current.
		this.amountToken = 0;
		this.expressWallet = null;
		this.paymentWallet = null;
		this.lastQuote = null;
		// WP-44: the donor's own choice among `this.form.gateways`, when there is more than one —
		// null means "not chosen yet, or only one gateway exists," and `currentGateway()` is the
		// one place that turns either state into the id every gateway-bearing request actually
		// sends.
		this.selectedGateway = null;
	}

	/**
	 * Fetches the fund's form schema and builds the wizard's DOM. Renders nothing, and leaves
	 * the container's `noscript` fallback in place, if the fetch fails — a checkout that cannot
	 * reach its own site should not render a form with nothing behind its buttons.
	 *
	 * @return {Promise<void>}
	 */
	async init() {
		this.sessionId = generateSessionId();

		if ( null === this.sessionId ) {
			/*
			 * Fail closed (issue 0014): no cryptographically secure generator, no session id,
			 * no checkout. The `noscript` fallback stays in place, the same as the fetch-failed
			 * path just below — a donor with a browser this old sees "enable JavaScript or use
			 * another browser" rather than a form whose PII-disclosure gate cannot actually
			 * gate anything.
			 */
			this.showLoadFailure(
				window.wp && window.wp.i18n
					? window.wp.i18n.__( 'This browser cannot take a donation securely. Please update it or try another browser.', 'anudan' )
					: 'This browser cannot take a donation securely. Please update it or try another browser.'
			);

			return;
		}

		// R2-15 (rc2): the fund settings screen's preview prints the schema of the unsaved settings; it is never fetched there.
		const previewForm = window.AnudanEngine && window.AnudanEngine.config && window.AnudanEngine.config.previewForm;
		const response = previewForm && ! this.cart
			? { ok: true, status: 200, body: previewForm }
			: ( this.cart ? await api.getCartForm( this.cartFundIds ) : await api.getForm( this.fundId ) );

		if ( ! response.ok || ! response.body ) {
			this.showLoadFailure( api.describeFailure(
				response,
				window.wp && window.wp.i18n
					? window.wp.i18n.__( 'The donation form could not be loaded. Please reload the page. If this keeps happening, please contact us.', 'anudan' )
					: 'The donation form could not be loaded. Please reload the page. If this keeps happening, please contact us.'
			) );

			return;
		}

		this.form = response.body;
		this.render();
		// R2-20 (rc2): the phone's button row pinned to the foot of the screen while the form is in view.
		this.initDock();
		this.restoreDraft();
		this.applyPrefill();
		this.updateSummary();
		await this.applyRestartLink();

		/*
		 * R2-03 (rc2): no wallet button outside the payment step. The express button on the gift step and the one under the
		 * security check (round 2's iPhone video: an Apple Pay button on Your details, and "Pay securely with Link") were
		 * Stripe's older Payment Request Button; Apple Pay and Google Pay are now only inside the card form on the payment step
		 * (paymentElementOptions()). `initWallets()` is no longer started and neither slot is built.
		 */
	}

	/**
	 * 0.9.15: a link from a giving widget (`[anudan_quick_give]`, `[anudan_monthly_ask]`) chooses the amount
	 * (`?anudan_amount=`, in minor units) and the frequency (`?anudan_frequency=`). Only a choice: nothing is
	 * charged, the donor can change both, and the server checks the amount as for any other gift. A value the
	 * fund does not offer is ignored, and a restart link or a cart never takes one.
	 *
	 * @return {void}
	 */
	applyPrefill() {
		let params;

		try {
			params = new URLSearchParams( window.location.search );
		} catch ( error ) {
			return;
		}

		if ( this.cart || params.get( 'anudan_restart' ) ) {
			return;
		}

		const frequencyValue = params.get( 'anudan_frequency' ) || '';

		if ( frequencyValue && this.frequencyGroup && ( this.form.frequencies || [] ).includes( frequencyValue ) ) {
			this.frequencyGroup.querySelectorAll( 'input[type="radio"]' ).forEach( ( input ) => {
				input.checked = input.value === frequencyValue;
			} );
		}

		const amountValue = params.get( 'anudan_amount' ) || '';

		if ( ! /^[1-9][0-9]{0,9}$/.test( amountValue ) ) {
			return;
		}

		const amount = Number( amountValue );
		const tier = ( this.form.amounts || [] ).find( ( candidate ) => candidate.amount === amount );

		if ( tier && this.amountsGroup ) {
			this.amountsGroup.querySelectorAll( 'input[type="radio"]' ).forEach( ( input ) => {
				input.checked = String( tier.id ) === input.value;
			} );
			this.selectedTierId = tier.id;
			this.customAmount = null;

			if ( this.customAmountInput ) {
				this.customAmountInput.value = '';
			}
		} else if ( this.form.allow_custom_amount && this.customAmountInput ) {
			if ( this.amountsGroup ) {
				this.amountsGroup.querySelectorAll( 'input[type="radio"]' ).forEach( ( input ) => {
					input.checked = false;
				} );
			}

			this.selectedTierId = null;
			this.customAmount = amount;
			this.customAmountInput.value = `${ Math.floor( amount / 100 ) }.${ String( amount % 100 ).padStart( 2, '0' ) }`;
		}
	}

	/**
	 * 0.9.12: a personal link moving a gift PayPal bills to this site (`?anudan_restart=…`). Fills in the
	 * gift's frequency and amount, and says when its first payment will be: the server decides the date from
	 * the invitation, and the donor chooses how to pay. A link that cannot be used is said plainly and the
	 * form stays an ordinary one.
	 *
	 * @return {Promise<void>}
	 */
	async applyRestartLink() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		let token = '';

		try {
			token = new URLSearchParams( window.location.search ).get( 'anudan_restart' ) || '';
		} catch ( error ) {
			token = '';
		}

		if ( this.cart || ! /^[a-f0-9]{64}$/i.test( token ) ) {
			return;
		}

		const response = await api.restart( token );
		const body = response.ok ? response.body : null;

		if ( ! body || Number( body.fund_id ) !== this.fundId || ! ( this.form.frequencies || [] ).includes( body.frequency ) ) {
			this.say( api.describeFailure( response, __( 'This link to move your gift has run out or was already used. Please contact us for a new one, or give as usual.' ) ) );

			return;
		}

		this.restart = { token, label: String( body.first_payment_label || '' ) };

		const frequency = this.frequencyGroup ? this.frequencyGroup.querySelector( `input[value="${ body.frequency }"]` ) : null;

		if ( frequency ) {
			frequency.checked = true;
			frequency.dispatchEvent( new Event( 'change', { bubbles: true } ) );
		}

		const tier = ( this.form.amounts || [] ).find( ( candidate ) => candidate.amount === body.gift_amount );

		if ( tier && this.amountsGroup ) {
			this.amountsGroup.querySelectorAll( 'input[type="radio"]' ).forEach( ( input ) => {
				input.checked = String( tier.id ) === input.value;
			} );
			this.selectedTierId = tier.id;
			this.customAmount = null;
		} else if ( this.customAmountInput && Number.isInteger( body.gift_amount ) ) {
			this.selectedTierId = null;
			this.customAmount = body.gift_amount;
			this.customAmountInput.value = `${ Math.floor( body.gift_amount / 100 ) }.${ String( body.gift_amount % 100 ).padStart( 2, '0' ) }`;
		}

		this.updateSummary();

		const note = document.createElement( 'p' );

		note.className = 'anudan-checkout__message anudan-checkout__restart-note';
		note.setAttribute( 'data-anudan-restart-note', '' );
		// 0.9.13 (OD-22): the PayPal gift takes its last payment first; this gift starts one period later.
		note.textContent = __( 'You are moving your gift from PayPal to us. Nothing is charged today. Your PayPal gift takes its last payment as usual on %1$s, then we cancel it, and the first payment here is on %2$s, so you are not charged twice.' )
			.replace( '%1$s', String( body.last_paypal_label || '' ) )
			.replace( '%2$s', this.restart.label );

		const title = this.stepOneEl ? this.stepOneEl.querySelector( '.anudan-checkout__step-title' ) : null;

		if ( title && title.parentNode ) {
			title.parentNode.insertBefore( note, title.nextSibling );
		}
	}

	/**
	 * 0.9.12: the end of a moved gift's checkout. Nothing was charged: the gift is set up for its first
	 * payment on the date, so there is nothing to wait for. The site is asked to check with the gateway, which
	 * is what the office's checklist goes by.
	 *
	 * @param {Object} body The intent's response.
	 * @return {void}
	 */
	showScheduled( body ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		this.paymentContainer.textContent = '';
		this.backButton.disabled = true;
		this.hidePaymentWallet();

		const done = document.createElement( 'div' );
		const heading = document.createElement( 'h2' );
		const text = document.createElement( 'p' );

		done.className = 'anudan-checkout__confirmation anudan-checkout__scheduled';
		done.setAttribute( 'data-anudan-scheduled', '' );
		heading.className = 'anudan-checkout__step-title';
		heading.textContent = __( 'Thank you. Your gift is set up.' );
		text.textContent = __( 'Nothing has been charged today. The first payment of %1$s will be taken on %2$s, and every payment after that is receipted by email. Your PayPal gift takes its last payment as usual before then, and we cancel it after that.' )
			.replace( '%1$s', this.formatMinorUnits( body.total_charged ) )
			.replace( '%2$s', String( body.first_payment_label || ( this.restart ? this.restart.label : '' ) ) );
		done.appendChild( heading );
		done.appendChild( text );
		this.paymentContainer.appendChild( done );
		this.liveRegion.textContent = heading.textContent + ' ' + text.textContent;

		if ( this.restart ) {
			api.restartConfirmed( this.restart.token );
		}
	}

	/**
	 * Says why there is no form, in the place the form would have been. The container's
	 * `noscript` fallback only helps a visitor with scripting off; a visitor whose request failed
	 * had scripting and got a blank space.
	 *
	 * @param {string} text
	 * @return {void}
	 */
	showLoadFailure( text ) {
		const message = document.createElement( 'p' );

		message.className = 'anudan-checkout__message';
		message.setAttribute( 'role', 'alert' );
		message.textContent = text;
		this.container.appendChild( message );
	}

	/**
	 * Detects wallet support for both slots the wallet lane can occupy.
	 *
	 * The express slot (top of step 1) exists in the DOM at all only when
	 * `form.wallets_eligible` — the server's own judgement (`Rest\WalletEligibility`,
	 * `07-CODE-STANDARDS.md`'s seam-consumption rule: this reads the flag, it does not
	 * recompute it from `form.fields`). The payment-step slot is attempted regardless, since
	 * PRD §1.5.1's fallback offers the wallet there even when the express lane is suppressed —
	 * it supplies only the payment method there, never identity.
	 *
	 * Both calls resolve to `null` immediately when `form.wallet` names no gateway, so a site
	 * with no wallet-capable gateway configured pays no cost for either check.
	 *
	 * @return {Promise<void>}
	 */
	async initWallets() {
		// A cart pays only through the payment step's own fields: a wallet sheet prices one fund.
		if ( this.cart ) {
			return;
		}

		if ( ! this.form.turnstile_ready ) {
			// Same rule `applyTurnstileAvailability()` enforces for the Give button: Turnstile
			// is solved for a wallet payment too (`handleWalletPaymentMethod()`'s own docblock),
			// so a misconfigured site must not offer a payment path that bypasses the check
			// this form has already disabled itself for.
			return;
		}

		if ( this.form.wallets_eligible && this.expressWalletSlot ) {
			const express = await createWalletController(
				this.form.wallet,
				this.form.currency,
				( event ) => this.handleWalletPaymentMethod( event, true )
			);

			if ( express ) {
				this.expressWallet = express;
				express.mount( this.expressWalletButton );
				this.expressWalletSlot.hidden = false;
				express.onSheetClosed = () => this.syncWalletTotal( express );
				this.priceNewWallet( express );
			}
		}

		if ( this.paymentWalletSlot ) {
			const payment = await createWalletController(
				this.form.wallet,
				this.form.currency,
				( event ) => this.handleWalletPaymentMethod( event, false )
			);

			if ( payment ) {
				this.paymentWallet = payment;
				payment.mount( this.paymentWalletSlot );
				// Still hidden if a payment got under way while the wallet was loading (round 7, LOW-2).
				this.paymentWalletSlot.hidden = !! this.walletSuppressed;
				payment.onSheetClosed = () => this.syncWalletTotal( payment );
				this.priceNewWallet( payment );
			}
		}
	}

	/**
	 * Pushes the last server-computed quote into a wallet sheet's own total. Never called with a
	 * client-derived figure (A-1) — `body` here is always what `POST /checkout/quote` returned.
	 *
	 * @param {{update: (amount: number, label: string) => void}} controller
	 * @return {void}
	 */
	priceNewWallet( controller ) {
		// A wallet that loaded after the latest quote was not there to be priced by it.
		if ( 'number' === typeof this.walletTotal ) {
			this.syncWalletTotal( controller );

			return;
		}

		if ( this.lastQuote && this.lastQuoteRequest && this.lastQuoteToken === this.quoteToken ) {
			this.quoteWallets( this.lastQuoteRequest, this.lastQuote, this.quoteToken );
		}
	}

	/**
	 * Shows the wallet total on one sheet.
	 *
	 * @param {{update: (amount: number, label: string) => void}} controller
	 * @return {void}
	 */
	syncWalletTotal( controller ) {
		if ( 'number' !== typeof this.walletTotal ) {
			return;
		}

		try {
			controller.update( this.walletTotal, this.form.fund.receipt_label || 'Donation' );
		} catch ( error ) {
			// The sheet is open and will not take a new total; it keeps the one it shows.
		}
	}

	/**
	 * Builds every element the wizard needs, once, from the fetched schema.
	 *
	 * @return {void}
	 */
	render() {
		this.container.innerHTML = '';

		if ( 'modal' === this.mode ) {
			const close = document.createElement( 'button' );

			close.type = 'button';
			close.className = 'anudan-checkout__close';
			close.setAttribute(
				'aria-label',
				window.wp && window.wp.i18n ? window.wp.i18n.__( 'Close', 'anudan' ) : 'Close'
			);
			close.textContent = '×';
			close.addEventListener( 'click', () => this.close() );

			this.container.appendChild( close );
		}

		const header = document.createElement( 'div' );

		header.className = 'anudan-checkout__header';

		this.stitch = buildStitch();

		this.liveRegion = document.createElement( 'div' );
		this.liveRegion.className = 'anudan-checkout__live-region';
		this.liveRegion.setAttribute( 'aria-live', 'polite' );
		this.liveRegion.setAttribute( 'role', 'status' );

		/*
		 * The visible twin of the live region. The live region is clipped to one pixel for screen
		 * readers, so text written only there is invisible to everybody else: on staging 0.5.4 a
		 * 429 from /checkout/intent left the donor looking at a Give button that did nothing.
		 * Every failure and notice goes through `say()`, which writes both. This element is hidden
		 * from assistive technology so the live region alone announces it.
		 */
		this.messageEl = document.createElement( 'p' );
		this.messageEl.className = 'anudan-checkout__message';
		this.messageEl.setAttribute( 'aria-hidden', 'true' );
		this.messageEl.hidden = true;

		header.appendChild( this.stitch );
		header.appendChild( this.liveRegion );
		header.appendChild( this.messageEl );

		// Which fund the gift goes to, unless the page around the form already says so (the
		// fund's own page). A donate button's modal, or a form embedded on any other page, never
		// named it (release gate 0.5.3, stage 4 visual review).
		const fundTitle = this.form && this.form.fund && this.form.fund.title;

		if ( fundTitle && '1' !== this.container.getAttribute( 'data-anudan-fund-named' ) ) {
			const fundName = document.createElement( 'p' );
			// The title arrives HTML-encoded (WordPress turns an apostrophe into &#8217;). A
			// textarea's content is parsed as text, never markup, so this decodes entities only.
			const decoder = document.createElement( 'textarea' );

			decoder.innerHTML = fundTitle;
			fundName.className = 'anudan-checkout__fund-name';
			fundName.textContent = decoder.value;
			header.appendChild( fundName );
		}

		const steps = document.createElement( 'div' );

		steps.className = 'anudan-checkout__steps';

		this.stepOneEl = this.buildStepOne();
		this.stepTwoEl = this.buildStepTwo();

		steps.appendChild( this.stepOneEl );
		steps.appendChild( this.stepTwoEl );

		this.summaryBar = this.buildSummaryBar();

		// R2-20 (rc2): everything the form draws sits in one frame, the query container; the phone's dock is its sibling.
		this.frame = document.createElement( 'div' );
		this.frame.className = 'anudan-checkout__frame';
		this.frame.appendChild( header );
		this.frame.appendChild( steps );
		this.frame.appendChild( this.summaryBar );
		this.container.appendChild( this.frame );

		this.showStep( 1, false );
	}

	/**
	 * Finds the wrapper of the control a server field key belongs to. The server names the donor's
	 * own details `first_name`, `last_name`, `email` (and the fund's custom fields by their own
	 * key); the browser's controls are `donor_first_name` and so on.
	 *
	 * @param {string} key
	 * @return {HTMLElement|null}
	 */
	fieldWrapper( key ) {
		const aliases = {
			first_name: 'donor_first_name',
			last_name: 'donor_last_name',
			email: 'donor_email',
			phone: 'donor_phone',
			// Q1 (0.9.40): the dedication's own fields.
			'dedicate.name': 'dedicate_name',
			'dedicate.notify_name': 'dedicate_notify_name',
			'dedicate.notify_email': 'dedicate_notify_email',
		};
		const entry = this.entries ? /^entries\.(\d+)\.(.+)$/.exec( key ) : null;

		// The server names an entry by its place; the page keeps it under a serial (EntriesUi.addBlock()).
		if ( entry && this.entries.blocks[ Number( entry[ 1 ] ) ] ) {
			key = `entries.${ this.entries.blocks[ Number( entry[ 1 ] ) ].serial }.${ entry[ 2 ] }`;
		}

		const own = aliases[ key ] || key;
		let control = null;

		if ( 'donor_phone' === own && this.phoneField ) {
			control = this.phoneField.element;
		} else if ( 'registration_number' === own && this.registrationInput ) {
			control = this.registrationInput;
		} else if ( this.fields[ own ] && this.fields[ own ].closest ) {
			control = this.fields[ own ];
		} else {
			const custom = this.container.querySelectorAll( '[data-anudan-field]' );

			for ( const candidate of custom ) {
				if ( candidate.getAttribute( 'data-anudan-field' ) === key ) {
					return candidate;
				}
			}
		}

		return control ? control.closest( '.anudan-checkout__field' ) : null;
	}

	/**
	 * Removes every message placed beside a field.
	 *
	 * @return {void}
	 */
	clearFieldErrors() {
		this.container.querySelectorAll( '.anudan-checkout__field-error' ).forEach( ( node ) => node.remove() );
		this.container.querySelectorAll( '.anudan-checkout__field--invalid' ).forEach( ( node ) => {
			node.classList.remove( 'anudan-checkout__field--invalid' );

			node.querySelectorAll( 'input, select, textarea' ).forEach( ( control ) => control.removeAttribute( 'aria-invalid' ) );
		} );
	}

	/**
	 * BACKLOG 31 (after 0.9.41-rc5): as the donor leaves the email field, the server looks its domain up and keeps the answer, so
	 * Continue to payment's final check reads it without waiting on DNS. Nothing waits for this: the donor may carry on at once, an
	 * answer later than {@see api.EMAIL_CHECK_LIMIT_MS} is let go, and a common provider is not sent at all. A refusal is shown
	 * beside the field, without moving the page or the cursor; the final check is what refuses.
	 *
	 * @param {HTMLInputElement} input
	 * @return {void}
	 */
	checkEmailOnLeave( input ) {
		let asked = '';

		input.addEventListener( 'change', () => {
			const value = String( input.value || '' ).trim().toLowerCase();
			const at = value.lastIndexOf( '@' );
			const domain = at > 0 ? value.slice( at + 1 ) : '';

			if ( value === asked || ! /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test( domain ) || COMMON_DOMAINS.includes( domain ) ) {
				return;
			}

			asked = value;

			api.checkEmail( value ).then( ( response ) => {
				const still = String( input.value || '' ).trim().toLowerCase() === value;

				if ( still && response.ok && response.body && false === response.body.ok && response.body.message ) {
					this.showEmailAdvice( input, String( response.body.message ) );
				}
			} );
		} );
	}

	/**
	 * The email check's refusal beside the email field, as a field error, without moving the page or the cursor.
	 *
	 * @param {HTMLInputElement} input
	 * @param {string}           message
	 * @return {void}
	 */
	showEmailAdvice( input, message ) {
		const wrapper = this.fieldWrapper( 'donor_email' );

		if ( ! wrapper ) {
			return;
		}

		wrapper.querySelectorAll( '.anudan-checkout__field-error' ).forEach( ( node ) => node.remove() );

		const error = document.createElement( 'p' );

		error.className = 'anudan-checkout__field-error';
		error.id = `${ this.idPrefix }-error-donor_email`;
		error.setAttribute( 'role', 'alert' );
		error.textContent = message;
		wrapper.appendChild( error );
		wrapper.classList.remove( 'anudan-checkout__field--fixed' );
		wrapper.classList.add( 'anudan-checkout__field--invalid' );
		input.setAttribute( 'aria-invalid', 'true' );
		input.setAttribute( 'aria-errormessage', error.id );
		this.watchFix( wrapper, input );
	}

	/**
	 * Puts the server's message for each refused field beside that field, goes back to the step it
	 * is on, and focuses the first. A general error alone ("details did not pass the checks") tells
	 * the donor nothing to fix (staging 0.5.9).
	 *
	 * @param {{body: any}} response A failed `POST /checkout/intent` response.
	 * @return {number} How many fields were marked. Zero means nothing could be placed beside a
	 *                  field, and the caller says it in general terms instead.
	 */
	showFieldErrors( response ) {
		const fields = response && response.body && response.body.data && response.body.data.fields;

		this.clearFieldErrors();

		if ( ! fields || 'object' !== typeof fields ) {
			return 0;
		}

		let first = null;
		let shown = 0;

		Object.keys( fields ).forEach( ( key ) => {
			const wrapper = this.fieldWrapper( key );

			if ( ! wrapper || 'string' !== typeof fields[ key ] ) {
				return;
			}

			const error = document.createElement( 'p' );

			error.className = 'anudan-checkout__field-error';
			error.id = `${ this.idPrefix }-error-${ key }`;
			error.setAttribute( 'role', 'alert' );
			error.textContent = fields[ key ];
			wrapper.appendChild( error );
			wrapper.classList.remove( 'anudan-checkout__field--fixed' );
			wrapper.classList.add( 'anudan-checkout__field--invalid' );
			wrapper.querySelectorAll( 'input, select, textarea' ).forEach( ( control ) => {
				control.setAttribute( 'aria-invalid', 'true' );
				control.setAttribute( 'aria-errormessage', error.id );
				this.watchFix( wrapper, control );
			} );

			shown += 1;
			first = first || wrapper;
		} );

		if ( first ) {
			const step = first.closest( '[data-anudan-step]' );
			const number = step ? parseInt( step.getAttribute( 'data-anudan-step' ), 10 ) : 0;

			if ( number && number !== this.step ) {
				this.showStep( number, false );
			}

			// C8 (0.9.40): the page glides to the first field to fix and the cursor is put in it.
			if ( 'function' === typeof first.scrollIntoView ) {
				first.scrollIntoView( { block: 'center', behavior: 'smooth' } );
			}

			const target = first.querySelector( 'input, select, textarea' );

			if ( target && 'function' === typeof target.focus ) {
				target.focus( { preventScroll: true } );
			}
		}

		return shown;
	}

	/**
	 * C8 (0.9.40): a field in error turns green once the donor changes it: its message goes, and it is no longer marked
	 * invalid. The server checks it again on the next press either way.
	 *
	 * @param {HTMLElement} wrapper The field.
	 * @param {HTMLElement} control Its input.
	 * @return {void}
	 */
	watchFix( wrapper, control ) {
		if ( control.anudanWatchingFix ) {
			return;
		}

		control.anudanWatchingFix = true;

		const fixed = () => {
			if ( ! wrapper.classList.contains( 'anudan-checkout__field--invalid' ) ) {
				return;
			}

			const value = 'checkbox' === control.type || 'radio' === control.type ? ( control.checked ? 'on' : '' ) : String( control.value || '' ).trim();

			if ( '' === value || ( 'email' === control.type && ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( value ) ) ) {
				return;
			}

			wrapper.classList.remove( 'anudan-checkout__field--invalid' );
			wrapper.classList.add( 'anudan-checkout__field--fixed' );
			wrapper.querySelectorAll( '.anudan-checkout__field-error' ).forEach( ( node ) => node.remove() );
			wrapper.querySelectorAll( 'input, select, textarea' ).forEach( ( input ) => {
				input.removeAttribute( 'aria-invalid' );
				input.removeAttribute( 'aria-errormessage' );
			} );
		};

		control.addEventListener( 'input', fixed );
		control.addEventListener( 'change', fixed );
	}

	/**
	 * C8 (0.9.40): the details every gift needs, checked before anything is sent, each said beside its field in the words
	 * the server uses: a name, and an email address that looks like one. The server checks everything again.
	 *
	 * @return {Object<string, string>} Field key to message; empty when nothing needs fixing.
	 */
	missingDetails() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const errors = {};
		const value = ( key ) => ( this.fields[ key ] ? String( this.fields[ key ].value || '' ).trim() : 'n/a' );

		if ( '' === value( 'donor_first_name' ) ) {
			errors.first_name = __( 'Enter your first name.' );
		}

		if ( '' === value( 'donor_last_name' ) ) {
			errors.last_name = __( 'Enter your last name.' );
		}

		const email = value( 'donor_email' );

		if ( 'n/a' !== email && ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( email ) ) {
			errors.email = __( 'Enter an email address like name@example.com.' );
		}

		// R2-11 (rc2): a phone or a registration number the fund requires.
		if ( 'required' === this.phoneMode() && ! ( this.phoneField && '' !== String( this.phoneField.getValue() || '' ).trim() ) ) {
			errors.donor_phone = __( 'Enter a phone number.' );
		}

		if ( this.registrationInput && this.registrationInput.required && '' === this.registrationInput.value.trim() ) {
			errors.registration_number = __( 'Enter your registration number.' );
		}

		return errors;
	}

	/**
	 * Q1 (0.9.40): what the open dedication still needs, keyed as the server names it; empty when it is closed or complete.
	 * The server checks the name and the email again whatever this says.
	 *
	 * @return {Object<string, string>}
	 */
	dedicateMissing() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const errors = {};
		const fields = this.dedicateFields;

		if ( ! fields || ! fields.isOpen || ! fields.isOpen() ) {
			return errors;
		}

		if ( '' === String( fields.name() ).trim() ) {
			errors[ 'dedicate.name' ] = __( 'Enter who this gift is dedicated to.' );
		}

		if ( fields.isTelling && fields.isTelling() ) {
			if ( '' === String( fields.notifyName() ).trim() ) {
				errors[ 'dedicate.notify_name' ] = __( 'Enter their name.' );
			}

			if ( ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( String( fields.notifyEmail() ).trim() ) ) {
				errors[ 'dedicate.notify_email' ] = __( 'Enter an email address like name@example.com.' );
			}
		}

		return errors;
	}

	/**
	 * Tells the donor something, visibly and to screen readers. An empty string clears it.
	 *
	 * @param {string}  text
	 * @param {boolean} [scroll]   Bring the message into view. Off for a standing notice shown while
	 *                             the page loads, which must not move the page.
	 * @param {boolean} [progress] 0.9.41 rc2: a wait, not a problem, shown in the calm box rather than the error box.
	 * @return {void}
	 */
	say( text, scroll = true, progress = false ) {
		this.liveRegion.textContent = text;

		if ( this.messageEl ) {
			this.messageEl.classList.toggle( 'anudan-checkout__message--progress', progress );
		}

		if ( ! this.messageEl ) {
			return;
		}

		// C8 (0.9.40): said right above the button the donor pressed, never in a box at the top of the form.
		let actions = this.currentActions ? this.anchorOf( this.currentActions() ) : null;

		// 0.9.41: the security check, when Cloudflare asks for it, stays directly above the button; the message goes above it.
		// The message itself may already sit between them; it is looked past, so it moves above the box.
		const before = actions && actions.previousSibling === this.messageEl ? this.messageEl.previousSibling : ( actions ? actions.previousSibling : null );

		if ( actions && this.securityBox && before === this.securityBox ) {
			actions = this.securityBox;
		}

		if ( actions && actions.parentNode && this.messageEl.nextSibling !== actions ) {
			actions.parentNode.insertBefore( this.messageEl, actions );
		}

		this.messageEl.textContent = text;
		this.messageEl.hidden = '' === text;

		if ( scroll && text && 'function' === typeof this.messageEl.scrollIntoView ) {
			// Nearest, not centre: a donor who pressed Give at the bottom of a long form must be
			// able to see what happened without hunting for it.
			this.messageEl.scrollIntoView( { block: 'nearest', behavior: 'smooth' } );
		}
	}

	/**
	 * 0.9.41 rc2 (decisions log 2026-10-29, "donors are never lost"): while Continue to payment gets the payment ready (the
	 * background check, then the gift made on the server), the button says "One moment…" and the line above it "Getting your
	 * secure payment ready.", never a frozen button and never the words "security check". Off puts the button's own words back
	 * and clears the line, unless something else has been said since.
	 *
	 * @param {boolean} on
	 * @return {void}
	 */
	showPreparing( on ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const preparing = __( 'Getting your secure payment ready.' );

		if ( on ) {
			this.preparing = true;
			this.giveButton.textContent = __( 'One moment…' );
			this.giveButton.setAttribute( 'aria-busy', 'true' );
			this.say( preparing, false, true );

			return;
		}

		if ( ! this.preparing ) {
			return;
		}

		this.preparing = false;
		this.giveButton.textContent = __( 'Continue to payment' );
		this.giveButton.removeAttribute( 'aria-busy' );

		if ( this.messageEl && preparing === this.messageEl.textContent ) {
			this.say( '', false );
		}
	}

	/**
	 * Re-enables Give after an attempt ends, unless something else disables it on purpose: a
	 * misconfigured Turnstile or a fund with no gateway. Setting `disabled = false` directly
	 * re-enabled Give on such a fund after a failed attempt (release gate 0.5.3, adversarial
	 * review round 9, LOW-2; the server refused the gift either way).
	 *
	 * @return {void}
	 */
	enableGive() {
		this.showPreparing( false );
		this.giveButton.disabled = false;

		if ( this.form ) {
			this.applyTurnstileAvailability();
			this.applyGatewayAvailability();
		}
	}

	/**
	 * After rc5 (staging: "This page is out of date" on every press): a page whose signed time was refused gets a fresh one with
	 * the refusal, so the next press of Continue to payment works without reloading. The server's minimum time on the form applies
	 * to a fresh token as to a page just loaded, so Continue waits that long first (three seconds).
	 *
	 * After rc7: taken once per page, never again. A second refusal of the same kind is not answered by a second refresh, which
	 * would loop; the donor is asked to reload the page instead.
	 *
	 * @param {{body: any}} response A refused intent response.
	 * @return {'none'|'taken'|'again'} Whether the refusal carried a fresh token, and whether this page had already taken one.
	 */
	takeFreshPageToken( response ) {
		const fresh = response && response.body && response.body.data && response.body.data.form_token;
		const engine = window.AnudanEngine;

		if ( 'string' !== typeof fresh || '' === fresh || ! engine ) {
			return 'none';
		}

		if ( this.pageTokenRefreshed ) {
			return 'again';
		}

		this.pageTokenRefreshed = true;
		engine.config = Object.assign( engine.config || {}, { formToken: fresh } );
		this.giveButton.disabled = true;
		window.setTimeout( () => {
			this.giveButton.disabled = false;
		}, 3100 );

		return 'taken';
	}

	/**
	 * `04-SECURITY.md` §4.5's middle branch: Turnstile required but not fully configured
	 * disables the donation form, rather than letting a donor press Give and discover it only
	 * as a failed attempt. `form.turnstile_ready` is `Rest\FormSchema`'s own read of
	 * `Security\Turnstile::configuration_status()` — the same check `verify()` itself makes —
	 * so this never has its own opinion about whether Turnstile is configured; it only reacts
	 * to the server's.
	 *
	 * Applied once, at render time, before the donor has done anything: the message shown is
	 * identical to `Rest\PublicGate::verify_human()`'s own `TURNSTILE_UNAVAILABLE` text, so a
	 * misconfigured site reads the same whether it is caught here or, for a donor who somehow
	 * reaches Give anyway, by the server's own refusal.
	 *
	 * @return {void}
	 */
	applyTurnstileAvailability() {
		if ( this.form.turnstile_ready ) {
			return;
		}

		this.giveButton.disabled = true;
		this.giveButton.setAttribute( 'data-anudan-turnstile-misconfigured', 'true' );
		this.say(
			window.wp && window.wp.i18n
				? window.wp.i18n.__( 'Donations are temporarily unavailable while a security check cannot be completed. Please try again shortly.', 'anudan' )
				: 'Donations are temporarily unavailable while a security check cannot be completed. Please try again shortly.',
			false
		);

		if ( this.turnstileContainer ) {
			this.turnstileContainer.hidden = true;
		}
	}

	/**
	 * WP-44's own most-emphasised requirement: "a donor must never see a checkout with no
	 * payment method and no explanation." `this.form.gateways` is already
	 * `Gateways\FundGatewayResolver`'s own filtered list — configured, and honouring the fund's
	 * restriction — so an empty list here means this fund genuinely cannot take a payment through
	 * any path right now, not merely that this browser has not yet chosen one. Applied at the
	 * same points {@see this.applyTurnstileAvailability} is, for the identical reason: before the
	 * donor has done anything, and again on every step change so the notice survives a
	 * `showStep()` re-render.
	 *
	 * **Never overwrites a Turnstile-misconfigured notice already showing.** Both conditions can
	 * hold on the same fund (an entirely unconfigured site, most plausibly), and both write to
	 * the same `liveRegion`; the Turnstile message wins because it names the more fundamental
	 * blocker — Turnstile disables every donation on the whole site, not only the ones this fund's
	 * own gateway situation would already have blocked. The button is left disabled either way,
	 * which is the only thing a donor pressing Give actually needs to be true.
	 *
	 * @return {void}
	 */
	applyGatewayAvailability() {
		if ( this.availableGateways().length > 0 ) {
			return;
		}

		this.giveButton.disabled = true;
		this.giveButton.setAttribute( 'data-anudan-no-gateway', 'true' );

		if ( this.form.turnstile_ready ) {
			this.say(
				window.wp && window.wp.i18n
					? window.wp.i18n.__( 'Donations cannot be taken right now. Please try again later, or contact us.', 'anudan' )
					: 'Donations cannot be taken right now. Please try again later, or contact us.',
				false
			);
		}

		if ( this.gatewaySelector ) {
			this.gatewaySelector.hidden = true;
		}

		if ( this.paymentContainer ) {
			this.paymentContainer.hidden = true;
		}
	}

	/**
	 * Step 1 — frequency, amount, fund custom fields, fee coverage (PRD §1.5.2).
	 *
	 * @return {HTMLElement}
	 */
	buildStepOne() {
		const step = document.createElement( 'div' );

		step.className = 'anudan-checkout__step';
		step.setAttribute( 'data-anudan-step', '1' );

		const title = document.createElement( 'h2' );

		// Q1 (0.9.40): the step bar names this step; its title is for screen readers and the focus a step change moves to.
		title.className = 'anudan-checkout__step-title anudan-checkout__visually-hidden';
		title.textContent = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Your gift', 'anudan' ) : 'Your gift';
		step.appendChild( title );

		// 0.9.14: a fund with priced entries is priced from its entries, not from amounts, and is one-time only.
		this.entries = ! this.cart && this.form.modules && this.form.modules.entries && this.form.entries ? new EntriesUi( this ) : null;
		// D2 (0.9.40): a fund with gift items is priced from the items chosen and an extra, one-time only.
		this.items = ! this.cart && ! this.entries && this.form.modules && this.form.modules.items && Array.isArray( this.form.items ) && this.form.items.length ? new ItemsUi( this ) : null;

		// R2-03 (rc2): no express wallet button on the gift step; the card form on the payment step carries the wallets.

		// Per-month giving: a fund with dated months chooses its frequency by how the donor gives by month (MonthsUi).
		this.dated = ! this.cart && ! this.entries && ! this.items && this.form.modules && this.form.modules.months && this.form.months && 'dated' === this.form.months.mode ? new MonthsUi( this ) : null;

		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		// Q1 (0.9.40): the gift step in titled parts: how often, the amount (or the names, the months, the funds), the fund's
		// questions, then a few choices, the summary of the gift, and the one button.
		if ( this.form.frequencies.length > 1 && ! this.entries && ! this.items && ! this.dated ) {
			const often = this.buildSection( __( 'How often' ) );

			often.appendChild( this.buildFrequencyToggle() );
			step.appendChild( often );
		}

		const amountTitle = this.cart ? __( 'Your gifts' ) : ( this.entries ? ( this.entries.sectionTitle ? this.entries.sectionTitle() : __( 'Your gift' ) ) : ( this.items ? __( 'Gift items' ) : ( this.dated ? __( 'Months' ) : __( 'Amount' ) ) ) );
		const amount = this.buildSection( amountTitle );

		amount.appendChild( this.cart ? this.buildCartLines() : ( this.entries ? this.entries.build() : ( this.items ? this.items.build() : ( this.dated ? this.dated.build() : this.buildAmounts() ) ) ) );

		if ( this.form.modules && this.form.modules.months && this.form.months && Array.isArray( this.form.months.periods ) && this.form.months.periods.length ) {
			amount.appendChild( this.buildMonthChips() );
		}

		// Q1 (0.9.40): what the gift includes in return, from the quote ("Your $200.00 gift includes a dinner ticket worth $50.00.").
		if ( this.form.goods && ! this.cart ) {
			this.goodsNote = document.createElement( 'div' );
			this.goodsNote.className = 'anudan-checkout__goods';
			this.goodsNote.setAttribute( 'aria-live', 'polite' );
			this.goodsNote.hidden = true;
			amount.appendChild( this.goodsNote );
		}

		step.appendChild( amount );

		// D2 (Phase 3b): gift items will have their own part here, between the amount and the questions.
		this.itemsSlot = document.createElement( 'div' );
		this.itemsSlot.className = 'anudan-checkout__items-slot';
		this.itemsSlot.hidden = true;
		step.appendChild( this.itemsSlot );

		// The fund's own questions, with the notice about another person's details before them (WP-38).
		const questions = this.form.fields.filter( ( field ) => ! ( this.entries && field.per_entry ) );
		const hasService = this.form.modules && this.form.modules.service && this.form.service && this.form.service.consent_notice;

		if ( questions.length > 0 || hasService ) {
			// Q1 (0.9.40): a request carried out for another person is "Your request", as the design names it.
			const asked = this.buildSection( hasService ? __( 'Your request' ) : __( 'A few questions' ) );

			if ( hasService ) {
				asked.appendChild( this.buildServiceNotice() );
			}

			questions.forEach( ( field ) => {
				asked.appendChild( this.buildField( field ) );
			} );

			step.appendChild( asked );
		}

		// Q1 (0.9.40): a dedication is its own part, as the design has it.
		if ( this.form.modules && this.form.modules.dedicate && this.form.dedicate ) {
			const honor = this.buildSection( __( 'In honor or in memory' ) );

			honor.appendChild( this.buildDedicateFields() );
			step.appendChild( honor );
		}

		const choices = this.buildSection( __( 'A few choices' ) );

		if ( this.form.modules && this.form.modules.anon ) {
			choices.appendChild( this.buildAnonCheckbox() );
		}

		choices.appendChild( this.buildFeeCoverage() );
		// Nothing to choose: no part at all.
		choices.hidden = ! Array.from( choices.children ).some( ( child ) => child.classList.contains( 'anudan-checkout__section-title' ) ? false : ! child.hidden );
		step.appendChild( choices );

		const actions = document.createElement( 'div' );

		actions.className = 'anudan-checkout__actions';

		const continueButton = document.createElement( 'button' );

		continueButton.type = 'button';
		continueButton.className = 'anudan-checkout__button anudan-checkout__button--block';
		continueButton.textContent = __( 'Continue to your details' );
		continueButton.addEventListener( 'click', async () => {
			// Nothing chosen yet: stay on this step and say so, in plain words. (The server checks the amount again
			// whatever this script does; this is only so a donor is not carried on to the details with no gift.)
			if ( this.nothingChosen() ) {
				this.amountPrompted = true;
				this.say(
					this.dated || this.items
						? ( this.dated || this.items ).prompt()
						: ( window.wp && window.wp.i18n
							? window.wp.i18n.__( 'Please choose an amount first.', 'anudan' )
							: 'Please choose an amount first.' ),
					false
				);

				// C8 (0.9.40): the amount is marked, the page glides to it and the first choice takes the cursor.
				const choices = this.amountsGroup || this.container.querySelector( '[data-anudan-step="1"] .anudan-checkout__section:not([hidden]) input' );
				const holder = choices && choices.closest ? choices.closest( '.anudan-checkout__section' ) : null;

				if ( holder ) {
					holder.classList.add( 'anudan-checkout__section--invalid' );
					holder.scrollIntoView( { block: 'center', behavior: 'smooth' } );

					const first = holder.querySelector( 'input:not([type="hidden"])' );

					if ( first ) {
						first.focus( { preventScroll: true } );
					}
				}

				return;
			}

			// 0.9.41-rc1: the server's total for this amount first, so the details step opens with it and nothing appears above its
			// button afterwards. Continue is unavailable while it comes, says "Still working…" after QUOTE_WAIT_MS, and goes on only
			// once the total is shown; a refusal of the amount, or a total that could not be worked out, is said and keeps the donor
			// here (decisions log, 2026-10-29). A second press while it comes does nothing.
			if ( this.awaitingTotal ) {
				return;
			}

			this.awaitingTotal = true;
			continueButton.setAttribute( 'aria-disabled', 'true' );

			let slowShown = false;
			let priced = false;

			try {
				priced = await this.whenQuoteSettled( () => {
					slowShown = true;
					this.say( __( 'Still working…' ), false, true );
				} );
			} finally {
				this.awaitingTotal = false;
				continueButton.removeAttribute( 'aria-disabled' );
			}

			if ( slowShown && ( priced || this.amountError ) ) {
				this.say( '', false );
			}

			// The amount refused is said under it, below; anything else that kept the total away is said already (the quote's own
			// message), and the donor stays here to try again.
			if ( ! priced && ! this.amountError ) {
				return;
			}

			// Q1, C8 (0.9.40): an amount the fund does not take stays on this step, with its message under the amount. The server's
			// own message when its quote has said so; the fund's limits, which the form knows, when Continue comes first.
			const outOfRange = this.amountOutOfRange();

			if ( outOfRange ) {
				this.showAmountError( outOfRange );
			}

			if ( this.amountError && this.customAmountWrapper ) {
				this.customAmountWrapper.scrollIntoView( { block: 'center', behavior: 'smooth' } );

				if ( this.customAmountInput ) {
					this.customAmountInput.focus( { preventScroll: true } );
				}

				return;
			}

			this.container.querySelectorAll( '.anudan-checkout__section--invalid' ).forEach( ( node ) => node.classList.remove( 'anudan-checkout__section--invalid' ) );

			// Q1, C8 (0.9.40): an open dedication without its name (or a note without its address) is fixed here, marked in red.
			const dedication = this.dedicateMissing();

			if ( Object.keys( dedication ).length > 0 ) {
				const marked = this.showFieldErrors( { body: { data: { fields: dedication } } } );
				const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

				this.say(
					1 === marked
						? __( 'Please fix 1 thing to continue. It is marked in red.' )
						: __( 'Please fix these %d things to continue. They are marked in red.' ).replace( '%d', String( marked ) ),
					false
				);

				return;
			}

			if ( await this.offerMonthly() ) {
				return;
			}

			this.continueToDetails();
		} );

		actions.appendChild( this.buildActionsTotal() );
		actions.appendChild( continueButton );
		step.appendChild( actions );
		this.stepOneActions = actions;

		return step;
	}

	/**
	 * Whether the donor has not yet chosen a gift: no preset and no typed amount, or, on a fund with priced entries,
	 * no entry (such a fund is priced from its entries, so it has neither a preset nor an amount of its own). The one
	 * rule the total, Continue and the wallet button all use, so they cannot disagree about it.
	 *
	 * @return {boolean}
	 */
	nothingChosen() {
		// A gift cart keeps its amounts in its lines, never in a preset or typed amount of its own.
		if ( this.cart ) {
			return ! this.cartComplete();
		}

		if ( this.dated ) {
			return this.dated.nothingChosen();
		}

		// D2 (0.9.40): a gift of items needs at least one item, or an amount on its own (a plain gift to the fund, decided after Phase 3b).
		if ( this.items ) {
			return this.items.count() < 1 && null === this.customAmount;
		}

		return this.entries
			? this.entries.count() < 1
			: ( null === this.selectedTierId && null === this.customAmount );
	}

	/**
	 * Moves from the gift step to the details-and-payment step.
	 *
	 * @return {void}
	 */
	continueToDetails() {
		this.showStep( 2 );
		this.trackBeginCheckoutIfKnown();
	}

	/**
	 * "Make it monthly?" (0.9.0): after a one-time amount is chosen, offer once to turn it into a
	 * smaller monthly gift. The figure is the server's (`monthly_suggestion` on a fresh quote); this
	 * only shows it. Returns true when the offer is now on screen, so Continue waits for an answer.
	 *
	 * @return {Promise<boolean>}
	 */
	async offerMonthly() {
		const prompt = this.form.monthly_prompt;

		if ( ! prompt || ! prompt.enabled || this.monthlyPromptOutcome || 'one_time' !== this.currentFrequency() || this.dated ) {
			return false;
		}

		// On a shared tablet one donor's "no thanks" says nothing about the next donor.
		if ( ( null === this.selectedTierId && null === this.customAmount ) || ( ! this.tablet && declinedRecently( this.fundId ) ) ) {
			return false;
		}

		let response;

		try {
			response = await api.quote( {
				fund_id: this.fundId,
				amount_tier_id: this.selectedTierId,
				custom_amount: this.customAmount,
				frequency: 'one_time',
				cover_fees: false,
				gateway: this.currentGateway(),
				selected_periods: this.selectedPeriods,
			} );
		} catch ( error ) {
			return false;
		}

		const suggestion = response && response.ok && response.body ? response.body.monthly_suggestion : null;

		if ( ! suggestion || ! Number.isInteger( suggestion.amount ) ) {
			return false;
		}

		this.showMonthlyPrompt( suggestion, response.body.gift_amount );

		return true;
	}

	/**
	 * Shows the offer in place of step 1's Continue button: one question, two answers.
	 *
	 * @param {{amount: number, tier_id: (number|null)}} suggestion The server's suggestion.
	 * @param {number}                                   oneTime    The one-time amount, minor units.
	 * @return {void}
	 */
	showMonthlyPrompt( suggestion, oneTime ) {
		const i18n = window.wp && window.wp.i18n ? window.wp.i18n : null;
		const panel = document.createElement( 'div' );
		const headingId = `anudan-monthly-prompt-${ this.fundId }`;
		const monthly = this.formatMinorUnits( suggestion.amount );
		const once = this.formatMinorUnits( oneTime );

		panel.className = 'anudan-checkout__monthly-prompt';
		panel.setAttribute( 'role', 'group' );
		panel.setAttribute( 'aria-labelledby', headingId );

		const heading = document.createElement( 'p' );

		heading.id = headingId;
		heading.className = 'anudan-checkout__monthly-prompt-title';
		heading.textContent = i18n ? i18n.__( 'Would you like to make it monthly?', 'anudan' ) : 'Would you like to make it monthly?';
		heading.setAttribute( 'tabindex', '-1' );
		panel.appendChild( heading );

		const text = document.createElement( 'p' );

		text.className = 'anudan-checkout__monthly-prompt-text';
		text.textContent = i18n
			? i18n.__( 'A smaller gift every month helps us plan ahead. You can change or stop it at any time.', 'anudan' )
			: 'A smaller gift every month helps us plan ahead. You can change or stop it at any time.';
		panel.appendChild( text );

		const actions = document.createElement( 'div' );

		// Its own row, side by side: not a step's button row, which stays at the foot of a phone's screen.
		actions.className = 'anudan-checkout__prompt-actions';

		const yes = document.createElement( 'button' );

		yes.type = 'button';
		yes.className = 'anudan-checkout__button anudan-checkout__button--outline';
		yes.textContent = ( i18n ? i18n.__( 'Yes, give {amount} monthly', 'anudan' ) : 'Yes, give {amount} monthly' ).replace( '{amount}', monthly );
		yes.addEventListener( 'click', () => this.answerMonthly( panel, true, suggestion ) );

		const no = document.createElement( 'button' );

		no.type = 'button';
		no.className = 'anudan-checkout__button anudan-checkout__button--outline';
		no.textContent = ( i18n ? i18n.__( 'No thanks, give {amount} once', 'anudan' ) : 'No thanks, give {amount} once' ).replace( '{amount}', once );
		no.addEventListener( 'click', () => this.answerMonthly( panel, false, suggestion ) );

		actions.appendChild( yes );
		actions.appendChild( no );
		panel.appendChild( actions );

		this.stepOneActions.hidden = true;

		const anchor = this.anchorOf( this.stepOneActions );

		anchor.parentNode.insertBefore( panel, anchor );
		this.syncDock();
		heading.focus();
	}

	/**
	 * Applies the donor's answer, then continues to step 2. Asked once: the answer is kept for this
	 * checkout, and a "no thanks" is remembered on this device.
	 *
	 * @param {HTMLElement}                              panel      The offer.
	 * @param {boolean}                                  accepted   Whether the donor said yes.
	 * @param {{amount: number, tier_id: (number|null)}} suggestion The server's suggestion.
	 * @return {void}
	 */
	answerMonthly( panel, accepted, suggestion ) {
		this.monthlyPromptOutcome = accepted ? 'accepted' : 'declined';
		panel.remove();
		this.stepOneActions.hidden = false;
		this.syncDock();

		if ( ! accepted ) {
			if ( ! this.tablet ) {
				rememberDecline( this.fundId );
			}

			this.continueToDetails();

			return;
		}

		const monthlyInput = this.frequencyGroup
			? this.frequencyGroup.querySelector( 'input[value="monthly"]' )
			: null;

		if ( monthlyInput ) {
			monthlyInput.checked = true;
		}

		this.amountsGroup.querySelectorAll( 'input[type="radio"]' ).forEach( ( input ) => {
			input.checked = null !== suggestion.tier_id && String( suggestion.tier_id ) === input.value;
		} );

		if ( null !== suggestion.tier_id ) {
			this.selectedTierId = suggestion.tier_id;
			this.customAmount = null;

			if ( this.customAmountInput ) {
				this.customAmountInput.value = '';
			}
		} else {
			this.selectedTierId = null;
			this.customAmount = suggestion.amount;

			if ( this.customAmountInput ) {
				this.customAmountInput.value = `${ Math.floor( suggestion.amount / 100 ) }.${ String( suggestion.amount % 100 ).padStart( 2, '0' ) }`;
			}
		}

		this.updateSummary();
		this.continueToDetails();
	}

	/**
	 * The express wallet slot — top of step 1, above everything else (PRD §1.5.1). Built only
	 * when `form.wallets_eligible`, and `hidden` until device support is actually confirmed, so
	 * a device that cannot pay this way never reserves space for a button it will not show
	 * (`initWallets()`; "graceful absence — no layout shift, no empty slot").
	 *
	 * @return {HTMLElement}
	 */
	buildExpressWalletSlot() {
		const wrapper = document.createElement( 'div' );

		wrapper.className = 'anudan-checkout__wallet-express';
		wrapper.hidden = true;

		this.expressWalletButton = document.createElement( 'div' );
		this.expressWalletButton.className = 'anudan-checkout__wallet-button';
		wrapper.appendChild( this.expressWalletButton );

		const divider = document.createElement( 'p' );

		divider.className = 'anudan-checkout__wallet-divider';
		divider.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'or give another way', 'anudan' )
			: 'or give another way';
		wrapper.appendChild( divider );

		this.expressWalletSlot = wrapper;

		return wrapper;
	}

	/**
	 * Step 2 — identity, phone, registration number (progressive), payment (PRD §1.5.2/§1.5.4).
	 *
	 * @return {HTMLElement}
	 */
	buildStepTwo() {
		const step = document.createElement( 'div' );

		step.className = 'anudan-checkout__step';
		step.setAttribute( 'data-anudan-step', '2' );

		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		// R2-06 (rc2): "Back" is a full-width button under the step's main button (placed with the actions below, and under the
		// payment box on the payment step: enterPaymentStep()); the step starts with its title: "Your details", then "Payment".
		const back = document.createElement( 'button' );

		back.type = 'button';
		back.className = 'anudan-checkout__back';
		back.textContent = __( 'Back' );
		back.addEventListener( 'click', () => this.showStep( 1 ) );
		this.backButton = back;

		const title = document.createElement( 'h2' );

		title.className = 'anudan-checkout__step-title';
		title.textContent = __( 'Your details' );
		this.stepTwoTitle = title;
		step.appendChild( title );

		// About you: the names side by side on a wide screen, then the email and the phone.
		const about = this.buildSection( __( 'About you' ) );
		const names = document.createElement( 'div' );

		names.className = 'anudan-checkout__pair';
		names.appendChild( this.buildTextField( 'donor_first_name', __( 'First name' ), 'given-name', true ) );
		names.appendChild( this.buildTextField( 'donor_last_name', __( 'Last name' ), 'family-name', true ) );
		about.appendChild( names );

		const emailField = this.buildTextField( 'donor_email', __( 'Email' ), 'email', true, 'email', __( 'We’ll send your receipt here.' ) );
		const emailInput = emailField.querySelector( 'input' );

		about.appendChild( emailField );

		// R2-09 (rc2): "Did you mean name@gmail.com?" for a likely typo of a common provider.
		if ( emailInput ) {
			attachEmailSuggestion( emailInput, __ );
			this.checkEmailOnLeave( emailInput );
		}
		const phone = this.buildPhone();

		if ( phone ) {
			about.appendChild( phone );
		}
		about.appendChild( this.buildRegistrationNumber() );
		about.appendChild( this.buildAskedFields() );
		step.appendChild( about );

		const consent = this.buildConsentFields();

		if ( consent.childNodes.length > 0 ) {
			const touch = this.buildSection( __( 'Stay in touch' ) );

			touch.appendChild( consent );
			step.appendChild( touch );
		}

		step.appendChild( this.buildTrapField() );

		/*
		 * 0.9.41 (replaces R3-5's "✓ Security check passed"): the security check is invisible unless Cloudflare needs the donor.
		 * No heading and no part of the form for it: the widget's box stays hidden, taking no space, and appears directly above
		 * Continue to payment, with one line, only while Cloudflare asks the donor to act ({@see showSecurityStatus}).
		 */
		this.turnstileContainer = document.createElement( 'div' );
		this.turnstileContainer.className = 'anudan-checkout__turnstile';

		/*
		 * The box stays in the page (never `display: none`), or Cloudflare's frame inside it cannot run; with interaction-only it
		 * draws nothing until it needs the donor. Until then the box has no line and no spacing (`is-asking` off).
		 */
		this.securityBox = document.createElement( 'div' );
		this.securityBox.className = 'anudan-checkout__security';
		this.securityBox.setAttribute( 'data-anudan-security-check', '' );

		this.securityStatus = document.createElement( 'p' );
		this.securityStatus.className = 'anudan-checkout__security-line';
		this.securityStatus.setAttribute( 'role', 'status' );
		this.securityStatus.hidden = true;
		this.securityStatus.textContent = __( "Please tick the box so we know you're a person." );
		this.securityBox.appendChild( this.securityStatus );
		this.securityBox.appendChild( this.turnstileContainer );

		// R2-03 (rc2): no wallet slot under the security check; Apple Pay and Google Pay are inside the card form.
		this.paymentWalletSlot = null;

		/*
		 * C9 (0.9.40): no choice of payment method before the payment. The card comes first ("Credit or debit card", with
		 * Apple Pay and Google Pay inside the card form where the device has them), and "Or pay with PayPal" under it opens
		 * PayPal instead: one method for a gift, the other made again for the same gift if the donor switches.
		 */
		const gateways = this.availableGateways();

		this.gatewaySelector = null;
		this.selectedGateway = gateways.includes( 'stripe' ) ? 'stripe' : ( gateways[ 0 ] || null );

		// The payment, once the gift is made: what is paid for, the method, and the other method under it.
		this.paymentArea = document.createElement( 'div' );
		this.paymentArea.className = 'anudan-checkout__pay';
		this.paymentArea.hidden = true;

		this.paymentBox = document.createElement( 'section' );
		this.paymentBox.className = 'anudan-checkout__pay-box';

		this.paymentTitle = document.createElement( 'h3' );
		this.paymentTitle.className = 'anudan-checkout__section-title';
		this.paymentBox.appendChild( this.paymentTitle );

		this.paymentContainer = document.createElement( 'div' );
		this.paymentContainer.className = 'anudan-checkout__payment';
		this.paymentBox.appendChild( this.paymentContainer );
		this.paymentArea.appendChild( this.paymentBox );

		this.switchMethod = document.createElement( 'div' );
		this.switchMethod.className = 'anudan-checkout__pay-other';
		this.switchMethod.hidden = true;

		const or = document.createElement( 'p' );

		or.className = 'anudan-checkout__or';
		or.setAttribute( 'aria-hidden', 'true' );
		or.textContent = __( 'or' );

		this.switchButton = document.createElement( 'button' );
		this.switchButton.type = 'button';
		this.switchButton.className = 'anudan-checkout__button anudan-checkout__button--outline';
		this.switchButton.addEventListener( 'click', () => this.switchPaymentMethod() );
		this.switchMethod.appendChild( or );
		this.switchMethod.appendChild( this.switchButton );
		this.paymentArea.appendChild( this.switchMethod );

		const secure = document.createElement( 'p' );

		secure.className = 'anudan-checkout__secure';
		secure.textContent = __( 'Secure payment. We never see or store your full card number.' );
		this.paymentArea.appendChild( secure );

		step.appendChild( this.paymentArea );

		const actions = document.createElement( 'div' );

		actions.className = 'anudan-checkout__actions';

		this.giveButton = document.createElement( 'button' );
		this.giveButton.type = 'button';
		this.giveButton.className = 'anudan-checkout__button anudan-checkout__button--block';
		this.giveButton.textContent = __( 'Continue to payment' );
		this.giveButton.addEventListener( 'click', () => this.submit() );

		actions.appendChild( this.buildActionsTotal() );
		actions.appendChild( this.giveButton );
		// 0.9.41: the security check's box, hidden until Cloudflare needs the donor, directly above the button.
		step.appendChild( this.securityBox );
		step.appendChild( actions );
		// R3-4 (rc4): Back is never in the row a phone pins to the foot of the screen (the total and the step's main button
		// only); it stands under the row, at the bottom of the form, full width.
		step.appendChild( this.backButton );
		this.stepTwoActions = actions;

		return step;
	}

	/**
	 * Q1 (0.9.40): the total beside a step's button. On a phone the button's row stays at the foot of the screen with it, as the
	 * design draws it ("Total $200.00 · Continue"); on a wider form the stylesheet leaves it out, the total being at the top.
	 * Only shown: the same figure as the step bar's, which is read out there.
	 *
	 * @return {HTMLElement}
	 */
	buildActionsTotal() {
		const total = document.createElement( 'p' );

		total.className = 'anudan-checkout__actions-total';
		total.setAttribute( 'data-anudan-progress-total', '' );
		total.setAttribute( 'aria-hidden', 'true' );
		total.hidden = true;

		return total;
	}

	/**
	 * A titled part of a step ("How often", "About you"), a rule above it.
	 *
	 * @param {string} heading
	 * @return {HTMLElement}
	 */
	buildSection( heading ) {
		const section = document.createElement( 'section' );
		const title = document.createElement( 'h3' );

		section.className = 'anudan-checkout__section';
		title.className = 'anudan-checkout__section-title';
		title.textContent = heading;
		section.appendChild( title );

		return section;
	}

	/**
	 * C9 (0.9.40): the payment method the donor did not start with. The payment already opened is left (it stays pending and
	 * the purge of abandoned checkouts collects it, as when a donor goes back), and the gift is made again for the other
	 * method: one method a payment, never two at once.
	 *
	 * @return {void}
	 */
	switchPaymentMethod() {
		const other = this.availableGateways().find( ( id ) => id !== this.currentGateway() );

		if ( ! other || this.submitting ) {
			return;
		}

		this.leavePaymentStep();
		this.selectedGateway = other;
		saveGatewaySelection( this.fundId, other );
		this.updateSummary();
		this.submit();
	}

	/**
	 * The payment box's title and the button offering the other method, for the method now shown.
	 *
	 * @return {void}
	 */
	describePaymentMethod() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const current = this.currentGateway();
		const other = this.availableGateways().find( ( id ) => id !== current );

		this.paymentTitle.textContent = 'paypal' === current ? __( 'PayPal' ) : __( 'Credit or debit card' );
		// 0.9.41 rc2 (decisions log, 2026-10-29): the PayPal box has no heading; its one line and PayPal's own button say it all. The
		// title stays in the page for screen readers, so the box is still named.
		this.paymentTitle.classList.toggle( 'anudan-checkout__visually-hidden', 'paypal' === current );
		this.switchMethod.hidden = ! other || ( 'paypal' !== other && 'stripe' !== other );
		this.switchButton.textContent = 'paypal' === other ? __( 'Or pay with PayPal' ) : __( 'Or pay by card' );
	}

	/**
	 * @return {HTMLElement}
	 */
	buildFrequencyToggle() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const holder = document.createElement( 'div' );
		const question = document.createElement( 'p' );
		const wrapper = document.createElement( 'div' );

		question.className = 'anudan-checkout__field-label';
		question.id = `${ this.idPrefix }-often`;
		question.textContent = __( 'How often would you like to give?' );
		holder.appendChild( question );

		wrapper.className = 'anudan-checkout__frequency';
		wrapper.setAttribute( 'role', 'radiogroup' );
		wrapper.setAttribute( 'aria-labelledby', question.id );
		holder.appendChild( wrapper );

		// Q1 (0.9.40): what a recurring choice means, in a sentence, under the choice ("You'll give $25.00 every month.").
		this.frequencyNote = document.createElement( 'div' );
		this.frequencyNote.className = 'anudan-checkout__note anudan-checkout__note--info';
		this.frequencyNote.hidden = true;
		holder.appendChild( this.frequencyNote );

		this.form.frequencies.forEach( ( frequency ) => {
			const label = document.createElement( 'label' );

			label.className = 'anudan-checkout__frequency-option';

			const input = document.createElement( 'input' );

			input.type = 'radio';
			input.name = `anudan-frequency-${ this.fundId }`;
			input.value = frequency;
			input.checked = frequency === this.form.default_frequency;
			input.addEventListener( 'change', () => this.updateSummary() );

			label.appendChild( input );
			// The server's plain label ("One-off"), never the stored identifier ("one_time").
			label.appendChild( document.createTextNode( ( this.form.frequency_labels || {} )[ frequency ] || frequency.replace( '_', ' ' ) ) );
			wrapper.appendChild( label );
		} );

		this.frequencyGroup = wrapper;

		return holder;
	}

	/**
	 * How often, as it follows an amount: "every month", "every week"…; '' for a one-time gift.
	 *
	 * @param {string} frequency
	 * @return {string}
	 */
	everyWords( frequency ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		return {
			weekly: __( 'every week' ),
			monthly: __( 'every month' ),
			quarterly: __( 'every quarter' ),
			annual: __( 'every year' ),
		}[ frequency ] || '';
	}

	/**
	 * D1 (0.9.41-rc3): the badge beside a recurring total: "Monthly", "Weekly"…; '' for a one-time gift. Never "a month".
	 *
	 * @param {string} frequency
	 * @return {string}
	 */
	perWords( frequency ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		return {
			weekly: __( 'Weekly' ),
			monthly: __( 'Monthly' ),
			quarterly: __( 'Quarterly' ),
			annual: __( 'Yearly' ),
		}[ frequency ] || '';
	}

	/**
	 * R2-05 (rc2): with suggested amounts, the "Your amount" box is shown only once "Other amount" is chosen. An amount put in the
	 * box by anything else (a widget's link, a moved PayPal gift, the monthly suggestion, a draft) is choosing "Other amount" too.
	 * With no suggested amounts the box is the only way to give, and always shown.
	 *
	 * @return {void}
	 */
	syncOwnAmount() {
		if ( ! this.customAmountWrapper || ! this.otherAmountRadio || ! this.customAmountInput ) {
			return;
		}

		if ( '' !== this.customAmountInput.value.trim() ) {
			this.otherAmountRadio.checked = true;
		}

		this.customAmountWrapper.hidden = ! this.otherAmountRadio.checked;
	}

	/**
	 * Real radio inputs, per WCAG requirement in WP-18's own spec — never a `<div>` styled to
	 * look selectable.
	 *
	 * @return {HTMLElement}
	 */
	buildAmounts() {
		const wrapper = document.createElement( 'div' );

		wrapper.className = 'anudan-checkout__amounts';
		wrapper.setAttribute( 'role', 'radiogroup' );
		wrapper.setAttribute(
			'aria-label',
			window.wp && window.wp.i18n ? window.wp.i18n.__( 'Choose an amount', 'anudan' ) : 'Choose an amount'
		);

		this.form.amounts.forEach( ( tier ) => {
			const label = document.createElement( 'label' );

			label.className = 'anudan-checkout__amount';

			const input = document.createElement( 'input' );

			input.type = 'radio';
			input.name = `anudan-amount-${ this.fundId }`;
			input.value = String( tier.id );

			input.addEventListener( 'change', () => {
				this.selectedTierId = tier.id;
				this.customAmount = null;

				// R2-05: a suggested amount puts the "Your amount" box away, emptied.
				if ( this.customAmountInput ) {
					this.customAmountInput.value = '';
				}

				// A preset replaces a typed amount, and with it any refusal of that amount.
				this.clearAmountError();
				this.updateSummary();
			} );

			const value = document.createElement( 'span' );

			value.className = 'anudan-checkout__amount-value';
			// Q1 (0.9.40): a whole amount on its tile as the design writes it, "$25"; cents only when there are any.
			value.textContent = this.formatTileAmount( tier.amount );

			label.appendChild( input );
			label.appendChild( value );

			if ( tier.label ) {
				const tierLabel = document.createElement( 'span' );

				tierLabel.className = 'anudan-checkout__amount-label';
				tierLabel.textContent = tier.label;
				label.appendChild( tierLabel );
			}

			wrapper.appendChild( label );
		} );

		// Q1 (0.9.40): "Other amount" is one of the choices; picking it puts the cursor in the box for the donor's own amount.
		if ( this.form.allow_custom_amount && this.form.amounts.length > 0 ) {
			const other = document.createElement( 'label' );
			const input = document.createElement( 'input' );
			const words = document.createElement( 'span' );

			other.className = 'anudan-checkout__amount anudan-checkout__amount--other';
			input.type = 'radio';
			input.name = `anudan-amount-${ this.fundId }`;
			input.value = 'other';
			input.addEventListener( 'change', () => {
				// R2-05: "Your amount" appears, with the cursor in it.
				this.syncOwnAmount();

				if ( this.customAmountInput ) {
					this.customAmountInput.focus();
				}
			} );
			words.className = 'anudan-checkout__amount-value';
			words.textContent = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Other amount', 'anudan' ) : 'Other amount';
			other.appendChild( input );
			other.appendChild( words );
			wrapper.appendChild( other );
			this.otherAmountRadio = input;
		}

		if ( this.form.allow_custom_amount ) {
			const customField = document.createElement( 'div' );

			customField.className = 'anudan-checkout__field';

			const customLabel = document.createElement( 'label' );

			customLabel.className = 'anudan-checkout__field-label';

			// The currency in words, for a screen reader and for anyone unsure what the symbol means.
			const currency = this.form.currency || 'USD';
			const ownAmount = this.form.amounts.length > 0
				? ( window.wp && window.wp.i18n ? window.wp.i18n.__( 'Your amount, in %s', 'anudan' ) : 'Your amount, in %s' )
				: ( window.wp && window.wp.i18n ? window.wp.i18n.__( 'Your gift, in %s', 'anudan' ) : 'Your gift, in %s' );

			customLabel.textContent = ownAmount.replace( '%s', currency );
			// This label is a sibling of the input, not a wrapper (buildTextField()'s implicit
			// shape), so the association has to be explicit — an axe-core scan of criterion 11
			// (09-PHASE-4-BUILD.md) found it missing: `for`/`id` were never set at all.
			customLabel.htmlFor = 'anudan-checkout-custom-amount';

			this.customAmountInput = document.createElement( 'input' );
			this.customAmountInput.type = 'text';
			this.customAmountInput.id = 'anudan-checkout-custom-amount';
			this.customAmountInput.inputMode = 'decimal';
			this.customAmountInput.className = 'anudan-checkout__field-input anudan-checkout__custom-amount';

			this.customAmountInput.addEventListener( 'input', () => {
				this.selectedTierId = null;
				this.customAmount = this.parseMinorUnits( this.customAmountInput.value );

				// Typing an amount is choosing "Other amount".
				if ( this.otherAmountRadio && '' !== this.customAmountInput.value.trim() ) {
					this.otherAmountRadio.checked = true;
				}

				// C8: a refusal was about the amount as it was; changed, the box turns green and Continue is judged afresh.
				this.clearAmountError();
				this.updateSummary();
			} );

			// The range the fund takes, under the label: "From $5.00 to $10,000.00".
			if ( Number.isInteger( this.form.min_amount ) && this.form.min_amount > 0 ) {
				const range = document.createElement( 'p' );
				const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

				range.className = 'anudan-checkout__field-help';
				range.id = 'anudan-checkout-custom-amount-range';
				range.textContent = Number.isInteger( this.form.max_amount ) && this.form.max_amount > 0
					? __( 'From %1$s to %2$s' ).replace( '%1$s', this.formatMinorUnits( this.form.min_amount, currency ) ).replace( '%2$s', this.formatMinorUnits( this.form.max_amount, currency ) )
					: __( 'At least %s' ).replace( '%s', this.formatMinorUnits( this.form.min_amount, currency ) );
				this.customAmountRange = range;
				this.customAmountInput.setAttribute( 'aria-describedby', range.id );
			}

			// The currency's symbol in front of the box, so the amount is never shown without its currency (visual review item 14).
			const money = document.createElement( 'div' );
			const symbol = document.createElement( 'span' );

			money.className = 'anudan-checkout__money';
			symbol.className = 'anudan-checkout__money-symbol';
			symbol.setAttribute( 'aria-hidden', 'true' );
			symbol.textContent = this.currencySymbol( currency );
			money.appendChild( symbol );
			money.appendChild( this.customAmountInput );

			customField.appendChild( customLabel );

			if ( this.customAmountRange ) {
				customField.appendChild( this.customAmountRange );
			}

			customField.appendChild( money );

			// C5 (0.9.40): when the amount typed is below what the choices cost, the server raises it; this says so.
			this.raisedNote = document.createElement( 'p' );
			this.raisedNote.className = 'anudan-checkout__raised-note';
			this.raisedNote.setAttribute( 'aria-live', 'polite' );
			this.raisedNote.hidden = true;
			customField.appendChild( this.raisedNote );

			this.customAmountWrapper = customField;
		}

		this.amountsGroup = wrapper;

		const container = document.createDocumentFragment();

		if ( this.form.amounts.length > 0 ) {
			const choose = document.createElement( 'p' );

			choose.className = 'anudan-checkout__field-label';
			choose.setAttribute( 'aria-hidden', 'true' );
			choose.textContent = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Choose an amount', 'anudan' ) : 'Choose an amount';
			container.appendChild( choose );
		}

		container.appendChild( wrapper );

		if ( this.customAmountWrapper ) {
			container.appendChild( this.customAmountWrapper );
		}

		const holder = document.createElement( 'div' );

		holder.appendChild( container );

		return holder;
	}

	/**
	 * The gift cart's lines (0.9.0): per line, a fund and an amount — one of the fund's suggested
	 * amounts, or the donor's own where the fund takes one. The server checks every line again and
	 * computes every figure (A-1); this keeps only what the donor chose.
	 *
	 * @return {HTMLElement}
	 */
	buildCartLines() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const holder = document.createElement( 'div' );
		const funds = ( this.form.cart && this.form.cart.funds ) || [];

		holder.className = 'anudan-checkout__cart';

		if ( ! funds.length ) {
			const none = document.createElement( 'p' );

			none.className = 'anudan-checkout__cart-empty';
			none.textContent = __( 'No funds can be given to together here right now. Please give to a fund on its own page.' );
			holder.appendChild( none );

			return holder;
		}

		this.cartList = document.createElement( 'div' );
		this.cartList.className = 'anudan-checkout__cart-lines';

		this.cartAdd = document.createElement( 'button' );
		this.cartAdd.type = 'button';
		this.cartAdd.className = 'anudan-checkout__button anudan-checkout__button--secondary anudan-checkout__cart-add';
		this.cartAdd.textContent = __( 'Add another fund' );
		this.cartAdd.addEventListener( 'click', () => {
			this.cartLines.push( { fundId: null, tierId: null, customAmount: null, other: false } );
			this.renderCartLines();

			const selects = this.cartList.querySelectorAll( '.anudan-checkout__cart-fund' );

			if ( selects.length ) {
				selects[ selects.length - 1 ].focus();
			}
		} );

		holder.appendChild( this.cartList );
		holder.appendChild( this.cartAdd );

		this.cartLines = [ { fundId: 1 === funds.length ? funds[ 0 ].id : null, tierId: null, customAmount: null, other: false } ];
		this.renderCartLines();

		return holder;
	}

	/**
	 * Draws every cart line from `this.cartLines`. Redrawn whole on add and remove, so each line's
	 * position — which the server names in its messages (`lines.N.amount`) — is always its index.
	 *
	 * @return {void}
	 */
	renderCartLines() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const funds = this.form.cart.funds;
		const max = Math.min( this.form.cart.max_lines || 10, funds.length );

		this.cartList.innerHTML = '';

		this.cartLines.forEach( ( line, index ) => {
			const fund = funds.find( ( candidate ) => candidate.id === line.fundId ) || null;
			const row = document.createElement( 'div' );
			const fundField = document.createElement( 'div' );
			const fundLabel = document.createElement( 'label' );
			const fundSelect = document.createElement( 'select' );
			const fundId = `${ this.idPrefix }-cart-${ index }-fund`;

			row.className = 'anudan-checkout__cart-line';
			row.setAttribute( 'role', 'group' );
			row.setAttribute( 'aria-label', __( 'Gift %s' ).replace( '%s', String( index + 1 ) ) );

			fundField.className = 'anudan-checkout__field';
			fundField.setAttribute( 'data-anudan-field', `lines.${ index }.fund_id` );
			fundLabel.className = 'anudan-checkout__field-label';
			fundLabel.htmlFor = fundId;
			fundLabel.textContent = __( 'Fund' );
			fundSelect.id = fundId;
			fundSelect.className = 'anudan-checkout__field-input anudan-checkout__cart-fund';

			const placeholder = document.createElement( 'option' );

			placeholder.value = '';
			placeholder.textContent = __( 'Choose a fund' );
			fundSelect.appendChild( placeholder );

			funds.forEach( ( candidate ) => {
				const option = document.createElement( 'option' );
				const decoder = document.createElement( 'textarea' );

				decoder.innerHTML = candidate.title;
				option.value = String( candidate.id );
				option.textContent = decoder.value;
				option.selected = candidate.id === line.fundId;
				option.disabled = candidate.id !== line.fundId && this.cartLines.some( ( other ) => other.fundId === candidate.id );
				fundSelect.appendChild( option );
			} );

			fundSelect.addEventListener( 'change', () => {
				line.fundId = fundSelect.value ? Number( fundSelect.value ) : null;
				line.tierId = null;
				line.customAmount = null;
				line.customText = '';
				line.other = false;
				this.renderCartLines();
				this.updateSummary();
			} );

			fundField.appendChild( fundLabel );
			fundField.appendChild( fundSelect );
			row.appendChild( fundField );

			if ( fund ) {
				row.appendChild( this.buildCartAmount( line, fund, index ) );
			}

			if ( this.cartLines.length > 1 ) {
				const remove = document.createElement( 'button' );

				remove.type = 'button';
				remove.className = 'anudan-checkout__cart-remove';
				remove.textContent = __( 'Remove' );
				remove.setAttribute( 'aria-label', __( 'Remove gift %s' ).replace( '%s', String( index + 1 ) ) );
				remove.addEventListener( 'click', () => {
					this.cartLines.splice( index, 1 );
					this.renderCartLines();
					this.updateSummary();

					const next = this.cartList.querySelector( '.anudan-checkout__cart-fund' );

					if ( next ) {
						next.focus();
					}
				} );
				row.appendChild( remove );
			}

			this.cartList.appendChild( row );
		} );

		this.cartAdd.hidden = this.cartLines.length >= max;
	}

	/**
	 * One cart line's amount: the fund's suggested amounts in a list, with "Another amount" when the
	 * fund takes the donor's own, which then shows a box for it.
	 *
	 * @param {Object} line  The line's state.
	 * @param {Object} fund  The fund, from `form.cart.funds`.
	 * @param {number} index The line's position.
	 * @return {HTMLElement}
	 */
	buildCartAmount( line, fund, index ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const field = document.createElement( 'div' );
		const currency = this.form.currency || 'USD';
		const amountId = `${ this.idPrefix }-cart-${ index }-amount`;
		const customId = `${ this.idPrefix }-cart-${ index }-custom`;

		field.className = 'anudan-checkout__field';
		field.setAttribute( 'data-anudan-field', `lines.${ index }.amount` );

		const showCustom = fund.allow_custom_amount && ( line.other || ! fund.amounts.length );

		if ( fund.amounts.length ) {
			const label = document.createElement( 'label' );
			const select = document.createElement( 'select' );
			const placeholder = document.createElement( 'option' );

			label.className = 'anudan-checkout__field-label';
			label.htmlFor = amountId;
			label.textContent = __( 'Amount' );
			select.id = amountId;
			select.className = 'anudan-checkout__field-input anudan-checkout__cart-amount';
			placeholder.value = '';
			placeholder.textContent = __( 'Choose an amount' );
			select.appendChild( placeholder );

			fund.amounts.forEach( ( tier ) => {
				const option = document.createElement( 'option' );

				option.value = String( tier.id );
				option.textContent = this.formatMinorUnits( tier.amount, currency );
				option.selected = tier.id === line.tierId;
				select.appendChild( option );
			} );

			if ( fund.allow_custom_amount ) {
				const other = document.createElement( 'option' );

				other.value = 'other';
				other.textContent = __( 'Another amount' );
				other.selected = line.other;
				select.appendChild( other );
			}

			select.addEventListener( 'change', () => {
				const wasOther = line.other;

				line.other = 'other' === select.value;
				line.tierId = select.value && ! line.other ? Number( select.value ) : null;
				line.customAmount = null;
				line.customText = '';

				if ( wasOther !== line.other ) {
					this.renderCartLines();

					const box = this.cartList.querySelector( `#${ customId }` );

					if ( box ) {
						box.focus();
					}
				}

				this.updateSummary();
			} );

			field.appendChild( label );
			field.appendChild( select );
		}

		if ( showCustom ) {
			const label = document.createElement( 'label' );
			const input = document.createElement( 'input' );
			const money = document.createElement( 'div' );
			const symbol = document.createElement( 'span' );

			label.className = 'anudan-checkout__field-label';
			label.htmlFor = customId;
			label.textContent = __( 'Your gift, in %s' ).replace( '%s', currency );
			input.type = 'text';
			input.id = customId;
			input.inputMode = 'decimal';
			input.className = 'anudan-checkout__field-input anudan-checkout__custom-amount';

			// What the donor typed, kept as typed, so a redraw shows it unchanged.
			input.value = line.customText || '';

			input.addEventListener( 'input', () => {
				line.tierId = null;
				line.customText = input.value;
				line.customAmount = this.parseMinorUnits( input.value );
				this.updateSummary();
			} );

			money.className = 'anudan-checkout__money';
			symbol.className = 'anudan-checkout__money-symbol';
			symbol.setAttribute( 'aria-hidden', 'true' );
			symbol.textContent = this.currencySymbol( currency );
			money.appendChild( symbol );
			money.appendChild( input );
			field.appendChild( label );
			field.appendChild( money );
		}

		return field;
	}

	/**
	 * Whether every cart line has a fund and an amount, so there is something to quote.
	 *
	 * @return {boolean}
	 */
	cartComplete() {
		return this.cartLines.length > 0 && this.cartLines.every( ( line ) => null !== line.fundId && ( null !== line.tierId || null !== line.customAmount ) );
	}

	/**
	 * The cart's lines as the server reads them: a fund and a tier or a typed amount, never a total.
	 *
	 * @return {Array<{fund_id: (number|null), amount_tier_id: (number|null), custom_amount: (number|null)}>}
	 */
	cartPayloadLines() {
		return this.cartLines.map( ( line ) => ( {
			fund_id: line.fundId,
			amount_tier_id: line.tierId,
			custom_amount: line.customAmount,
		} ) );
	}

	/**
	 * WP-37 (M-MONTHS): the period chip UI. `06-DESIGN-SYSTEM.md`'s own note — "Selected months
	 * appear as removable chips … the chip is the affordance, no separate remove-all" — is why a
	 * chip's own click removes it rather than a separate control existing beside each one. An
	 * "Add a period" dropdown is the only way to add one; it lists only periods not already
	 * selected, and disables itself once `max_selections` is reached.
	 *
	 * @return {HTMLElement}
	 */
	buildMonthChips() {
		const wrapper = document.createElement( 'div' );

		wrapper.className = 'anudan-checkout__months';

		const label = document.createElement( 'span' );

		label.className = 'anudan-checkout__field-label';
		label.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'Which periods does this gift cover?', 'anudan' )
			: 'Which periods does this gift cover?';
		wrapper.appendChild( label );

		this.monthChipsList = document.createElement( 'div' );
		this.monthChipsList.className = 'anudan-checkout__months-chips';
		this.monthChipsList.setAttribute( 'role', 'list' );
		wrapper.appendChild( this.monthChipsList );

		this.monthAddSelect = document.createElement( 'select' );
		this.monthAddSelect.className = 'anudan-checkout__months-add';
		this.monthAddSelect.setAttribute(
			'aria-label',
			window.wp && window.wp.i18n ? window.wp.i18n.__( 'Add a period', 'anudan' ) : 'Add a period'
		);

		this.monthAddSelect.addEventListener( 'change', () => {
			const key = this.monthAddSelect.value;

			if ( '' === key ) {
				return;
			}

			this.selectedPeriods.push( key );
			this.renderMonthChips();
			this.updateSummary();
			this.saveDraftNow();
		} );

		wrapper.appendChild( this.monthAddSelect );

		this.renderMonthChips();

		return wrapper;
	}

	/**
	 * Redraws the chip row and the "add a period" dropdown from `this.selectedPeriods` — called
	 * after every add or removal rather than patched incrementally, since the whole point of a
	 * chip UI this small is that a full redraw is cheap and never has a stale option to forget
	 * to update.
	 *
	 * @return {void}
	 */
	renderMonthChips() {
		const periods = this.form.months.periods;
		const byKey = new Map( periods.map( ( period ) => [ period.key, period.label ] ) );

		this.monthChipsList.textContent = '';

		this.selectedPeriods.forEach( ( key ) => {
			const chip = document.createElement( 'button' );

			chip.type = 'button';
			chip.className = 'anudan-checkout__months-chip';
			chip.setAttribute( 'role', 'listitem' );
			chip.textContent = byKey.get( key ) || key;
			chip.setAttribute(
				'aria-label',
				( window.wp && window.wp.i18n
					? window.wp.i18n.__( 'Remove %s', 'anudan' )
					: 'Remove %s'
				).replace( '%s', byKey.get( key ) || key )
			);

			chip.addEventListener( 'click', () => {
				this.selectedPeriods = this.selectedPeriods.filter( ( selected ) => selected !== key );
				this.renderMonthChips();
				this.updateSummary();
				this.saveDraftNow();
			} );

			this.monthChipsList.appendChild( chip );
		} );

		const atMax = 'number' === typeof this.form.months.max_selections
			&& this.selectedPeriods.length >= this.form.months.max_selections;

		this.monthAddSelect.textContent = '';

		const placeholder = document.createElement( 'option' );

		placeholder.value = '';
		placeholder.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'Add a period…', 'anudan' )
			: 'Add a period…';
		this.monthAddSelect.appendChild( placeholder );

		periods
			.filter( ( period ) => ! this.selectedPeriods.includes( period.key ) )
			.forEach( ( period ) => {
				const option = document.createElement( 'option' );

				option.value = period.key;
				option.textContent = period.label;
				this.monthAddSelect.appendChild( option );
			} );

		this.monthAddSelect.hidden = atMax;
	}

	/**
	 * @return {HTMLElement}
	 */
	buildFeeCoverage() {
		const wrapper = document.createElement( 'label' );

		wrapper.className = 'anudan-checkout__fee-coverage';
		wrapper.hidden = ! this.form.fee_coverage.enabled;

		this.feeCoverageInput = document.createElement( 'input' );
		this.feeCoverageInput.type = 'checkbox';
		this.feeCoverageInput.checked = false;

		this.feeCoverageInput.addEventListener( 'change', () => {
			this.coverFees = this.feeCoverageInput.checked;
			this.showTotalForFeeBox();
			this.updateSummary( true );
		} );

		this.feeCoverageLabel = document.createElement( 'span' );
		this.feeCoverageLabel.textContent = this.feeCoverageMessage();

		wrapper.appendChild( this.feeCoverageInput );
		wrapper.appendChild( this.feeCoverageLabel );

		return wrapper;
	}

	/**
	 * @param {string} key
	 * @param {string} label
	 * @param {string} autocomplete
	 * @param {boolean} required
	 * @param {string} [type]
	 * @return {HTMLElement}
	 */
	buildTextField( key, label, autocomplete, required, type = 'text', help = '' ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const wrapper = document.createElement( 'div' );

		wrapper.className = 'anudan-checkout__field';

		const labelEl = document.createElement( 'label' );

		labelEl.className = 'anudan-checkout__field-label';
		labelEl.appendChild( document.createTextNode( label ) );

		// Q1 (0.9.40): a field says whether it is needed, in words beside its name. Drawn by the stylesheet from this attribute
		// (and read with the name), so the label's own text stays the field's name for every script that looks for it by it.
		labelEl.setAttribute( 'data-anudan-need', required ? __( '(required)' ) : __( '(optional)' ) );

		const input = document.createElement( 'input' );

		input.type = type;
		input.id = `${ this.idPrefix }-${ key }`;
		input.className = 'anudan-checkout__field-input';
		input.autocomplete = autocomplete;
		input.required = required;
		labelEl.htmlFor = input.id;
		wrapper.appendChild( labelEl );

		// The hint is said after the field's name, not as part of it.
		if ( '' !== help ) {
			const hint = document.createElement( 'p' );

			hint.className = 'anudan-checkout__field-help';
			hint.id = `${ input.id }-help`;
			hint.textContent = help;
			wrapper.appendChild( hint );
			input.setAttribute( 'aria-describedby', hint.id );
		}

		wrapper.appendChild( input );

		this.fields[ key ] = input;

		return wrapper;
	}

	/**
	 * R2-11 (rc2): hidden, optional or required, as the fund's Donor details say (the site's setting unless the fund chooses).
	 * The server resolves it; a schema from before rc2 carries none, and the phone was then optional.
	 *
	 * @return {string}
	 */
	phoneMode() {
		const mode = this.form && this.form.phone ? this.form.phone.mode : '';

		return [ 'hidden', 'optional', 'required' ].includes( mode ) ? mode : 'optional';
	}

	/**
	 * The phone field, or null when the fund does not ask for one (R2-11).
	 *
	 * @return {HTMLElement|null}
	 */
	buildPhone() {
		const mode = this.phoneMode();

		if ( 'hidden' === mode ) {
			return null;
		}

		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const wrapper = document.createElement( 'div' );

		wrapper.className = 'anudan-checkout__field';

		const labelEl = document.createElement( 'span' );

		labelEl.className = 'anudan-checkout__field-label';
		labelEl.textContent = __( 'Phone' );
		// Q1 (0.9.40): optional or required, said as every field says it.
		labelEl.setAttribute( 'data-anudan-need', 'required' === mode ? __( '(required)' ) : __( '(optional)' ) );

		this.phoneField = buildPhoneField( ( value ) => {
			this.fields.donor_phone = { value };
		} );

		wrapper.appendChild( labelEl );
		wrapper.appendChild( this.phoneField.element );

		return wrapper;
	}

	/**
	 * Batch A: the fund's two optional questions — a company name (a gift made on behalf of a company) and a
	 * comment. Each is shown only when the fund asks for it; neither is ever required.
	 *
	 * @return {HTMLElement}
	 */
	buildAskedFields() {
		const wrapper = document.createElement( 'div' );
		const ask = this.form.ask || {};
		const i18n = window.wp && window.wp.i18n ? window.wp.i18n : null;

		this.companyInput = null;
		this.commentInput = null;

		if ( ask.company && ! this.cart ) {
			const field = document.createElement( 'div' );
			const label = document.createElement( 'label' );

			field.className = 'anudan-checkout__field';
			label.className = 'anudan-checkout__field-label';
			label.textContent = i18n
				? i18n.__( 'Company name (optional, if you give on behalf of a company)', 'anudan' )
				: 'Company name (optional, if you give on behalf of a company)';

			this.companyInput = document.createElement( 'input' );
			this.companyInput.type = 'text';
			this.companyInput.name = 'company_name';
			this.companyInput.className = 'anudan-checkout__field-input';
			this.companyInput.autocomplete = 'organization';
			this.companyInput.maxLength = 190;

			label.appendChild( this.companyInput );
			field.appendChild( label );
			wrapper.appendChild( field );
		}

		if ( ask.comment && ! this.cart ) {
			const field = document.createElement( 'div' );
			const label = document.createElement( 'label' );

			field.className = 'anudan-checkout__field';
			label.className = 'anudan-checkout__field-label';
			label.textContent = i18n ? i18n.__( 'Comment (optional)', 'anudan' ) : 'Comment (optional)';

			this.commentInput = document.createElement( 'textarea' );
			this.commentInput.name = 'donor_comment';
			this.commentInput.className = 'anudan-checkout__field-input';
			this.commentInput.rows = 3;
			this.commentInput.maxLength = 1000;

			label.appendChild( this.commentInput );
			field.appendChild( label );
			wrapper.appendChild( field );
		}

		return wrapper;
	}

	/**
	 * Batch B; after rc5, the owner's decision: one consent checkbox, "Stay in touch", with the words the server sent, ticked
	 * when the form opens if the site says so (on by default), and an optional help line under it. Asked once in a browser: after
	 * a gift with the answer was accepted, a cookie says so and it is not shown again (on the tablet page, shared by every donor,
	 * it is always shown). Nothing is shown when the site asks nothing.
	 *
	 * @return {HTMLElement}
	 */
	buildConsentFields() {
		const wrapper = document.createElement( 'div' );
		const consent = this.form.consent && 'object' === typeof this.form.consent && ! Array.isArray( this.form.consent ) ? this.form.consent : null;

		this.consentInputs = [];

		if ( ! consent || ! consent.label || ( ! this.tablet && /(?:^|;\s*)anudan_consent_answered=1/.test( document.cookie ) ) ) {
			return wrapper;
		}

		const field = document.createElement( 'div' );
		const label = document.createElement( 'label' );
		const input = document.createElement( 'input' );

		field.className = 'anudan-checkout__field anudan-checkout__consent';
		label.className = 'anudan-checkout__choice';
		input.type = 'checkbox';
		input.name = 'consent';
		input.value = 'yes';
		input.checked = true === consent.ticked;

		label.appendChild( input );
		label.appendChild( document.createTextNode( ' ' + consent.label ) );
		field.appendChild( label );

		if ( consent.help ) {
			const help = document.createElement( 'p' );

			help.className = 'anudan-checkout__field-help';
			help.id = `${ this.idPrefix }-consent-help`;
			help.textContent = consent.help;
			input.setAttribute( 'aria-describedby', help.id );
			field.appendChild( help );
		}

		this.consentInputs.push( input );
		wrapper.appendChild( field );

		return wrapper;
	}

	/**
	 * The consent answer for the intent request: whether the box was left ticked, that it was shown, and on the tablet page that
	 * it was given there (the server decides how it records it).
	 *
	 * @return {{consent?: boolean, consent_shown?: boolean, consent_how?: string}}
	 */
	consentPayload() {
		if ( ! this.consentInputs || 0 === this.consentInputs.length ) {
			return {};
		}

		return {
			consent: this.consentInputs[ 0 ].checked,
			consent_shown: true,
			...( this.tablet ? { consent_how: 'tablet_page' } : {} ),
		};
	}

	/**
	 * Remembers in this browser, for a year, that the consent question was answered, so it is not asked again. After rc5 (the
	 * consent audit): only once the gift was accepted (it was set as Continue was pressed, so a refused payment hid the boxes on
	 * every later visit), and never on the tablet page, where every donor is a different person on the same device.
	 *
	 * @return {void}
	 */
	rememberConsentAnswered() {
		if ( this.tablet || ! this.consentInputs || 0 === this.consentInputs.length ) {
			return;
		}

		document.cookie = 'anudan_consent_answered=1; path=/; max-age=31536000; SameSite=Lax';
	}

	/**
	 * The company name and comment for the intent request, when typed (Batch A).
	 *
	 * @return {{company_name?: string, donor_comment?: string}}
	 */
	askedPayload() {
		const payload = {};

		if ( this.companyInput && this.companyInput.value.trim() ) {
			payload.company_name = this.companyInput.value.trim();
		}

		if ( this.commentInput && this.commentInput.value.trim() ) {
			payload.donor_comment = this.commentInput.value.trim();
		}

		return payload;
	}

	/**
	 * Progressive disclosure — unchecked donors never see the field (PRD §1.5.4).
	 *
	 * @return {HTMLElement}
	 */
	buildRegistrationNumber() {
		const wrapper = document.createElement( 'div' );

		if ( 'hidden' === this.form.registration_number.mode ) {
			return wrapper;
		}

		const toggleLabel = document.createElement( 'label' );

		toggleLabel.className = 'anudan-checkout__registration-toggle';

		const toggle = document.createElement( 'input' );

		toggle.type = 'checkbox';

		const field = document.createElement( 'div' );

		field.className = 'anudan-checkout__field anudan-checkout__registration-field';
		field.hidden = true;

		const help = document.createElement( 'p' );

		help.className = 'anudan-checkout__field-help';
		help.textContent = this.form.registration_number.help || '';

		this.registrationInput = document.createElement( 'input' );
		this.registrationInput.type = 'text';
		this.registrationInput.className = 'anudan-checkout__field-input';
		this.registrationInput.required = 'required' === this.form.registration_number.mode;

		toggle.addEventListener( 'change', () => {
			field.hidden = ! toggle.checked;
		} );

		toggleLabel.appendChild( toggle );
		// The words are the administrator's (Settings › Checkout › Registration number); never an organization's own, in code.
		toggleLabel.appendChild(
			document.createTextNode(
				this.form.registration_number.toggle_label ||
					( window.wp && window.wp.i18n
						? window.wp.i18n.__( 'I have a registration number', 'anudan' )
						: 'I have a registration number' )
			)
		);

		field.appendChild( help );
		field.appendChild( this.registrationInput );

		wrapper.appendChild( toggleLabel );
		wrapper.appendChild( field );

		return wrapper;
	}

	/**
	 * WP-44: "the donor chooses when more than one gateway is enabled for a fund." Returns null
	 * — nothing to render — when `this.form.gateways` has zero or one entries: zero is
	 * {@see this.applyGatewayAvailability}'s own case, and one leaves nothing to choose between.
	 * In both of those cases `this.selectedGateway` is still set here, to whatever the single
	 * entry (or nothing) is, so {@see this.currentGateway} never has to special-case "there was
	 * no picker."
	 *
	 * Real radio inputs, the identical WCAG requirement {@see this.buildAmounts}'s own docblock
	 * states. The donor's previous choice for this fund, if any and still offered, is restored
	 * from `gateway-session.js` — WP-44's "selection persists within the checkout session."
	 *
	 * @return {HTMLElement|null}
	 */
	buildGatewaySelector() {
		const gateways = this.availableGateways();

		if ( gateways.length <= 1 ) {
			this.selectedGateway = gateways[ 0 ] || null;

			return null;
		}

		const remembered = loadGatewaySelection( this.fundId );
		const initial = remembered && gateways.includes( remembered ) ? remembered : gateways[ 0 ];

		this.selectedGateway = initial;

		const wrapper = document.createElement( 'fieldset' );

		wrapper.className = 'anudan-checkout__gateway-select';

		const legend = document.createElement( 'legend' );

		legend.className = 'anudan-checkout__field-label';
		legend.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'How would you like to pay?', 'anudan' )
			: 'How would you like to pay?';
		wrapper.appendChild( legend );

		const labels = this.form.gateway_labels || {};

		gateways.forEach( ( id ) => {
			const label = document.createElement( 'label' );

			label.className = 'anudan-checkout__gateway-option';

			const input = document.createElement( 'input' );

			input.type = 'radio';
			input.name = `${ this.idPrefix }-gateway`;
			input.value = id;
			input.checked = id === initial;

			input.addEventListener( 'change', () => {
				this.selectedGateway = id;
				saveGatewaySelection( this.fundId, id );
				this.updateSummary();
			} );

			label.appendChild( input );
			label.appendChild( document.createTextNode( ' ' + ( labels[ id ] || id ) ) );
			wrapper.appendChild( label );
		} );

		return wrapper;
	}

	/**
	 * WP-38 (M-SERVICE): the third-party consent notice, above the fund's own questions (PRD
	 * §1.12b: "the donor is submitting another person's details and must be told what happens to
	 * them"). The text is the server's (`Donations\ServiceRequestPolicy::consent_notice()`, never
	 * empty) and is set with `textContent`, never as markup — an administrator's own wording is
	 * plain text end to end.
	 *
	 * @return {HTMLElement}
	 */
	buildServiceNotice() {
		const notice = document.createElement( 'div' );

		notice.className = 'anudan-checkout__service-notice';
		notice.setAttribute( 'role', 'note' );
		notice.id = `${ this.idPrefix }-service-notice`;

		// Q1 (0.9.40): the design's info box, titled; the fund's own notice under the title, whole (it says what happens to the
		// details), and still above the first question, so it is read before anything about the other person is typed (WP-38).
		const title = document.createElement( 'p' );
		const text = document.createElement( 'p' );

		title.className = 'anudan-checkout__service-notice-title';
		title.textContent = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Your request is private.', 'anudan' ) : 'Your request is private.';
		text.className = 'anudan-checkout__service-notice-text';
		text.textContent = this.form.service.consent_notice;
		notice.appendChild( title );
		notice.appendChild( text );

		return notice;
	}

	/**
	 * WP-42 (M-ANON): the "give anonymously" checkbox. Clear labelling of the scope
	 * (`12-PHASE-6-BUILD.md`'s own requirement) — this hides the donor's name from public
	 * displays only; `Funds\Modules\AnonModule`'s own docblock is what actually enforces that a
	 * receipt, export, or admin view is never affected, so this control only needs to say so, not
	 * implement it.
	 *
	 * @return {HTMLElement}
	 */
	buildAnonCheckbox() {
		const wrapper = document.createElement( 'div' );

		wrapper.className = 'anudan-checkout__anon';

		const label = document.createElement( 'label' );
		const input = document.createElement( 'input' );

		input.type = 'checkbox';
		input.checked = this.isAnonymous;
		input.addEventListener( 'change', () => {
			this.isAnonymous = input.checked;
			this.saveDraftNow();
		} );

		this.anonCheckboxInput = input;

		label.appendChild( input );
		label.appendChild( document.createTextNode(
			' ' + ( window.wp && window.wp.i18n
				? window.wp.i18n.__( 'Give anonymously', 'anudan' )
				: 'Give anonymously' )
		) );
		wrapper.appendChild( label );

		const description = document.createElement( 'p' );

		description.className = 'anudan-checkout__field-help';
		description.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'Don’t show my name publicly with this gift. Your name still appears on your receipt and in our own records.', 'anudan' )
			: 'Don’t show my name publicly with this gift. Your name still appears on your receipt and in our own records.';
		wrapper.appendChild( description );

		return wrapper;
	}

	/**
	 * WP-42 (M-DEDICATE): "in honour of / in memory of," an optional message, and an optional
	 * notification email to a third party. Leaving the name blank means the donor is not
	 * dedicating this gift at all — the same "presence of a name is the signal" rule
	 * `Funds\Modules\DedicateModule::is_dedicating()`'s own docblock states server-side; nothing
	 * here needs a separate "dedicate this gift?" toggle to duplicate that.
	 *
	 * The amount is **never** offered here as part of the notification — WP-42's own
	 * requirement, "omitted by default and includable only by explicit donor choice," is the
	 * `includeAmount` checkbox below, which only ever adds the amount, never removes anything
	 * else the donor typed.
	 *
	 * @return {HTMLElement}
	 */
	buildDedicateFields() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const wrapper = document.createElement( 'div' );

		wrapper.className = 'anudan-checkout__dedicate';

		/*
		 * Q1 (0.9.40), on the design: "Dedicate this gift" opens the part. Closed, the name is sent empty, which the server reads
		 * as not dedicating (above); the answers typed are kept, so ticking it again brings them back.
		 */
		const open = this.buildCheckRow( `${ this.idPrefix }-dedicate-on`, __( 'Dedicate this gift' ), '' );
		const panel = document.createElement( 'div' );

		panel.className = 'anudan-checkout__dedicate-panel';
		panel.hidden = true;
		open.input.addEventListener( 'change', () => {
			panel.hidden = ! open.input.checked;

			if ( open.input.checked ) {
				const first = panel.querySelector( 'input:not([type="checkbox"]), textarea' );

				if ( first ) {
					first.focus();
				}
			}
		} );
		wrapper.appendChild( open.row );
		wrapper.appendChild( panel );

		const types = Array.isArray( this.form.dedicate.types ) && this.form.dedicate.types.length
			? this.form.dedicate.types
			: [ 'in_honour_of', 'in_memory_of' ];
		const typeLabels = {
			in_honour_of: __( 'In honor of' ),
			in_memory_of: __( 'In memory of' ),
		};

		// "This gift is": two choice buttons, as How often draws its choices.
		const typeGroup = document.createElement( 'fieldset' );
		const typeLegend = document.createElement( 'legend' );
		const typeRow = document.createElement( 'div' );
		const typeRadios = [];

		typeGroup.className = 'anudan-checkout__field anudan-checkout__dedicate-type';
		typeLegend.className = 'anudan-checkout__field-label';
		typeLegend.textContent = __( 'This gift is' );
		typeRow.className = 'anudan-checkout__frequency';
		typeGroup.appendChild( typeLegend );

		types.forEach( ( type, index ) => {
			const radioLabel = document.createElement( 'label' );
			const radio = document.createElement( 'input' );
			const text = document.createElement( 'span' );

			radioLabel.className = 'anudan-checkout__frequency-option';
			radio.type = 'radio';
			radio.name = `${ this.idPrefix }-dedicate-type`;
			radio.value = type;
			radio.checked = 0 === index;
			text.textContent = typeLabels[ type ] || type;
			typeRadios.push( radio );

			radioLabel.appendChild( radio );
			radioLabel.appendChild( text );
			typeRow.appendChild( radioLabel );
		} );

		typeGroup.appendChild( typeRow );
		panel.appendChild( typeGroup );

		const name = this.buildTextField( 'dedicate_name', __( 'Their name' ), 'off', true );
		const nameInput = name.querySelector( 'input' );

		nameInput.classList.add( 'anudan-checkout__dedicate-name' );
		panel.appendChild( name );

		const messageField = document.createElement( 'div' );
		const messageLabel = document.createElement( 'label' );
		const messageHelp = document.createElement( 'p' );
		const messageInput = document.createElement( 'textarea' );

		messageField.className = 'anudan-checkout__field';
		messageLabel.className = 'anudan-checkout__field-label';
		messageLabel.htmlFor = `${ this.idPrefix }-dedicate-message`;
		messageLabel.textContent = __( 'Message' );
		messageLabel.setAttribute( 'data-anudan-need', __( '(optional)' ) );
		messageHelp.className = 'anudan-checkout__field-help';
		messageHelp.id = `${ this.idPrefix }-dedicate-message-help`;
		messageHelp.textContent = __( 'Up to 300 characters.' );
		messageInput.id = `${ this.idPrefix }-dedicate-message`;
		messageInput.className = 'anudan-checkout__field-input anudan-checkout__dedicate-message';
		messageInput.rows = 3;
		messageInput.maxLength = 300;
		messageInput.setAttribute( 'aria-describedby', messageHelp.id );
		messageInput.value = this.form.dedicate.default_message || '';
		messageField.appendChild( messageLabel );
		messageField.appendChild( messageHelp );
		messageField.appendChild( messageInput );
		panel.appendChild( messageField );

		this.dedicateFields = {
			type: () => {
				const checked = typeRadios.find( ( radio ) => radio.checked );

				return checked ? checked.value : types[ 0 ];
			},
			name: () => ( open.input.checked ? nameInput.value : '' ),
			message: () => ( open.input.checked ? messageInput.value : '' ),
			notifyName: () => '',
			notifyEmail: () => '',
			notifyIncludeAmount: () => false,
			isOpen: () => open.input.checked,
			isTelling: () => false,
		};

		if ( this.form.dedicate.allow_notification ) {
			// "Let someone know": a note to a person the donor names, by email, only when ticked.
			const tell = this.buildCheckRow( `${ this.idPrefix }-dedicate-notify`, __( 'Let someone know' ), __( 'We’ll email them a note about your gift.' ) );
			const notifyWrapper = document.createElement( 'div' );

			notifyWrapper.className = 'anudan-checkout__dedicate-notify';
			notifyWrapper.hidden = true;
			tell.input.addEventListener( 'change', () => {
				notifyWrapper.hidden = ! tell.input.checked;
			} );

			const notifyName = this.buildTextField( 'dedicate_notify_name', __( 'Their name' ), 'off', true );
			const notifyEmail = this.buildTextField( 'dedicate_notify_email', __( 'Their email' ), 'off', true, 'email' );
			const notifyNameInput = notifyName.querySelector( 'input' );
			const notifyEmailInput = notifyEmail.querySelector( 'input' );
			const amount = this.buildCheckRow( `${ this.idPrefix }-dedicate-amount`, __( 'Include the amount in the note' ), '' );

			notifyWrapper.appendChild( notifyName );
			notifyWrapper.appendChild( notifyEmail );
			notifyWrapper.appendChild( amount.row );
			panel.appendChild( tell.row );
			panel.appendChild( notifyWrapper );

			const telling = () => open.input.checked && tell.input.checked;

			this.dedicateFields.notifyName = () => ( telling() ? notifyNameInput.value : '' );
			this.dedicateFields.notifyEmail = () => ( telling() ? notifyEmailInput.value : '' );
			this.dedicateFields.notifyIncludeAmount = () => telling() && amount.input.checked;
			this.dedicateFields.isTelling = telling;
		}

		return wrapper;
	}

	/**
	 * Q1 (0.9.40): a tick box with its words beside it and, when given, a line under them, as "Give anonymously" is drawn.
	 *
	 * @param {string} id   The box's id.
	 * @param {string} text Its words.
	 * @param {string} help The line under them, or ''.
	 * @return {{row: HTMLElement, input: HTMLInputElement}}
	 */
	buildCheckRow( id, text, help ) {
		const row = document.createElement( 'div' );
		const label = document.createElement( 'label' );
		const input = document.createElement( 'input' );
		const words = document.createElement( 'span' );

		row.className = 'anudan-checkout__anon';
		label.className = 'anudan-checkout__anon-label';
		label.htmlFor = id;
		input.type = 'checkbox';
		input.id = id;
		input.className = 'anudan-checkout__anon-checkbox';
		words.textContent = text;
		label.appendChild( input );
		label.appendChild( words );
		row.appendChild( label );

		if ( help ) {
			const note = document.createElement( 'p' );

			note.className = 'anudan-checkout__field-help';
			note.id = `${ id }-help`;
			note.textContent = help;
			input.setAttribute( 'aria-describedby', note.id );
			row.appendChild( note );
		}

		return { row, input };
	}

	/**
	 * The donor's own M-DEDICATE input, as `POST /checkout/intent`'s `dedicate` object — or
	 * `undefined` when the fields were never rendered (module off for this fund), the same
	 * "absent, not an empty object" shape `registration_number` above already uses for an
	 * optional field. An empty name is still sent (never omitted): `Funds\Modules\
	 * DedicateModule::validate()` is what decides whether that counts as "dedicating" at all
	 * (T-02 — the client's own opinion about its state is never authoritative).
	 *
	 * @return {Object<string, string|boolean>|undefined}
	 */
	collectDedicateAnswer() {
		if ( ! this.dedicateFields.name ) {
			return undefined;
		}

		return {
			type: this.dedicateFields.type(),
			name: this.dedicateFields.name(),
			message: this.dedicateFields.message(),
			notify_name: this.dedicateFields.notifyName(),
			notify_email: this.dedicateFields.notifyEmail(),
			notify_include_amount: this.dedicateFields.notifyIncludeAmount(),
		};
	}

	/**
	 * One fund custom field, rendered as the control its type actually is.
	 *
	 * Before WP-38 every type rendered as a bare `<input>` whose label was not associated with it,
	 * and — the part that mattered — **no answer was ever sent**: `submit()` posted `fields: {}`
	 * unconditionally, so a fund with a required custom field could not complete a checkout at
	 * all (the same shape as issue 0015, one field type over). Each control now registers a
	 * getter in `this.customFields`, which `collectFieldAnswers()` reads at submit time.
	 *
	 * Kept separate from `this.fields` (the donor's own name/email/phone): a fund's custom field
	 * key must never overwrite, or be overwritten by, one of those.
	 *
	 * @param {object} field A definition from `GET /funds/{id}/form`'s `fields` array.
	 * @return {HTMLElement}
	 */
	buildField( field ) {
		const id = `${ this.idPrefix }-field-${ field.key }`;
		const options = Array.isArray( field.options ) ? field.options : [];
		const isGroup = 'radio' === field.type || 'checkbox_group' === field.type;
		const wrapper = document.createElement( isGroup ? 'fieldset' : 'div' );

		wrapper.className = 'anudan-checkout__field';
		wrapper.setAttribute( 'data-anudan-field', field.key );

		const label = document.createElement( isGroup ? 'legend' : 'label' );

		label.className = 'anudan-checkout__field-label';
		label.textContent = field.label;

		// Q1 (0.9.40): every question says whether it must be answered, as the donor's own fields do.
		if ( 'checkbox' !== field.type ) {
			label.setAttribute( 'data-anudan-need', field.required ? __( '(required)' ) : __( '(optional)' ) );
		}

		if ( ! isGroup ) {
			label.htmlFor = id;
		}

		wrapper.appendChild( label );

		const describedBy = [];

		if ( field.help_text ) {
			const help = document.createElement( 'p' );

			help.className = 'anudan-checkout__field-help';
			help.id = `${ id }-help`;
			help.textContent = field.help_text;
			wrapper.appendChild( help );
			describedBy.push( help.id );
		}

		if ( isGroup ) {
			const inputs = options.map( ( option, index ) => {
				const optionLabel = document.createElement( 'label' );
				const input = document.createElement( 'input' );

				optionLabel.className = 'anudan-checkout__field-option';
				input.type = 'radio' === field.type ? 'radio' : 'checkbox';
				input.name = id;
				input.value = option;
				input.id = `${ id }-${ index }`;

				if ( 'radio' === field.type && field.required ) {
					input.required = true;
				}

				optionLabel.appendChild( input );
				optionLabel.appendChild( document.createTextNode( option ) );
				wrapper.appendChild( optionLabel );

				return input;
			} );

			this.customFields[ field.key ] = 'radio' === field.type
				? () => {
					const checked = inputs.find( ( input ) => input.checked );

					return checked ? checked.value : '';
				}
				: () => inputs.filter( ( input ) => input.checked ).map( ( input ) => input.value );

			return wrapper;
		}

		let control;

		if ( 'textarea' === field.type ) {
			control = document.createElement( 'textarea' );
			control.rows = 4;
		} else if ( 'select' === field.type ) {
			control = document.createElement( 'select' );

			const placeholder = document.createElement( 'option' );

			placeholder.value = '';
			placeholder.textContent = field.placeholder || ( window.wp && window.wp.i18n ? window.wp.i18n.__( 'Choose…', 'anudan' ) : 'Choose…' );
			control.appendChild( placeholder );

			options.forEach( ( option ) => {
				const element = document.createElement( 'option' );

				element.value = option;
				element.textContent = option;
				control.appendChild( element );
			} );
		} else {
			control = document.createElement( 'input' );
			control.type = [ 'number', 'email', 'url', 'date', 'checkbox' ].includes( field.type ) ? field.type : 'text';

			if ( 'phone' === field.type ) {
				control.type = 'tel';
			}
		}

		control.id = id;
		control.className = 'anudan-checkout__field-input';
		control.required = !! field.required;

		// A6 (0.9.40), 0.9.41: a number question takes no negative answer, whatever its minimum; the server refuses it too.
		if ( 'number' === control.type ) {
			const minimum = field.validation && Number.isFinite( Number( field.validation.min ) ) ? Number( field.validation.min ) : 0;

			control.min = String( Math.max( 0, minimum ) );
			control.step = '1';
			// R3-3 (rc4): digits only as it is typed or pasted, the numeric keyboard on phones.
			digitsOnly( control );
		}

		if ( 'select' !== field.type && 'checkbox' !== field.type ) {
			control.placeholder = field.placeholder || '';
		}

		if ( describedBy.length ) {
			control.setAttribute( 'aria-describedby', describedBy.join( ' ' ) );
		}

		wrapper.appendChild( control );

		this.customFields[ field.key ] = 'checkbox' === field.type ? () => control.checked : () => control.value;

		return wrapper;
	}

	/**
	 * R2-12 (rc2): a field no person sees, reaches by Tab or hears read out, which a script filling every box fills.
	 *
	 * After rc7 (P0: every desktop Chrome payment refused on staging): Chrome's address autofill filled it with the donor's full
	 * name. Chrome skips only fields that cannot take focus (Chromium `FormFiller::GetFillingSkipReasonsForField`: "Don't fill
	 * unfocusable fields"), and ignores `autocomplete="off"` for its own address data; it was hidden by opacity and clipping, which
	 * leave it focusable, and sat between the name and the email, where Chrome guessed it was a name. So it is now `inert` and
	 * `visibility: hidden` (neither can take focus, and both leave the accessibility tree), it sits after every question rather
	 * than among them, and it carries the ignore attributes the common password managers document. Its name is unchanged, so a
	 * page open since before an update still sends it.
	 *
	 * @return {HTMLInputElement}
	 */
	buildTrapField() {
		const trap = document.createElement( 'input' );

		trap.type = 'text';
		trap.name = 'anudan_leave_blank';
		trap.className = 'anudan-checkout__trap';
		trap.tabIndex = -1;
		trap.autocomplete = 'off';
		trap.inert = true;
		trap.setAttribute( 'inert', '' );
		trap.setAttribute( 'aria-hidden', 'true' );
		trap.setAttribute( 'data-1p-ignore', '' );
		trap.setAttribute( 'data-lpignore', 'true' );
		trap.setAttribute( 'data-bwignore', 'true' );
		trap.setAttribute( 'data-form-type', 'other' );
		this.trapField = trap;

		return trap;
	}

	/**
	 * R2-12 (rc2): what the payment route reads to tell a person from a script: the page's signed timing token, and the
	 * hidden field, which a person leaves empty. The server decides; this only passes them on.
	 *
	 * @return {{form_token: string, anudan_leave_blank: string}}
	 */
	botSignals() {
		const config = window.AnudanEngine && window.AnudanEngine.config;

		return {
			form_token: ( config && config.formToken ) || '',
			anudan_leave_blank: this.trapField ? this.trapField.value : '',
		};
	}

	/**
	 * The fund custom-field answers, keyed by field key — what `POST /checkout/intent` receives as
	 * `fields`. An empty answer is omitted rather than sent as `''`, so an optional question the
	 * donor skipped is the same on the wire as one never asked. `Fields\FieldValidator` is the
	 * authority on whether any of it is acceptable (T-02); this only reads the controls.
	 *
	 * **Never written to the draft.** `saveDraftNow()` deliberately persists only the amount and
	 * the donor's own name and email (ADR 0023). On an M-SERVICE fund these answers describe a
	 * third party, and `localStorage` outlives the tab on whatever device — possibly shared — the
	 * donor used.
	 *
	 * @return {Object<string, string|boolean|string[]>}
	 */
	collectFieldAnswers() {
		const answers = {};

		Object.keys( this.customFields ).forEach( ( key ) => {
			// 0.9.14: an entry's answers travel as `entries`, never as the gift's own (EntriesUi.collect()).
			if ( key.startsWith( 'entries.' ) ) {
				return;
			}

			const value = this.customFields[ key ]();

			if ( '' === value || false === value || ( Array.isArray( value ) && ! value.length ) ) {
				return;
			}

			answers[ key ] = value;
		} );

		return answers;
	}

	/**
	 * @return {HTMLElement}
	 */
	buildSummaryBar() {
		const bar = document.createElement( 'div' );

		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		// Q1 (0.9.40): the "Your gift" card: a line per part of the gift, the fee as its own line (C7), then the total.
		bar.className = 'anudan-checkout__summary-bar';
		bar.setAttribute( 'aria-label', __( 'Your gift' ) );
		// Shown once there is a total (showTotal()); empty, it was a bare strip under the form.
		bar.hidden = true;
		this.summaryBar = bar;

		const title = document.createElement( 'h3' );

		title.className = 'anudan-checkout__section-title';
		title.textContent = __( 'Your gift' );
		bar.appendChild( title );

		this.summaryLines = document.createElement( 'div' );
		this.summaryLines.className = 'anudan-checkout__summary-lines';
		bar.appendChild( this.summaryLines );

		this.summaryBreakdown = document.createElement( 'div' );
		this.summaryBreakdown.className = 'anudan-checkout__summary-breakdown';
		this.summaryBreakdown.hidden = true;
		bar.appendChild( this.summaryBreakdown );

		const totalRow = document.createElement( 'div' );
		const totalWord = document.createElement( 'span' );
		const totalFigure = document.createElement( 'span' );

		totalRow.className = 'anudan-checkout__summary-row';
		totalWord.className = 'anudan-checkout__summary-word';
		totalWord.textContent = __( 'Total' );

		this.summaryTotal = document.createElement( 'span' );
		this.summaryTotal.className = 'anudan-checkout__summary-total';
		this.summaryPer = document.createElement( 'span' );
		this.summaryPer.className = 'anudan-checkout__summary-per';

		totalFigure.appendChild( this.summaryTotal );
		totalFigure.appendChild( this.summaryPer );
		totalRow.appendChild( totalWord );
		totalRow.appendChild( totalFigure );
		bar.appendChild( totalRow );

		return bar;
	}

	/**
	 * Shows one step, updates the stitch, moves focus to the step heading (WCAG: an
	 * announced, focus-managed transition rather than a silent DOM swap), and saves the draft.
	 *
	 * @param {number}  step
	 * @param {boolean} [moveFocus] False when the form is first drawn or a draft restored, on page
	 *                              load: focusing the heading then took a page-embedded form's
	 *                              visitor, and their screen reader, halfway down the page before
	 *                              they had done anything (release gate 0.5.3, stage 4). A modal's
	 *                              focus is placed by its focus trap.
	 * @return {void}
	 */
	showStep( step, moveFocus = true ) {
		// A refusal from the last attempt is not news on the next step. Standing notices (a
		// misconfigured gateway or security check) are put back by the checks further down.
		this.say( '', false );

		/*
		 * Back from the payment step abandons it. The mounted card form or PayPal buttons belong
		 * to the intent made from what was on screen when Give was pressed; left live, a donor who
		 * went back and changed the amount was charged the old one while the summary showed the
		 * new (adversarial review round 5, MEDIUM-1). Give is shown again and makes a new intent.
		 */
		if ( 1 === step && this.paymentStepActive ) {
			this.leavePaymentStep();
		}

		/*
		 * Back while Give is still waiting on the intent or the gateway's script abandons that
		 * Give too: without this, the donor changed the amount and the payment fields then mounted
		 * for the old intent (release gate 0.5.3, adversarial review round 6, MEDIUM-1).
		 * {@see submit} checks the generation after every wait and stops if it moved.
		 */
		if ( 1 === step && this.submitting ) {
			this.submitGeneration = ( this.submitGeneration || 0 ) + 1;
			this.submitting = false;
			this.lockGatewayChoice( false );
			this.paymentContainer.textContent = '';
			this.enableGive();
		}

		this.step = step;

		[ this.stepOneEl, this.stepTwoEl ].forEach( ( element, index ) => {
			element.classList.toggle( 'anudan-checkout__step--active', index === step - 1 );
		} );

		this.refreshProgress();

		// Q1 (0.9.40): the "Your gift" summary sits above the step's own button, on every step.
		if ( this.summaryBar && this.currentActions() ) {
			const anchor = this.anchorOf( this.currentActions() );

			anchor.parentNode.insertBefore( this.summaryBar, anchor );
		}

		this.syncDock();

		const heading = this.container.querySelector(
			`[data-anudan-step="${ step }"] .anudan-checkout__step-title`
		);

		if ( heading && moveFocus ) {
			// R2-19 (rc2): the new step from its top (the step bar and the step's heading), then focus on the heading for screen
			// readers, which must not scroll again (Safari's own scroll for focus is what left the page at the footer).
			this.scrollToStep( this.stepTop( step ) );
			heading.setAttribute( 'tabindex', '-1' );
			heading.focus( { preventScroll: true } );
		}

		// After `updateStitch()`'s own step announcement, which would otherwise overwrite it:
		// a misconfigured-Turnstile notice must survive every step change, not just the render
		// that first showed it. The no-gateway notice needs the identical survival for the
		// identical reason.
		this.applyTurnstileAvailability();
		this.applyGatewayAvailability();

		this.saveDraftNow();
	}

	/**
	 * Q1 (0.9.40): the step the donor sees: 1 the gift, 2 the details, 3 the payment, once the payment fields are shown.
	 *
	 * @return {number}
	 */
	displayStep() {
		if ( 1 === this.step ) {
			return 1;
		}

		return this.paymentStepActive ? 3 : 2;
	}

	/**
	 * Brings the step bar, the step's title and the step's own button up to date with the step the donor sees.
	 *
	 * @return {void}
	 */
	refreshProgress() {
		const shown = this.displayStep();
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		updateStitch( this.stitch, this.liveRegion, shown, SHOWN_STEPS, false );

		if ( this.stepTwoTitle ) {
			this.stepTwoTitle.textContent = 3 === shown ? __( 'Payment' ) : __( 'Your details' );
		}

		if ( this.stepTwoEl ) {
			this.stepTwoEl.classList.toggle( 'is-paying', 3 === shown );
		}
	}

	/**
	 * R2-19 (rc2): the top of a step as the donor reads it: the form's step bar ("Step 2 of 3" and the total), with the step's
	 * heading right under it; the step itself where the form shows no step bar.
	 *
	 * @param {number} step The step.
	 * @return {HTMLElement|null}
	 */
	stepTop( step ) {
		const header = this.container.querySelector( '.anudan-checkout__header' );

		if ( header && header.offsetParent ) {
			return header;
		}

		return this.container.querySelector( `[data-anudan-step="${ step }"]` );
	}

	/**
	 * R2-19 (rc2): how far down the screen the site's own fixed or sticky header (and WordPress's admin bar) reaches, so a step
	 * scrolled to is not hidden under it. Read from what is actually drawn across the top of the screen, whatever the theme.
	 *
	 * @return {number} Pixels from the top of the viewport.
	 */
	stickyTopOffset() {
		let bottom = 0;
		const width = window.innerWidth || document.documentElement.clientWidth;

		[ 0.1, 0.5, 0.9 ].forEach( ( share ) => {
			const found = 'function' === typeof document.elementsFromPoint ? document.elementsFromPoint( width * share, 1 ) : [];

			found.forEach( ( element ) => {
				for ( let node = element; node && node !== document.body && node !== document.documentElement; node = node.parentElement ) {
					if ( node === this.container || node === this.dock || this.container.contains( node ) ) {
						break;
					}

					const position = window.getComputedStyle( node ).position;

					if ( 'fixed' === position || 'sticky' === position ) {
						bottom = Math.max( bottom, node.getBoundingClientRect().bottom );
						break;
					}
				}
			} );
		} );

		return Math.min( bottom, ( window.innerHeight || 0 ) / 2 );
	}

	/**
	 * R2-19 (rc2): brings the top of a new step into view, its heading just below any sticky site header. Round 2's iPhone video:
	 * a donor who pressed "Continue to your details" with the footer on screen got the next, shorter step with the page still
	 * scrolled to the footer, and saw no form. Focus moving to the heading did not scroll in Safari.
	 *
	 * The scroll is instant, not smooth (a smooth scroll is cut short when Safari's address bar or the keyboard changes the
	 * viewport), and is made again if the visual viewport changes in the moment after (the keyboard closing as the button takes
	 * the tap), so it lands where it should once the screen settles.
	 *
	 * @param {HTMLElement} target The new step's first thing to show.
	 * @return {void}
	 */
	scrollToStep( target ) {
		if ( ! target || 'function' !== typeof target.getBoundingClientRect ) {
			return;
		}

		const gap = 12;
		const go = () => {
			if ( 'modal' === this.mode ) {
				const top = target.getBoundingClientRect().top - this.container.getBoundingClientRect().top + this.container.scrollTop - gap;

				this.container.scrollTop = Math.max( 0, top );

				return;
			}

			const visual = window.visualViewport;
			const top = target.getBoundingClientRect().top + window.scrollY - this.stickyTopOffset() - gap - ( visual ? visual.offsetTop : 0 );

			window.scrollTo( 0, Math.max( 0, top ) );
		};

		go();

		if ( window.visualViewport && 'function' === typeof window.visualViewport.addEventListener ) {
			const again = () => go();

			window.visualViewport.addEventListener( 'resize', again );
			window.setTimeout( () => window.visualViewport.removeEventListener( 'resize', again ), 800 );
		}

		window.requestAnimationFrame( go );
	}

	/**
	 * Where a step's button row stands in the form: the row itself, or, while it is docked at the foot of the screen
	 * (R2-20), the placeholder that keeps its place. What goes "above the button" goes before this.
	 *
	 * @param {HTMLElement} actions A step's button row.
	 * @return {HTMLElement}
	 */
	anchorOf( actions ) {
		return actions && actions.anudanSpot && actions.anudanSpot.isConnected && this.dock && actions.parentNode === this.dock
			? actions.anudanSpot
			: actions;
	}

	/**
	 * R2-20 (rc2): the phone's total bar. A step's button row (the total, the main button, Back) stays pinned to the foot of the
	 * screen whenever any part of the form is on screen, and goes completely once the form is out of view. It used to stick
	 * inside its step only, so past the form's end it travelled up the page with it, over the footer (round 2's iPhone video).
	 *
	 * The form's frame is a query container (its phone layout answers to its own width), and a container is the containing
	 * block of anything fixed inside it, so a row fixed inside the frame would be fixed to the frame, not the screen. The row is
	 * therefore moved into a dock beside the frame (still inside the form) while pinned, and a placeholder of its height keeps
	 * its place, so the last field is never under it. It is not pinned while the donor types in a field (a phone's keyboard is up
	 * and the bar would cover what they type), on a payment step (the pay button is in the payment box), in the modal form, or on
	 * a wider form.
	 *
	 * @return {void}
	 */
	initDock() {
		if ( 'page' !== this.mode || ! this.frame || 'function' !== typeof window.IntersectionObserver ) {
			return;
		}

		this.dock = document.createElement( 'div' );
		this.dock.className = 'anudan-checkout-dock';
		this.dock.hidden = true;
		this.container.appendChild( this.dock );
		this.formInView = false;
		this.typing = false;

		new window.IntersectionObserver( ( entries ) => {
			this.formInView = entries.some( ( entry ) => entry.isIntersecting );
			this.syncDock();
		} ).observe( this.frame );

		const textField = ( element ) => element instanceof window.HTMLElement && (
			'TEXTAREA' === element.tagName || 'SELECT' === element.tagName || 'IFRAME' === element.tagName
			|| ( 'INPUT' === element.tagName && ! [ 'radio', 'checkbox', 'button', 'submit' ].includes( element.type ) )
		);

		/*
		 * A press that ends the typing (the donor typed, then pressed Continue) must not move the row between the press and its
		 * click: the browser drops a click whose button moved under it. So while a pointer is down the row stays where it is,
		 * and is docked again once the pointer is up, after the click it made.
		 */
		this.pointerDown = false;
		window.addEventListener( 'pointerdown', () => {
			this.pointerDown = true;
		}, true );
		window.addEventListener( 'pointerup', () => {
			this.pointerDown = false;
			window.setTimeout( () => this.syncDock(), 0 );
		}, true );
		window.addEventListener( 'pointercancel', () => {
			this.pointerDown = false;
		}, true );

		this.container.addEventListener( 'focusin', ( event ) => {
			this.typing = textField( event.target );
			this.syncDock();
		} );
		this.container.addEventListener( 'focusout', () => {
			// The next field's focusin, if any, comes after this; a tap outside the form leaves typing.
			window.setTimeout( () => {
				this.typing = textField( document.activeElement ) && this.container.contains( document.activeElement );
				this.syncDock();
			}, 0 );
		} );

		window.addEventListener( 'resize', () => this.syncDock() );

		/*
		 * 0.9.41 (audit, real Chrome on Android): the form grows as it fills in (an amount chosen brings the total and the fee
		 * lines), which can push the row's place below the screen with no scroll and no resize; the bar then stayed off, the
		 * button out of sight, until the donor scrolled. A change in the form's size is answered too, once per frame.
		 */
		if ( 'function' === typeof window.ResizeObserver ) {
			let sizing = false;

			new window.ResizeObserver( () => {
				if ( sizing ) {
					return;
				}

				sizing = true;
				window.requestAnimationFrame( () => {
					sizing = false;
					this.syncDock();
				} );
			} ).observe( this.container );
		}

		// F3 (rc3): whether the row's own place is on screen changes as the page scrolls; once per frame is enough.
		let queued = false;

		window.addEventListener( 'scroll', () => {
			if ( queued ) {
				return;
			}

			queued = true;
			window.requestAnimationFrame( () => {
				queued = false;
				this.syncDock();
			} );
		}, { passive: true } );
	}

	/**
	 * Docks the active step's button row, or puts every row back in the form, by the rules in {@see initDock}.
	 *
	 * @return {void}
	 */
	syncDock() {
		if ( ! this.dock || this.pointerDown ) {
			return;
		}

		// A row moved while one of its buttons has the keyboard's focus loses it; it is given back after the move.
		const focused = document.activeElement;

		const rem = parseFloat( window.getComputedStyle( document.documentElement ).fontSize ) || 16;
		const phone = this.frame.getBoundingClientRect().width < 36 * rem;
		const active = 1 === this.step ? this.stepOneActions : ( this.paymentStepActive ? null : this.stepTwoActions );
		const pin = phone && this.formInView && ! this.typing && !! active && ! active.hidden && ! this.container.classList.contains( 'is-confirmed' )
			&& this.homeBelowScreen( active );

		[ this.stepOneActions, this.stepTwoActions ].forEach( ( actions ) => {
			if ( actions && actions.parentNode === this.dock && ( ! pin || actions !== active ) ) {
				actions.anudanSpot.replaceWith( actions );
			}
		} );

		if ( pin && active.parentNode !== this.dock ) {
			if ( ! active.anudanSpot ) {
				active.anudanSpot = document.createElement( 'div' );
				active.anudanSpot.className = 'anudan-checkout__actions-spot';
				active.anudanSpot.setAttribute( 'aria-hidden', 'true' );
			}

			active.anudanSpot.style.blockSize = `${ active.offsetHeight }px`;
			active.replaceWith( active.anudanSpot );
			this.dock.appendChild( active );
		}

		this.dock.hidden = ! pin;

		/*
		 * `.anudan-root` isolates its own stacking (reset.css), so the dock's z-index counts only inside it and a theme's footer,
		 * later on the page, was drawn over it (and took the taps). While the row is docked the form's root stands above the
		 * page's ordinary content.
		 */
		const root = this.container.closest( '.anudan-root' );

		if ( root ) {
			root.classList.toggle( 'anudan-root--docked', pin );
		}

		if ( focused && focused !== document.activeElement && focused.isConnected && 'function' === typeof focused.focus ) {
			focused.focus( { preventScroll: true } );
		}
	}

	/**
	 * F3 (rc3): whether a row's own place in the form is still below the foot of the screen, so pinning it there covers nothing
	 * but the form. Once its place is on screen the row goes back to it, as a sticky row ends at its own place. Round 3's real
	 * iPhone: only the empty end of the form was on screen (the placeholder holding the row's place), so the frame counted as in
	 * view and the row stayed pinned over the theme's footer.
	 *
	 * @param {HTMLElement} actions A step's button row.
	 * @return {boolean}
	 */
	homeBelowScreen( actions ) {
		const home = actions.parentNode === this.dock && actions.anudanSpot && actions.anudanSpot.isConnected ? actions.anudanSpot : actions;
		const visual = window.visualViewport;
		const foot = visual ? Math.min( window.innerHeight || Infinity, visual.offsetTop + visual.height ) : window.innerHeight;

		return home.getBoundingClientRect().bottom > foot + 1;
	}

	/**
	 * The active step's button row: where its message and its "Your gift" summary go.
	 *
	 * @return {HTMLElement|null}
	 */
	currentActions() {
		if ( 1 === this.step ) {
			return this.stepOneActions || null;
		}

		return this.paymentStepActive && this.paymentArea ? this.paymentArea : ( this.stepTwoActions || null );
	}

	/**
	 * Debounced call to `POST /checkout/quote` — the summary bar's own data source, per A-1
	 * ("the client displays; it never calculates").
	 *
	 * @return {void}
	 */
	/**
	 * Converts a typed amount to integer minor units, or null when it is not a plain amount.
	 *
	 * Digits, then optionally a point and one or two more digits; nothing else. Integer
	 * arithmetic on the two parts, never a float. This was `Math.round( Number( value ) * 100 )`,
	 * which accepted "1e3" as 1,000 and "0x10" as 16 and rounded "25.555" without saying so
	 * (release gate 0.5.3, adversarial review LOW-1). The server validates the integer it
	 * receives either way; this keeps that integer equal to what the donor typed.
	 *
	 * @param {string} text What the donor typed.
	 * @return {number|null} Minor units, or null.
	 */
	parseMinorUnits( text ) {
		const match = /^\s*(\d{1,10})(?:\.(\d{1,2}))?\s*$/.exec( String( text ) );

		if ( ! match ) {
			return null;
		}

		const cents = undefined === match[ 2 ] ? 0 : parseInt( ( match[ 2 ] + '0' ).slice( 0, 2 ), 10 );
		const minorUnits = ( parseInt( match[ 1 ], 10 ) * 100 ) + cents;

		return minorUnits > 0 ? minorUnits : null;
	}

	/**
	 * C7 (0.9.40): the covered fee is a line of its own, "Covering the fees" and the server's figure; none when nothing is covered.
	 *
	 * @param {number} feeMinorUnits The server's covered fee.
	 * @return {void}
	 */
	showFeeLine( feeMinorUnits ) {
		if ( ! this.summaryBreakdown ) {
			return;
		}

		if ( ! ( feeMinorUnits > 0 ) ) {
			this.summaryBreakdown.hidden = true;

			return;
		}

		const label = document.createElement( 'span' );
		const colon = document.createElement( 'span' );
		const figure = document.createElement( 'span' );

		label.textContent = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Covering the fees', 'anudan' ) : 'Covering the fees';
		colon.className = 'anudan-checkout__visually-hidden';
		colon.textContent = ': ';
		label.appendChild( colon );
		figure.textContent = this.formatMinorUnits( feeMinorUnits );
		this.summaryBreakdown.textContent = '';
		this.summaryBreakdown.appendChild( label );
		this.summaryBreakdown.appendChild( figure );
		this.summaryBreakdown.hidden = false;
	}

	/**
	 * Item 3 after rc5: the fee box ticked or unticked shows its total at once, from the server's own figures in the last answer
	 * (`totals_by_box`), when that answer is for the amount still chosen. The form asks again straight after and shows that answer;
	 * nothing here is added up, and Give charges what the intent works out.
	 *
	 * @return {void}
	 */
	showTotalForFeeBox() {
		const last = this.lastQuote;

		if ( ! last || ! last.totals_by_box || this.lastQuoteAmountToken !== this.amountToken ) {
			return;
		}

		const total = this.coverFees ? last.totals_by_box.covered : last.totals_by_box.not_covered;

		if ( 'number' !== typeof total ) {
			return;
		}

		this.showTotal( total );
		this.showFeeLine( this.coverFees ? last.fee_if_covered : 0 );
	}

	updateSummary( feeBoxOnly = false ) {
		if ( ! feeBoxOnly ) {
			this.amountToken += 1;
		}

		const amountToken = this.amountToken;

		this.syncOwnAmount();

		// The "choose an amount" prompt is only for as long as nothing is chosen.
		if ( this.amountPrompted && ( this.dated ? ! this.dated.nothingChosen() : ( null !== this.selectedTierId || null !== this.customAmount ) ) ) {
			this.amountPrompted = false;
			this.say( '', false );
			this.container.querySelectorAll( '.anudan-checkout__section--invalid' ).forEach( ( node ) => node.classList.remove( 'anudan-checkout__section--invalid' ) );
		}

		const token = ++this.quoteToken;

		// A total still to come for the amount as it was is no longer waited for ({@see whenQuoteSettled}).
		this.settleQuote();

		if ( this.cart ? ! this.cartComplete() : this.nothingChosen() ) {
			window.clearTimeout( this._quoteTimer );
			this.showTotal( null );

			if ( this.summaryBreakdown ) {
				this.summaryBreakdown.hidden = true;
			}

			return;
		}

		window.clearTimeout( this._quoteTimer );

		// True once the total and the "Your gift" card show the server's answer; false when it ended without one (refused, failed,
		// no answer in time, or overtaken). The first word counts.
		let resolveSettled;
		let done = false;
		const pending = new Promise( ( resolve ) => {
			resolveSettled = resolve;
		} );
		const settled = ( ok = false ) => {
			if ( done ) {
				return;
			}

			done = true;

			if ( pending === this.quoteSettled ) {
				this.quoteResult = ok;
			}

			resolveSettled( ok );
		};

		this.quoteSettled = pending;
		this.quoteResult = null;
		this._settleQuote = settled;

		const quote = async () => {
			const request = this.cart
				? {
					lines: this.cartPayloadLines(),
					cover_fees: this.coverFees,
					gateway: this.currentGateway(),
				}
				: {
					fund_id: this.fundId,
					amount_tier_id: this.selectedTierId,
					custom_amount: this.customAmount,
					frequency: this.currentFrequency(),
					cover_fees: this.coverFees,
					gateway: this.currentGateway(),
					selected_periods: this.selectedPeriods,
				};

			// 0.9.14: what the server prices the entries from — their number, and each unit count.
			if ( this.entries ) {
				request.entries = this.entries.quotePayload();
				request.amount_tier_id = null;
			}

			// D2 (0.9.40): each item's key and quantity, and the extra as the custom amount; the server prices them.
			if ( this.items ) {
				request.items = this.items.payload();
				request.amount_tier_id = null;
			}

			// Per-month giving: the months chosen and how the donor gives by month; the server adds them up.
			if ( this.dated ) {
				request.months = this.dated.payload();
				request.month_mode = this.dated.monthMode();
				Object.assign( request, this.dated.extras() );
			}

			const response = this.cart ? await api.cartQuote( request ) : await api.quote( request );

			if ( token !== this.quoteToken ) {
				return;
			}

			if ( ! response.ok || ! response.body ) {
				/*
				 * The total on screen was for the previous amount. Left there, a donor could be
				 * charged a total the page never showed (release gate 0.5.3, adversarial review
				 * round 11, MEDIUM-2). It is cleared; the intent's own total replaces it at Give.
				 */
				this.showTotal( null );

				// A month or an amount the fund does not take is said beside the months, in the server's own words.
				if ( this.dated && response.body && response.body.data && response.body.data.fields ) {
					this.dated.showErrors( response.body.data.fields );
				}

				// Q1, C8 (0.9.40): an amount the fund does not take ("At least $1.00") is said under the amount, in the server's own
				// words, and Continue stays on this step; it is not "the total could not be updated".
				const refused = 422 === response.status && response.body && response.body.data && response.body.data.fields;

				// D2 (0.9.40): an item the fund no longer offers, or a quantity it does not take, is said under the items.
				if ( refused && this.items ) {
					const itemMessage = Object.keys( refused ).filter( ( key ) => 'items' === key || key.startsWith( 'items.' ) || 'amount' === key || 'custom_amount' === key ).map( ( key ) => refused[ key ] )[ 0 ];

					if ( 'string' === typeof itemMessage ) {
						this.items.showError( itemMessage );

						return;
					}
				}

				const amountMessage = refused && ! this.dated && ! this.cart && ! this.entries ? ( refused.custom_amount || refused.amount ) : null;

				if ( 'string' === typeof amountMessage && this.customAmountWrapper ) {
					this.showAmountError( amountMessage );

					if ( this.quoteFailureShown ) {
						this.quoteFailureShown = false;
						this.say( '', false );
					}

					return;
				}

				// Said, not silent: a total that vanishes with no word looks like a broken page. No button is named: the quote runs
				// on the gift step and the details step alike, whose buttons differ (0.9.40 Phase 4). 0.9.41-rc1: Continue on the
				// gift step no longer goes on without the server's total (decisions log, 2026-10-29), so the donor tries again.
				this.say(
					window.wp && window.wp.i18n
						? window.wp.i18n.__( 'We could not work out the total just now. Nothing has been charged. Please try again in a moment.', 'anudan' )
						: 'We could not work out the total just now. Nothing has been charged. Please try again in a moment.',
					false
				);
				this.quoteFailureShown = true;

				return;
			}

			if ( this.quoteFailureShown ) {
				this.quoteFailureShown = false;
				this.say( '', false );
			}

			const body = response.body;

			this.clearAmountError();
			this.lastQuote = body;
			this.lastQuoteToken = token;
			this.lastQuoteAmountToken = amountToken;
			this.lastQuoteRequest = request;

			// Continue was pressed before this quote came back: the funnel event waited for it.
			if ( this.beginCheckoutPending && 2 === this.step ) {
				this.trackBeginCheckoutIfKnown();
			}

			this.showTotal( body.total_charged );
			this.showGiftLines( body );

			if ( this.entries ) {
				this.entries.showMinimum( body.minimum_amount );
			}

			this.showRaised( body );

			if ( this.dated ) {
				this.dated.showResolved( body );
			}

			this.showFeeLine( body.fee_covered_amount );
			this.saveDraftNow();

			// The total and the "Your gift" card show this answer: a Continue waiting for them goes on ({@see whenQuoteSettled}).
			settled( true );

			// Last: it may wait on a second quote, and must not hold up the summary.
			await this.quoteWallets( request, body, token );
		};

		const run = async () => {
			this._quoteFlush = null;

			try {
				await quote();
			} finally {
				settled( false );
			}
		};

		this._quoteFlush = run;
		// The pause lets typing settle into one quote. A single action (`feeBoxOnly`: the fee box ticked) asks at once; it waited the
		// same quarter second for nothing (item 3 after rc5).
		this._quoteTimer = window.setTimeout( run, feeBoxOnly ? 0 : 250 );
	}

	/**
	 * Ends the wait for the total last asked for, without its answer: the amount changed, or there is nothing to price.
	 *
	 * @return {void}
	 */
	settleQuote() {
		if ( this._settleQuote ) {
			this._settleQuote( false );
		}

		this._settleQuote = null;
		this._quoteFlush = null;
		this.quoteSettled = null;
		this.quoteResult = null;
	}

	/**
	 * 0.9.41-rc1: waits for the server's total for the amount as it stands, and says whether it came. Continue on the gift step goes
	 * on only once it has, so the details step opens with its "Your gift" card and total in place: opened before them, they appeared
	 * above "Continue to payment" when the total came, moving the button under a tap (the WebKit-Mobile fund-type run).
	 *
	 * A total still in its pause after the last change is asked for at once; one that failed before is asked for again. There is no
	 * time limit here: the request has its own (QUOTE_REQUEST_LIMIT_MS, api.js), so the wait always ends in a total or a message.
	 * After QUOTE_WAIT_MS `onSlow` is called, for "Still working…" (decisions log, 2026-10-29).
	 *
	 * @param {function(): void} onSlow
	 * @return {Promise<boolean>} True once the server's total for this amount is shown.
	 */
	async whenQuoteSettled( onSlow ) {
		if ( ! this.quoteSettled || false === this.quoteResult ) {
			this.updateSummary();
		}

		if ( this._quoteFlush ) {
			window.clearTimeout( this._quoteTimer );
			this._quoteFlush();
		}

		const slow = window.setTimeout( onSlow, QUOTE_WAIT_MS );
		let result = false;

		try {
			// A change of amount while waiting replaces the total asked for; the wait follows it to the newest.
			while ( this.quoteSettled ) {
				const pending = this.quoteSettled;

				result = await pending;

				if ( pending === this.quoteSettled ) {
					break;
				}
			}
		} finally {
			window.clearTimeout( slow );
		}

		return true === result;
	}

	/**
	 * Prices the wallet sheets for the wallet's own gateway and shows that total on them.
	 *
	 * A wallet pays through its own gateway (Stripe), and with fees covered the total depends on
	 * the gateway. The sheet showed the donor's selected gateway's total, so with PayPal selected
	 * every wallet payment was refused as "the amount changed" (release gate 0.5.3, adversarial
	 * review round 10, MEDIUM-1). Each sheet update is guarded: a throw while a sheet is open left
	 * the summary unupdated when this ran first (round 10, LOW-3).
	 *
	 * @param {Object} request The quote just made.
	 * @param {Object} body    Its answer.
	 * @param {number} token   Its quote token.
	 * @return {Promise<void>}
	 */
	async quoteWallets( request, body, token ) {
		const walletGateway = this.form.wallet && this.form.wallet.gateway;

		if ( ! walletGateway || ( ! this.expressWallet && ! this.paymentWallet ) ) {
			return;
		}

		let total = body.total_charged;

		if ( walletGateway !== request.gateway ) {
			const priced = await api.quote( { ...request, gateway: walletGateway } );

			if ( token !== this.quoteToken ) {
				return;
			}

			// Not priced: the sheet keeps its old total, and a payment against it is refused.
			total = priced.ok && priced.body ? priced.body.total_charged : null;
		}

		this.walletTotal = 'number' === typeof total ? total : null;

		[ this.expressWallet, this.paymentWallet ].forEach( ( controller ) => {
			if ( controller ) {
				this.syncWalletTotal( controller );
			}
		} );
	}

	/**
	 * @return {string}
	 */
	currentFrequency() {
		// 0.9.14: gifts with entries are one-time gifts; D2 (0.9.40): so are gifts of items.
		if ( this.entries || this.items ) {
			return 'one_time';
		}

		// Per-month giving: specific months are paid once; every month (and both) is a monthly gift.
		if ( this.dated ) {
			return this.dated.frequency();
		}

		if ( ! this.frequencyGroup ) {
			return this.form.default_frequency;
		}

		const checked = this.frequencyGroup.querySelector( 'input:checked' );

		return checked ? checked.value : this.form.default_frequency;
	}

	/**
	 * The gateway ids this fund can currently use — already filtered server-side by
	 * `Gateways\FundGatewayResolver` (configured, and honouring the fund's own restriction), so
	 * this never re-derives eligibility from anything client-side (T-02).
	 *
	 * @return {string[]}
	 */
	availableGateways() {
		return Array.isArray( this.form.gateways ) ? this.form.gateways : [];
	}

	/**
	 * The gateway every gateway-bearing request actually sends: the donor's own choice when one
	 * was made and is still available, falling back to the first (and, in the common case, only)
	 * gateway this fund offers. Empty when none is available at all — {@see this.submit} and
	 * {@see this.updateSummary} both already handle an empty gateway string the same way a quote
	 * or an intent for a fund with no usable gateway always has (`ErrorEnvelope::GATEWAY_ERROR`).
	 *
	 * @return {string}
	 */
	currentGateway() {
		const gateways = this.availableGateways();

		if ( this.selectedGateway && gateways.includes( this.selectedGateway ) ) {
			return this.selectedGateway;
		}

		return gateways[ 0 ] || '';
	}

	/**
	 * @param {number} totalMinorUnits
	 * @return {string}
	 */
	giveButtonLabel( totalMinorUnits ) {
		const amount = this.formatMinorUnits( totalMinorUnits );
		const template = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Give %s', 'anudan' ) : 'Give %s';

		return template.replace( '%s', amount );
	}

	/**
	 * C5 (0.9.40): the server raises an amount below what the donor's choices cost (a price per name, for example) to that
	 * minimum, and the total changed with no word said, so a donor who typed $100 paid $125 without being told. This says it,
	 * under the amount box, whenever the quote's gift is more than what was typed; and hides it otherwise.
	 *
	 * @param {{gift_amount: number}} quoteBody
	 * @return {void}
	 */
	showRaised( quoteBody ) {
		if ( ! this.raisedNote ) {
			return;
		}

		const typed = null === this.selectedTierId && Number.isInteger( this.customAmount ) ? this.customAmount : null;

		if ( null === typed || ! Number.isInteger( quoteBody.gift_amount ) || quoteBody.gift_amount <= typed ) {
			this.raisedNote.hidden = true;
			this.raisedNote.textContent = '';

			return;
		}

		// "You can give more" only where the fund lets a donor give more than the price.
		const more = ! this.entries || ! this.entries.schema || false !== this.entries.schema.allow_more;
		const i18n = window.wp && window.wp.i18n;
		let template;

		if ( more ) {
			template = i18n
				? i18n.__( 'Your gift has been raised from %1$s to %2$s, the least for what you chose. You can give more.', 'anudan' )
				: 'Your gift has been raised from %1$s to %2$s, the least for what you chose. You can give more.';
		} else {
			template = i18n
				? i18n.__( 'Your gift has been raised from %1$s to %2$s, the price of what you chose.', 'anudan' )
				: 'Your gift has been raised from %1$s to %2$s, the price of what you chose.';
		}

		this.raisedNote.textContent = template
			.replace( '%1$s', this.formatMinorUnits( typed ) )
			.replace( '%2$s', this.formatMinorUnits( quoteBody.gift_amount ) );
		this.raisedNote.hidden = false;
	}

	/**
	 * C7 (0.9.40, replacing C4's wording): the offer names no amount. The fee is shown only once, as its own line in the total
	 * ("Covering the fees", {@see self::formatBreakdown()}), from the server's figure for the fee settings in force.
	 *
	 * @return {string}
	 */
	feeCoverageMessage() {
		return window.wp && window.wp.i18n
			? window.wp.i18n.__( 'Make every dollar count: cover the card fee so your whole gift reaches those in need.', 'anudan' )
			: 'Make every dollar count: cover the card fee so your whole gift reaches those in need.';
	}

	/**
	 * @param {{gift_amount: number, fee_covered_amount: number}} quoteBody
	 * @return {string}
	 */
	formatBreakdown( quoteBody ) {
		// C7 (0.9.40): the covered fee as its own line in the total, the server's figure as charged.
		const fee = this.formatMinorUnits( quoteBody.fee_covered_amount );
		const label = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Covering the fees', 'anudan' ) : 'Covering the fees';

		return `${ label }: ${ fee }`;
	}

	/**
	 * Minor units to a display string. `Intl.NumberFormat` only, never arithmetic on the
	 * value — every figure it renders already came from the server (A-1).
	 *
	 * @param {number} minorUnits
	 * @param {string} [currency] ISO 4217 code. Defaults to USD for call sites that predate
	 *                            WP-22, none of which yet have a currency to pass.
	 * @return {string}
	 */
	formatMinorUnits( minorUnits, currency = 'USD' ) {
		return new Intl.NumberFormat( undefined, { style: 'currency', currency } ).format( minorUnits / 100 );
	}

	/**
	 * Q1, C8 (0.9.40): the server refused the amount typed. Its message goes under the amount box, which glows red, and is read
	 * by screen readers; Continue stays on the gift step until a quote for an amount the fund takes comes back.
	 *
	 * @param {string} message The server's words.
	 * @return {void}
	 */
	showAmountError( message ) {
		const wrapper = this.customAmountWrapper;
		let error = wrapper.querySelector( '.anudan-checkout__field-error[data-anudan-amount-error]' );

		if ( ! error ) {
			error = document.createElement( 'p' );
			error.className = 'anudan-checkout__field-error';
			error.id = `${ this.idPrefix }-error-amount`;
			error.setAttribute( 'role', 'alert' );
			error.setAttribute( 'data-anudan-amount-error', '' );
			wrapper.appendChild( error );
		}

		error.textContent = message;
		this.amountError = message;
		wrapper.classList.remove( 'anudan-checkout__field--fixed' );
		wrapper.classList.add( 'anudan-checkout__field--invalid' );

		if ( this.customAmountInput ) {
			this.customAmountInput.setAttribute( 'aria-invalid', 'true' );
			this.customAmountInput.setAttribute( 'aria-errormessage', error.id );
		}
	}

	/**
	 * A typed amount below the fund's least or above its most, in words; '' when it is within them or none is typed. Only what
	 * the form already knows (`min_amount`, `max_amount`); the server checks the amount again whatever this says.
	 *
	 * @return {string}
	 */
	amountOutOfRange() {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		// D2 (0.9.40): with an item chosen the typed amount is an extra on top of the items, not the gift, so no least applies; with
		// none it is the gift itself, checked like any typed amount.
		if ( this.cart || this.dated || this.entries || ( this.items && this.items.count() > 0 ) || null !== this.selectedTierId || ! Number.isInteger( this.customAmount ) ) {
			return '';
		}

		const least = Number.isInteger( this.form.min_amount ) ? this.form.min_amount : 0;
		const most = Number.isInteger( this.form.max_amount ) && this.form.max_amount > 0 ? this.form.max_amount : null;

		if ( least > 0 && this.customAmount < least ) {
			/* translators: %s: the least a gift can be, e.g. "$1.00". */
			return __( 'Enter at least %s.' ).replace( '%s', this.formatMinorUnits( least ) );
		}

		if ( null !== most && this.customAmount > most ) {
			/* translators: %s: the most a gift can be. */
			return __( 'Enter no more than %s.' ).replace( '%s', this.formatMinorUnits( most ) );
		}

		return '';
	}

	/**
	 * Undoes {@see showAmountError} once a quote for the amount comes back: the box turns green if it had been red.
	 *
	 * @return {void}
	 */
	clearAmountError() {
		if ( ! this.amountError || ! this.customAmountWrapper ) {
			return;
		}

		const error = this.customAmountWrapper.querySelector( '[data-anudan-amount-error]' );

		if ( error ) {
			error.remove();
		}

		this.amountError = null;
		this.customAmountWrapper.classList.remove( 'anudan-checkout__field--invalid' );
		this.customAmountWrapper.classList.add( 'anudan-checkout__field--fixed' );

		if ( this.customAmountInput ) {
			this.customAmountInput.removeAttribute( 'aria-invalid' );
			this.customAmountInput.removeAttribute( 'aria-errormessage' );
		}
	}

	/**
	 * An amount for an amount tile: "$25" for a whole amount, "$25.50" otherwise.
	 *
	 * @param {number} minorUnits
	 * @param {string} currency ISO 4217 code.
	 * @return {string}
	 */
	formatTileAmount( minorUnits, currency = 'USD' ) {
		const whole = 0 === minorUnits % 100;

		return new Intl.NumberFormat( undefined, { style: 'currency', currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 } ).format( minorUnits / 100 );
	}

	/**
	 * A currency's symbol as this browser writes it ("$", "£"), or its code when there is none.
	 *
	 * @param {string} currency ISO 4217 code.
	 * @return {string}
	 */
	currencySymbol( currency ) {
		try {
			const part = new Intl.NumberFormat( undefined, { style: 'currency', currency } )
				.formatToParts( 0 )
				.find( ( piece ) => 'currency' === piece.type );

			return part ? part.value : currency;
		} catch ( error ) {
			return currency;
		}
	}

	/**
	 * The server-resolved total and fund identity, shared by every GA4 event this wizard can
	 * send (WP-69) — never a figure this file computes, per A-1.
	 *
	 * @return {{totalMinorUnits: number, currency: string, fundId: number, fundName: string}|null}
	 *         Null when no quote has come back yet, which a GA4 event without a value would be
	 *         worse than simply not sending.
	 */
	analyticsDetail() {
		// The latest quote only: one for an amount the donor has since changed would send GA4 a
		// stale value, once (release gate 0.5.3, adversarial review round 8, LOW-3).
		if ( ! this.lastQuote || 'number' !== typeof this.lastQuote.total_charged || this.lastQuoteToken !== this.quoteToken ) {
			return null;
		}

		return {
			totalMinorUnits: this.lastQuote.total_charged,
			currency: this.form ? this.form.currency : 'USD',
			fundId: this.fundId,
			fundName: ( this.form && this.form.fund && this.form.fund.receipt_label ) || 'Donation',
		};
	}

	/**
	 * Locks or unlocks the gateway choice. Changed while Give was working, it re-quoted for the
	 * other gateway and the page showed that total while the intent already made charged its own
	 * (release gate 0.5.3, adversarial review round 12, MEDIUM-3).
	 *
	 * @param {boolean} locked
	 * @return {void}
	 */
	lockGatewayChoice( locked ) {
		if ( ! this.gatewaySelector ) {
			return;
		}

		this.gatewaySelector.querySelectorAll( 'input' ).forEach( ( input ) => {
			input.disabled = locked;
		} );
	}

	/**
	 * Shows a total in the summary and on the Give button, or clears both when none is known.
	 *
	 * @param {number|null} totalMinorUnits
	 * @return {void}
	 */
	showTotal( totalMinorUnits ) {
		const known = 'number' === typeof totalMinorUnits;
		const per = this.perWords( this.currentFrequency() );
		const amount = known ? this.formatMinorUnits( totalMinorUnits ) : '';

		this.summaryTotal.textContent = amount;

		if ( this.summaryPer ) {
			// D1 (0.9.41-rc3): "Monthly" as a badge beside the amount, never "a month".
			this.summaryPer.textContent = '';

			if ( known && '' !== per ) {
				this.summaryPer.appendChild( document.createTextNode( ' ' ) );
				this.summaryPer.appendChild( frequencyBadge( per ) );
			}
		}

		if ( this.summaryBar ) {
			this.summaryBar.hidden = ! known;
		}

		if ( ! known && this.summaryBreakdown ) {
			this.summaryBreakdown.hidden = true;
		}

		// Q1 (0.9.40): the total beside "Step 1 of 3", and in a sentence under a recurring choice.
		setStepTotal( this.stitch, amount, known ? per : '' );
		[ this.stepOneActions, this.stepTwoActions ].forEach( ( actions ) => setStepTotal( actions, amount, known ? per : '' ) );

		if ( this.frequencyNote ) {
			const every = this.everyWords( this.currentFrequency() );
			const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

			this.frequencyNote.hidden = ! known || '' === every;
			this.frequencyNote.textContent = '';

			if ( known && '' !== every ) {
				const lead = document.createElement( 'strong' );
				const more = document.createElement( 'span' );

				lead.textContent = __( 'You’ll give %1$s %2$s.' ).replace( '%1$s', amount ).replace( '%2$s', every );
				more.textContent = __( 'Change or cancel anytime from your account.' );
				this.frequencyNote.appendChild( lead );
				this.frequencyNote.appendChild( more );
			}
		}

		if ( this.giveButton ) {
			// Q1 (0.9.40): the details step's button goes on to the payment, where the button that pays names the amount.
			const label = window.wp && window.wp.i18n ? window.wp.i18n.__( 'Continue to payment', 'anudan' ) : 'Continue to payment';

			// Only when it differs: WebKit drops a tap whose button had its words rewritten between press and release, as a total
			// arriving then did.
			if ( this.giveButton.textContent !== label && ! this.preparing ) {
				this.giveButton.textContent = label;
			}
		}
	}

	/**
	 * Q1 (0.9.40): the lines of the "Your gift" card, from the server's quote: what the gift is ("Monthly gift to the General
	 * Fund", "3 names × $25.00", a line per fund of a cart), and its amount. The covered fee is the breakdown line under them.
	 *
	 * @param {Object} body A quote.
	 * @return {void}
	 */
	showGiftLines( body ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const lines = [];

		if ( this.cart && Array.isArray( body.lines ) ) {
			body.lines.forEach( ( line ) => {
				lines.push( [ String( line.fund_title || line.fund_name || '' ), line.gift_amount ] );
			} );
		} else {
			const decoder = document.createElement( 'textarea' );

			decoder.innerHTML = ( this.form.fund && this.form.fund.title ) || '';

			const frequency = this.currentFrequency();
			const kind = 'one_time' === frequency
				? __( 'One-time gift' )
				: __( '%s gift' ).replace( '%s', ( this.form.frequency_labels || {} )[ frequency ] || frequency );
			let what = '' !== decoder.value ? __( '%1$s to %2$s' ).replace( '%1$s', kind ).replace( '%2$s', decoder.value ) : kind;

			if ( this.entries && this.entries.summaryLine ) {
				what = this.entries.summaryLine() || what;
			}

			// D2 (0.9.40): a gift of items lists each item chosen ("2 × Food pack"), and the extra as "Extra".
			const perEntry = this.items ? this.items.summaryLines() : ( this.entries && this.entries.summaryLines ? this.entries.summaryLines() : null );

			if ( perEntry && perEntry.length && Number.isInteger( body.gift_amount ) ) {
				// Q1 (0.9.40): one line per entry; what the donor added on top is its own line, so the lines add up to the server's figure.
				const priced = perEntry.reduce( ( sum, [ , minor ] ) => sum + minor, 0 );

				perEntry.forEach( ( line ) => lines.push( line ) );

				if ( body.gift_amount > priced ) {
					lines.push( [ this.items ? __( 'Extra' ) : __( 'Added to your gift' ), body.gift_amount - priced ] );
				}
			} else {
				lines.push( [ what, body.gift_amount ] );
			}

			this.showGoods( body, lines );
		}

		this.summaryLines.textContent = '';

		lines.forEach( ( [ what, minor ] ) => {
			const row = document.createElement( 'div' );
			const name = document.createElement( 'span' );
			const figure = document.createElement( 'span' );

			row.className = 'anudan-checkout__summary-line';
			name.textContent = what;
			figure.textContent = Number.isInteger( minor ) ? this.formatMinorUnits( minor ) : '';
			row.appendChild( name );
			row.appendChild( figure );
			this.summaryLines.appendChild( row );
		} );
	}

	/**
	 * Q1 (0.9.40): what a gift includes in return, from the quote's `goods_value` (the figure the payment will freeze): a note
	 * under the amount, and two lines in the "Your gift" card. The deductible part is named only when the form schema says the
	 * fund's gifts are tax-deductible; the receipt states it the same way.
	 *
	 * @param {Object}        body  A quote.
	 * @param {Array<Array>}  lines The card's lines so far, added to.
	 * @return {void}
	 */
	showGoods( body, lines ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const goods = this.form.goods;
		const value = Number.isInteger( body.goods_value ) ? body.goods_value : 0;

		if ( ! goods || ! this.goodsNote ) {
			return;
		}

		if ( value <= 0 || ! Number.isInteger( body.gift_amount ) || body.gift_amount <= 0 ) {
			this.goodsNote.hidden = true;
			this.goodsNote.textContent = '';

			return;
		}

		// Inside a sentence "A dinner ticket" reads "a dinner ticket"; a first word in capitals ("VIP seat") keeps them.
		const raw = String( goods.description || '' );
		const described = /^[A-Z](?![A-Z])/.test( raw ) ? raw.charAt( 0 ).toLowerCase() + raw.slice( 1 ) : raw;
		const deductible = body.gift_amount - value;
		const title = document.createElement( 'p' );

		title.className = 'anudan-checkout__goods-title';
		/* translators: 1: the gift, 2: what it includes, 3: what that is worth. */
		title.textContent = __( 'Your %1$s gift includes %2$s worth %3$s.' )
			.replace( '%1$s', this.formatMinorUnits( body.gift_amount ) )
			.replace( '%2$s', described )
			.replace( '%3$s', this.formatMinorUnits( value ) );
		this.goodsNote.textContent = '';
		this.goodsNote.appendChild( title );

		if ( goods.deductible ) {
			const part = document.createElement( 'p' );

			part.className = 'anudan-checkout__goods-text';
			/* translators: %s: the part of the gift above what it includes. */
			part.textContent = deductible > 0 ? __( '%s of it is tax-deductible.' ).replace( '%s', this.formatMinorUnits( deductible ) ) : __( 'None of it is tax-deductible.' );
			this.goodsNote.appendChild( part );
		}

		this.goodsNote.hidden = false;

		/* translators: %s: what the gift includes. */
		lines.push( [ __( 'Includes %s worth' ).replace( '%s', described ), value ] );

		if ( goods.deductible ) {
			lines.push( [ __( 'Tax-deductible part' ), Math.max( 0, deductible ) ] );
		}
	}

	/**
	 * Takes the intent's total as the checkout's figure for GA4. It is the server's own figure for
	 * what is on screen, so it stands in when the latest quote failed and the funnel events would
	 * otherwise never be sent (release gate 0.5.3, adversarial review round 9, LOW-4).
	 *
	 * @param {Object} body The intent response.
	 * @return {void}
	 */
	adoptIntentTotal( body ) {
		if ( ! body || 'number' !== typeof body.total_charged ) {
			return;
		}

		// What is being charged is what the page says (round 11, MEDIUM-2).
		this.showTotal( body.total_charged );

		this.lastQuote = { ...( this.lastQuote || {} ), total_charged: body.total_charged };
		this.lastQuoteToken = this.quoteToken;

		if ( this.beginCheckoutPending ) {
			this.trackBeginCheckoutIfKnown();
		}
	}

	/**
	 * @return {void}
	 */
	trackBeginCheckoutIfKnown() {
		const detail = this.analyticsDetail();

		/*
		 * No quote yet - the donor typed an amount and pressed Continue inside the quote's
		 * debounce - so the event is sent when the quote arrives. Before, it was dropped, and
		 * GA4 never saw that checkout begin (found by the release gate's feature journey).
		 */
		this.beginCheckoutPending = ! detail;

		if ( detail ) {
			trackBeginCheckout( detail );
		}
	}

	/**
	 * @param {string} paymentType
	 * @return {void}
	 */
	trackAddPaymentInfoIfKnown( paymentType ) {
		const detail = this.analyticsDetail();

		if ( detail ) {
			trackAddPaymentInfo( { ...detail, paymentType } );
		}
	}

	/**
	 * R2-15 (rc2): whether this form is the fund settings screen's preview (its schema says so; it is printed into that page only).
	 *
	 * @return {boolean}
	 */
	isPreview() {
		return !! this.form && true === this.form.preview;
	}

	/**
	 * Saves the current, unsubmitted, entirely client-side draft.
	 *
	 * @return {void}
	 */
	saveDraftNow() {
		// A shared tablet keeps no donor's details for the next person (0.9.0). A cart keeps none either, nor the fund settings
		// screen's preview (R2-15): it runs on the same site as the real form, and a staff member's test typing is no donor's draft.
		if ( this.tablet || this.cart || this.isPreview() ) {
			return;
		}

		saveDraft( this.fundId, {
			step: this.step,
			selectedTierId: this.selectedTierId,
			customAmount: this.customAmount,
			selectedPeriods: this.selectedPeriods,
			coverFees: this.coverFees,
			isAnonymous: this.isAnonymous,
			donorFirstName: this.fields.donor_first_name ? this.fields.donor_first_name.value : '',
			donorLastName: this.fields.donor_last_name ? this.fields.donor_last_name.value : '',
			donorEmail: this.fields.donor_email ? this.fields.donor_email.value : '',
		} );
	}

	/**
	 * Restores whatever `draft-storage.js` has for this fund, if anything.
	 *
	 * @return {void}
	 */
	restoreDraft() {
		// R2-15 (rc2): the preview shows the fund's form as a new donor first sees it, never a draft left by the real form.
		if ( this.cart || this.isPreview() ) {
			return;
		}

		if ( this.tablet ) {
			clearDraft( this.fundId );

			return;
		}

		const draft = loadDraft( this.fundId );

		if ( ! draft ) {
			return;
		}

		this.selectedTierId = draft.selectedTierId || null;
		this.customAmount = draft.customAmount || null;
		// `buildMonthChips()` (called from `buildStepOne()`, after this method) reads
		// `this.selectedPeriods` when it first renders — restored here, before the DOM for it
		// exists yet, the same ordering `selectedTierId`/`customAmount` above already rely on.
		this.selectedPeriods = Array.isArray( draft.selectedPeriods ) ? draft.selectedPeriods : [];
		this.coverFees = !! draft.coverFees;
		this.isAnonymous = !! draft.isAnonymous;

		if ( this.feeCoverageInput ) {
			this.feeCoverageInput.checked = this.coverFees;
		}

		if ( this.anonCheckboxInput ) {
			this.anonCheckboxInput.checked = this.isAnonymous;
		}

		if ( this.customAmountInput && this.customAmount ) {
			this.customAmountInput.value = String( this.customAmount / 100 );
		}

		if ( this.fields.donor_first_name ) {
			this.fields.donor_first_name.value = draft.donorFirstName || '';
		}

		if ( this.fields.donor_last_name ) {
			this.fields.donor_last_name.value = draft.donorLastName || '';
		}

		if ( this.fields.donor_email ) {
			this.fields.donor_email.value = draft.donorEmail || '';
		}

		if ( draft.step && draft.step !== this.step ) {
			this.showStep( draft.step, false );
		}
	}

	/**
	 * `POST /checkout/intent`, then hands off to the gateway.
	 *
	 * Solves Turnstile first, synchronously with this call rather than pre-solved on step-2
	 * entry: a token is single-use (`Security\Turnstile`'s replay defence) and this is the
	 * fund's own `turnstile_site_key` from the already-fetched form schema, so nothing here is
	 * requested until the donor actually presses Give.
	 *
	 * @return {Promise<void>}
	 */
	async submit() {
		// One Give at a time: a second press, or a second alternate-gateway button, made a second
		// intent and left step 2 locked after Back (round 6, LOW-3).
		if ( this.submitting || this.paymentStepActive ) {
			return;
		}

		// C8 (0.9.40): a missing name or an email that is not one is said beside its field before anything is sent.
		const missing = this.missingDetails();

		if ( Object.keys( missing ).length > 0 ) {
			const marked = this.showFieldErrors( { body: { data: { fields: missing } } } );
			const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

			this.say(
				1 === marked
					? __( 'Please fix 1 thing to continue. It is marked in red.' )
					: __( 'Please fix these %d things to continue. They are marked in red.' ).replace( '%d', String( marked ) ),
				false
			);

			return;
		}

		this.submitting = true;
		this.submitGeneration = ( this.submitGeneration || 0 ) + 1;
		this.lockGatewayChoice( true );

		const generation = this.submitGeneration;
		const abandoned = () => generation !== this.submitGeneration;

		// 0.9.38: the busy state always ends. Every way out of submitOnce() is a message, the payment step or an abandoned
		// attempt; a step that never answers is ended by its own limit instead (busyTimedOut).
		this.watchBusy( 'security_check' );

		try {
			await this.submitOnce( abandoned );
		} finally {
			if ( ! abandoned() ) {
				this.endBusyWatch();
				this.submitting = false;
				this.lockGatewayChoice( false );
			}
		}
	}

	/**
	 * Starts (or restarts) the time limit for the step Give has reached (BUSY_LIMITS_MS).
	 *
	 * @param {string} step
	 * @return {void}
	 */
	watchBusy( step ) {
		window.clearTimeout( this.busyTimer );

		const generation = this.submitGeneration;

		this.busyStep = step;
		this.busyTimer = window.setTimeout( () => this.busyTimedOut( step, generation ), BUSY_LIMITS_MS[ step ] );
	}

	/**
	 * Stops the time limit: the attempt ended on its own.
	 *
	 * @return {void}
	 */
	endBusyWatch() {
		window.clearTimeout( this.busyTimer );
		this.busyTimer = null;
		this.busyStep = null;
	}

	/**
	 * Holds the time limit while the security check asks the donor to act, and gives the step its whole limit again after.
	 *
	 * @param {boolean} asking
	 * @return {void}
	 */
	pauseBusyWatch( asking ) {
		if ( asking ) {
			window.clearTimeout( this.busyTimer );
			this.busyTimer = null;
		} else if ( this.busyStep && ! this.busyTimer ) {
			this.watchBusy( this.busyStep );
		}
	}

	/**
	 * A step has taken longer than it legitimately can. The attempt still waiting is abandoned, so nothing it returns later is
	 * acted on (submitOnce() checks `abandoned()` after every wait); the donor is told and Give works again. No payment step
	 * was shown, so the donor confirmed nothing and nothing can have been charged. The step reached goes to the event log.
	 *
	 * @param {string} step
	 * @param {number} generation The attempt the limit was set for.
	 * @return {void}
	 */
	busyTimedOut( step, generation ) {
		this.busyTimer = null;

		if ( generation !== this.submitGeneration || ! this.submitting || this.paymentStepActive ) {
			return;
		}

		this.submitGeneration += 1;
		this.submitting = false;
		this.busyStep = null;
		this.lockGatewayChoice( false );
		this.paymentContainer.textContent = '';
		this.enableGive();
		// Every limited step follows the details step's button, so that is the one named (0.9.40 Phase 4: "press Give" named a
		// button the donor could not see there).
		this.say(
			window.wp && window.wp.i18n
				? window.wp.i18n.__( 'This is taking longer than it should. Nothing has been charged. Please press Continue to payment to try again.', 'anudan' )
				: 'This is taking longer than it should. Nothing has been charged. Please press Continue to payment to try again.'
		);
		api.reportStall( step, this.currentGateway() );
	}

	/**
	 * The donor's phone for the intent request: `+<dial><digits>` and the country it was typed under.
	 *
	 * Staging 0.5.13: the phone field filled `this.fields.donor_phone` and nothing ever sent it, so
	 * a typed number was lost. A blank phone sends nothing (it is optional). A number a wallet
	 * supplied without a leading `+` cannot be made international without guessing its country, so
	 * it is left out rather than saved wrong; the country is sent only when the typed field is where
	 * the number came from.
	 *
	 * @return {{donor_phone?: string, donor_phone_country?: string}}
	 */
	phonePayload() {
		const value = this.fields.donor_phone ? String( this.fields.donor_phone.value || '' ).replace( /[\s().-]/g, '' ) : '';

		if ( ! /^\+[0-9]{4,15}$/.test( value ) && ! this.typedPhoneIsPartial( value ) ) {
			return {};
		}

		const payload = { donor_phone: value };

		if ( this.phoneField && this.phoneField.getValue() === value ) {
			payload.donor_phone_country = this.phoneField.getCountry();
		}

		return payload;
	}

	/**
	 * Whether a phone value was typed into the field (so the server should be asked to judge it and
	 * show its message beside the field) rather than being absent or wallet-supplied junk.
	 *
	 * @param {string} value The normalised value.
	 * @return {boolean}
	 */
	typedPhoneIsPartial( value ) {
		return '' !== value && !! this.phoneField && this.phoneField.getValue() === value;
	}

	/**
	 * What to tell a donor when no security-check token could be had. The check's script comes from the network, so a donor
	 * whose connection has dropped cannot get one; telling them to "complete the security check" sends them looking for a
	 * problem they do not have, so a browser that says it is offline gets the connection message instead. A browser that still
	 * says it is online is asked once whether the site answers (0.9.37, PHN-12: WebKit reports going offline a moment late, and a
	 * donor who pressed Give in that moment was asked to complete a security check that could not load).
	 *
	 * @return {Promise<string>}
	 */
	async noSecurityCheckMessage() {
		if ( ( window.navigator && false === window.navigator.onLine ) || ! ( await api.siteAnswers() ) ) {
			return api.describeFailure( { ok: false, status: 0, body: null } );
		}

		// 0.9.41: the check is invisible, so the donor is not sent looking for one; plain words, and a press tries again.
		return window.wp && window.wp.i18n
			? window.wp.i18n.__( "We couldn't finish a quick security check. Nothing has been charged. Please press Continue to payment to try again.", 'anudan' )
			: "We couldn't finish a quick security check. Nothing has been charged. Please press Continue to payment to try again.";
	}

	/**
	 * Whether the server refused a request only for its security token (403 `anudan_turnstile_required`): one a fresh token
	 * answers. A 503 is not one of these; that is the site unable to check, and a new token does not help.
	 *
	 * @param {{status: number, body: any}} response
	 * @return {boolean}
	 */
	securityTokenRefused( response ) {
		return !! response && 403 === response.status && !! response.body && 'anudan_turnstile_required' === response.body.code;
	}

	/**
	 * The body of {@see submit}, which stops after any wait once `abandoned()` says the donor has
	 * gone Back since Give was pressed (round 6, MEDIUM-1).
	 *
	 * @param {function(): boolean} abandoned
	 * @return {Promise<void>}
	 */
	async submitOnce( abandoned ) {
		// R2-15 (rc2): the fund settings screen's preview shows the form and never starts a payment.
		if ( this.isPreview() ) {
			this.say( window.wp && window.wp.i18n
				? window.wp.i18n.__( 'This is a preview. Donors can give once you save.', 'anudan' )
				: 'This is a preview. Donors can give once you save.' );

			return;
		}

		this.giveButton.disabled = true;
		this.clearFieldErrors();
		this.showPreparing( true );

		const turnstileToken = await getTurnstileToken(
			this.turnstileContainer,
			this.form.turnstile_site_key || '',
			this.sessionId,
			{ interactive: ( asking ) => this.pauseBusyWatch( asking ), status: ( what ) => this.showSecurityStatus( what ) }
		);

		if ( abandoned() ) {
			return;
		}

		if ( ! turnstileToken ) {
			// After rc5: why, in the site's event log (staging's iPhone left no trace there).
			api.reportStall( 'security_check_failed', this.currentGateway(), turnstileLastError( this.turnstileContainer ) );
			this.watchBusy( 'connection_check' );

			const message = await this.noSecurityCheckMessage();

			if ( abandoned() ) {
				return;
			}

			this.enableGive();
			this.say( message );

			return;
		}

		const donor = {
			cover_fees: this.coverFees,
			gateway: this.currentGateway(),
			donor_email: this.fields.donor_email ? this.fields.donor_email.value : '',
			donor_first_name: this.fields.donor_first_name ? this.fields.donor_first_name.value : '',
			donor_last_name: this.fields.donor_last_name ? this.fields.donor_last_name.value : '',
			session_id: this.sessionId,
			turnstile_token: turnstileToken,
			idempotency_key: generateSessionId(),
			fields: this.collectFieldAnswers(),
			...this.botSignals(),
		};

		// A cart sends only its lines: a fund and a tier or a typed amount each, never a figure (A-1).
		const payload = this.cart
			? { ...donor, lines: this.cartPayloadLines() }
			: {
				...donor,
				fund_id: this.fundId,
				amount_tier_id: this.selectedTierId,
				custom_amount: this.customAmount,
				selected_periods: this.selectedPeriods,
				frequency: this.currentFrequency(),
				monthly_prompt: this.monthlyPromptOutcome || '',
				is_anonymous: this.isAnonymous,
			};

		// 0.9.14: the entries, each with its answers; the server prices the gift from them.
		if ( ! this.cart && this.entries ) {
			payload.entries = this.entries.collect();
			payload.amount_tier_id = null;
		}

		// D2 (0.9.40): the items chosen, keys and quantities only; the extra travels as the custom amount.
		if ( ! this.cart && this.items ) {
			payload.items = this.items.payload();
			payload.amount_tier_id = null;
		}

		// Per-month giving: the months chosen (each with its own amount choice only when the donor gave different amounts) and the
		// way of giving by month. Never a total: the server adds the months up, and checks the window again, when it opens the payment.
		if ( ! this.cart && this.dated ) {
			payload.months = this.dated.payload();
			payload.month_mode = this.dated.monthMode();
			Object.assign( payload, this.dated.extras() );
		}

		// 0.9.12: a gift moved from PayPal. The server takes the first payment date from the link, never from here.
		if ( ! this.cart && this.restart ) {
			payload.restart_token = this.restart.token;
		}

		const dedicate = this.cart ? undefined : this.collectDedicateAnswer();

		if ( undefined !== dedicate ) {
			payload.dedicate = dedicate;
		}

		if ( ! this.cart && this.registrationInput && this.registrationInput.value.trim() ) {
			payload.registration_number = this.registrationInput.value.trim();
		}

		Object.assign( payload, this.phonePayload(), this.consentPayload() );

		if ( ! this.cart ) {
			Object.assign( payload, this.askedPayload() );
		}

		this.watchBusy( 'intent' );

		let response = this.cart ? await api.cartIntent( payload ) : await api.intent( payload );

		spendTurnstileToken( this.turnstileContainer );

		if ( abandoned() ) {
			return;
		}

		/*
		 * F1 (rc3): a security token the server refused as expired, already used or not accepted is replaced and the request
		 * sent once more, without asking the donor to do anything. Nothing was started for the refused one, so the retry has a
		 * key of its own. A second refusal is said in words.
		 */
		if ( this.securityTokenRefused( response ) ) {
			this.watchBusy( 'security_check' );

			const fresh = await getTurnstileToken(
				this.turnstileContainer,
				this.form.turnstile_site_key || '',
				this.sessionId,
				{ interactive: ( asking ) => this.pauseBusyWatch( asking ), status: ( what ) => this.showSecurityStatus( what ) }
			);

			if ( abandoned() ) {
				return;
			}

			if ( fresh ) {
				payload.turnstile_token = fresh;
				payload.idempotency_key = generateSessionId();
				this.watchBusy( 'intent' );
				response = this.cart ? await api.cartIntent( payload ) : await api.intent( payload );
				spendTurnstileToken( this.turnstileContainer );

				if ( abandoned() ) {
					return;
				}
			}
		}

		if ( ! response.ok || ! response.body ) {
			this.enableGive();

			const refreshed = this.takeFreshPageToken( response );

			// A refused field is shown beside that field; only what cannot be placed there is said in
			// general terms.
			const marked = 422 === response.status ? this.showFieldErrors( response ) : 0;

			if ( 'again' === refreshed ) {
				// After rc7: the page was already brought up to date once and was refused the same way again: no second loop.
				const reference = response.body && response.body.data && response.body.data.reference;

				this.say(
					__( 'This page could not be brought up to date. Please reload the page, then try again. You have not been charged.' ) +
					( 'string' === typeof reference && '' !== reference ? ` ${ __( 'Reference: %s' ).replace( '%s', reference ) }` : '' )
				);

				return;
			}

			this.say(
				marked
					? __( 'Some details need fixing. Please correct the fields marked in red, then press Continue to payment again.' )
					: api.describeFailure( response )
			);

			return;
		}

		// Still disabled: a second press while the gateway's script loads made a second
		// donation (adversarial review round 5, LOW-2). The payment step replaces the button.
		clearDraft( this.fundId );
		this.adoptIntentTotal( response.body );
		this.rememberConsentAnswered();

		const donationUid = response.body.donation_uid;
		const gatewayId = response.body.gateway;

		this.paymentContainer.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'Loading payment…', 'anudan' )
			: 'Loading payment…';
		// Q1 (0.9.40): the payment part opens now, so "Loading payment…" (and an offer of the other method, if the script
		// does not load) is on screen while the payment's own fields are fetched.
		this.paymentContainer.hidden = false;
		this.paymentArea.hidden = false;
		this.describePaymentMethod();
		// The other method waits for this one's fields: a switch while the script loads re-quoted for the other gateway and
		// showed its total beside an intent charging this one's (release gate 0.5.3, round 12, MEDIUM-3).
		this.switchButton.disabled = true;

		const publicConfig = response.body.public_config || {};

		this.watchBusy( 'payment_script' );

		const gatewayGlobal = await loadGateway( gatewayId, 8000, publicConfig );

		if ( abandoned() ) {
			return;
		}

		this.watchBusy( 'payment_fields' );

		if ( ! gatewayGlobal ) {
			this.enableGive();
			this.offerAlternateGateway( gatewayId, donationUid );

			return;
		}

		this.trackAddPaymentInfoIfKnown( gatewayId );

		/*
		 * Release gate 0.5.3, adversarial review round 4: this used to go straight to polling, so
		 * no card field or PayPal button was ever shown and only the express wallet could pay.
		 * Card details are entered in the gateway's own fields, never this page's (rule 5), and
		 * the donation completes only when the gateway's webhook says so (rule 6).
		 */
		// 0.9.12: a moved gift's first payment is on a later date; nothing is charged now.
		this.scheduledIntent = response.body.first_payment_date ? response.body : null;

		if ( 'stripe' === gatewayId ) {
			this.intentTotal = response.body.total_charged;
			this.mountStripePayment( gatewayGlobal, publicConfig, response.body.client_token, donationUid, 'setup' === response.body.confirmation );

			return;
		}

		if ( 'paypal' === gatewayId ) {
			this.mountPayPalButtons( gatewayGlobal, response.body.client_token, donationUid, true === response.body.recurring );

			return;
		}

		this.pollStatus( donationUid );
	}

	/**
	 * Replaces the Give button with the gateway's payment fields, once the donation exists.
	 *
	 * @return {void}
	 */
	enterPaymentStep() {
		if ( this.paymentStepActive ) {
			// Already locked: recomputing would find every input disabled and lose them (LOW-3).
			this.paymentContainer.textContent = '';

			return;
		}

		this.paymentStepActive = true;
		this.paymentContainer.textContent = '';
		this.paymentContainer.hidden = false;

		// Q1 (0.9.40): step 3 of 3, "Payment": the details fold away, the summary and the method are what is on screen.
		this.paymentArea.hidden = false;
		this.describePaymentMethod();
		this.switchButton.disabled = false;
		this.refreshProgress();

		if ( this.summaryBar ) {
			this.paymentArea.insertBefore( this.summaryBar, this.paymentArea.firstChild );
		}

		// R3-1 (rc4): the card form and its Give button, then "Or pay with PayPal", then Back, always last, full width.
		this.paymentArea.appendChild( this.backButton );

		// Give is about to be hidden, and it holds focus when pressed from the keyboard: focus
		// would fall to <body>, outside the focus trap, where Safari's next Tab leaves the page
		// (release gate 0.5.3, WebKit). It moves to the payment fields' container instead, the
		// way a step change moves it to the step's heading. Give is disabled while the intent is
		// made, which can already have dropped focus to <body>; focus the donor moved anywhere
		// else is left where it is.
		const focused = document.activeElement;
		const giveHadFocus = ! focused || focused === document.body || focused === this.giveButton;

		this.giveButton.hidden = true;
		this.showPreparing( false );
		this.hidePaymentWallet();

		// R2-19 (rc2): the payment step from its top, as every step change; focus then moves without scrolling again.
		this.scrollToStep( this.stepTop( 2 ) );

		if ( giveHadFocus ) {
			this.paymentContainer.setAttribute( 'tabindex', '-1' );
			this.paymentContainer.focus( { preventScroll: true } );
		}

		if ( this.gatewaySelector ) {
			this.gatewaySelector.hidden = true;
		}

		// What this payment is for is fixed now: a name or email edited after Give would not
		// reach the donation (round 5, MEDIUM-1). Back unlocks them, and makes a new intent.
		this.lockedInputs = Array.from( this.stepTwoEl.querySelectorAll( 'input, select, textarea' ) )
			.filter( ( input ) => ! this.paymentContainer.contains( input ) && ! input.disabled );
		this.lockedInputs.forEach( ( input ) => {
			input.disabled = true;
		} );

		// R2-20: the details step's row leaves the foot of the screen; the payment box holds the button that pays.
		this.syncDock();
	}

	/**
	 * Undoes {@see enterPaymentStep}: the mounted payment fields go, Give and the gateway choice
	 * come back, and step 2's answers are editable again. The donation already created stays
	 * pending, and the abandoned-checkout purge collects it.
	 *
	 * @return {void}
	 */
	leavePaymentStep() {
		this.paymentStepActive = false;
		this.paymentContainer.textContent = '';
		this.paymentArea.hidden = true;
		this.switchButton.disabled = false;
		this.giveButton.hidden = false;
		// R2-06, R3-4: Back returns under Continue to payment's row (under its place, when a phone has the row pinned).
		this.anchorOf( this.stepTwoActions ).after( this.backButton );
		this.enableGive();
		this.refreshProgress();

		if ( this.summaryBar && this.stepTwoActions && 2 === this.step ) {
			const anchor = this.anchorOf( this.stepTwoActions );

			anchor.parentNode.insertBefore( this.summaryBar, anchor );
		}

		this.syncDock();

		if ( this.gatewaySelector ) {
			this.gatewaySelector.hidden = false;
		}

		( this.lockedInputs || [] ).forEach( ( input ) => {
			input.disabled = false;
		} );
		this.lockedInputs = [];

		this.walletSuppressed = false;

		if ( this.paymentWallet && this.paymentWalletSlot ) {
			this.paymentWalletSlot.hidden = false;
		}
	}

	/**
	 * Takes the wallet button off screen once a payment is under way. Left live beside the card
	 * fields, the "confirming" message or the thank-you screen, one tap made and charged a second
	 * donation (release gate 0.5.3, adversarial review round 6, MEDIUM-5).
	 *
	 * @return {void}
	 */
	hidePaymentWallet() {
		this.walletSuppressed = true;

		if ( this.paymentWalletSlot ) {
			this.paymentWalletSlot.hidden = true;
		}
	}

	/**
	 * Stripe's Payment Element: card details typed into Stripe's own iframe, confirmed against
	 * the intent the server created. 3-D Secure, when the bank asks for it, happens in Stripe's
	 * own dialog (`redirect: 'if_required'`).
	 *
	 * @param {Function} Stripe        Stripe's own global.
	 * @param {Object}   publicConfig  `{ publishable_key }`, from the intent response.
	 * @param {string}   clientSecret  The intent's client secret.
	 * @param {string}   donationUid
	 * @param {boolean}  setup         0.9.12: the secret is a SetupIntent's. The card is saved for a first
	 *                                 payment on a later date, and nothing is charged now.
	 * @return {void}
	 */
	mountStripePayment( Stripe, publicConfig, clientSecret, donationUid, setup = false ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		if ( ! publicConfig.publishable_key || ! clientSecret ) {
			this.offerAlternateGateway( 'stripe', donationUid );

			return;
		}

		this.enterPaymentStep();

		const stripe = Stripe( publicConfig.publishable_key );
		const elements = stripe.elements( stripeElementsOptions( { clientSecret } ) );
		const fields = document.createElement( 'div' );

		fields.className = 'anudan-checkout__card-fields';
		this.paymentContainer.appendChild( fields );
		elements.create( 'payment', paymentElementOptions() ).mount( fields );

		/*
		 * 0.9.1: what happened to a payment is said right above the button that started it. At phone
		 * width the form's own message sits a screen or more above Stripe's fields (Link's "save my
		 * information" box alone is half a screen), so a refusal written only there was easy to miss.
		 */
		const note = document.createElement( 'p' );

		note.className = 'anudan-checkout__message anudan-checkout__payment-note';
		note.setAttribute( 'aria-hidden', 'true' );
		note.hidden = true;
		this.paymentContainer.appendChild( note );
		this.paymentNote = note;

		// 0.9.41 rc2 ("donors are never lost"): said before the bank's own check can appear, so its window is expected.
		const bank = document.createElement( 'p' );

		bank.className = 'anudan-checkout__payment-hint';
		bank.setAttribute( 'data-anudan-bank-hint', '' );
		bank.textContent = __( 'Your bank may ask you to confirm this payment.' );
		this.paymentContainer.appendChild( bank );

		const pay = document.createElement( 'button' );

		pay.type = 'button';
		pay.className = 'anudan-checkout__button';
		// The amount being charged, on the button that charges it (round 11, MEDIUM-2).
		// The intent's own total: what `confirmPayment` charges (round 12, MEDIUM-3).
		// Q1 (0.9.40): "Give $25.00", "Give $25.00 monthly" for a recurring gift.
		const often = 'one_time' === this.currentFrequency() ? '' : String( ( this.form.frequency_labels || {} )[ this.currentFrequency() ] || '' ).toLowerCase();

		pay.className = 'anudan-checkout__button anudan-checkout__button--block';
		pay.textContent = 'number' === typeof this.intentTotal
			/* translators: 1: the amount being charged, e.g. $25.00, 2: how often, e.g. "monthly", or nothing. */
			? __( 'Give %1$s %2$s' ).replace( '%1$s', this.formatMinorUnits( this.intentTotal ) ).replace( '%2$s', often ).trim()
			: __( 'Give' );

		if ( setup && this.scheduledIntent ) {
			/* translators: %s: the date of the first payment. */
			pay.textContent = __( 'Set up my gift (first payment %s)' ).replace( '%s', String( this.scheduledIntent.first_payment_label || '' ) );
		}

		this.paymentContainer.appendChild( pay );
		this.holdButtonSteady( fields, pay );

		pay.addEventListener( 'click', async () => {
			pay.disabled = true;
			// Not Back while Stripe confirms: going back now left a payment confirming behind a
			// summary showing another amount (release gate 0.5.3, adversarial review round 7, LOW-3).
			this.backButton.disabled = true;
			// 0.9.41 rc2: the payment itself is a wait too; a plain line, never a frozen button.
			this.sayAtPayment( __( 'Processing your payment. Please keep this page open.' ), true );

			/*
			 * 0.9.1: Stripe's confirmation never answering (a stalled bank check, a script that stopped
			 * responding) left the button disabled and nothing said, for ever. After a wait the donor is
			 * told not to pay again and the event log records it; the confirmation, if it comes, still
			 * carries on below.
			 */
			let answered = false;
			const watchdog = window.setTimeout( () => {
				if ( answered ) {
					return;
				}

				this.sayAtPayment( __( 'Your bank has not answered yet. Please do not pay again; this page will update when it does. If nothing changes in a few minutes, please contact us before trying again.' ) );
				api.reportProblem( donationUid, 'no_answer', 'confirm_timeout' );
			}, STRIPE_CONFIRM_PATIENCE_MS );

			let result;

			try {
				result = setup
					? await stripe.confirmSetup( {
						elements,
						confirmParams: { return_url: window.location.href },
						redirect: 'if_required',
					} )
					: await stripe.confirmPayment( {
						elements,
						confirmParams: { return_url: window.location.href },
						redirect: 'if_required',
					} );
			} catch ( error ) {
				// A rejection is no answer either: treated as one, not left with Pay and Back
				// disabled and nothing said (round 12, LOW-2).
				result = { error: { type: 'api_connection_error' } };
			} finally {
				answered = true;
				window.clearTimeout( watchdog );
			}

			// No clear answer: the payment may have gone through, so wait for Stripe rather than
			// letting Back and Give make a second one (round 11, LOW-1).
			if ( result.error && isUnclearStripeError( result.error ) ) {
				this.pollStatus( donationUid );
				this.say( __( 'We could not hear back about this payment. It may have gone through, so please do not pay again; this page will update when it is confirmed.' ) );
				api.reportProblem( donationUid, 'no_answer', stripeErrorCode( result.error ) );

				return;
			}

			if ( result.error ) {
				pay.disabled = false;
				this.backButton.disabled = false;
				// Q1 (0.9.40), the design's decline: the bank's own words, then what to do and that nothing was taken. A card the bank
				// refused (`card_error`) was not charged; Stripe's other refusals are the card form's own fields, said beside them.
				const said = result.error.message || __( 'We could not confirm this payment. You have not been charged. Please check your card details and try again.' );

				this.sayAtPayment( result.error.message && 'card_error' === result.error.type ? `${ said } ${ __( 'Please check the details or try another card. You have not been charged.' ) }` : said );
				api.reportProblem( donationUid, 'refused_in_browser', stripeErrorCode( result.error ) );

				return;
			}

			if ( setup && this.scheduledIntent ) {
				this.showScheduled( this.scheduledIntent );

				return;
			}

			api.confirm( donationUid );
			this.pollStatus( donationUid );
		} );
	}

	/**
	 * Keeps the payment button where it is on screen while Stripe's fields change height above it.
	 *
	 * Once the card is complete Stripe adds Link's "save my information" box inside its frame, which at
	 * phone width pushes the button down half a screen as the donor reaches for it, so a tap landed on
	 * whatever moved into its place (issue 0093). When the fields grow or shrink while the button is on
	 * screen, the page (or the dialog, when the form is in one) scrolls by the same amount, so the
	 * button stays under the donor's finger and the change happens above it.
	 *
	 * @param {HTMLElement} fields The element Stripe's fields are mounted in.
	 * @param {HTMLElement} button The payment button below it.
	 * @return {void}
	 */
	holdButtonSteady( fields, button ) {
		if ( 'function' !== typeof window.ResizeObserver ) {
			return;
		}

		let lastHeight = fields.getBoundingClientRect().height;
		// What a whole-pixel scroll left over from the last change (Q1, 0.9.40).
		let carry = 0;

		const observer = new window.ResizeObserver( () => {
			if ( ! button.isConnected ) {
				observer.disconnect();

				return;
			}

			const height = fields.getBoundingClientRect().height;
			const change = height - lastHeight;

			lastHeight = height;

			if ( 0 === change ) {
				return;
			}

			const scroller = scrollingAncestor( button );
			const view = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
			const rect = button.getBoundingClientRect();

			// Only a button on screen is about to be tapped; one off screen is left where the page puts it.
			if ( rect.bottom - change <= view.top || rect.top - change >= view.bottom ) {
				return;
			}

			// A page scrolls by whole pixels, and the browser rounds a fraction given to it its own way (23.45px became 24, and
			// the button crept): the nearest whole pixel, with what was left over from the last change carried, keeps the
			// button within half a pixel of where it was however many changes come.
			const wanted = change + carry;
			const by = Math.round( wanted );

			carry = wanted - by;

			if ( scroller ) {
				scroller.scrollTop += by;
			} else {
				window.scrollBy( 0, by );
			}
		} );

		observer.observe( fields );
	}

	/**
	 * Says something about the payment right above the payment button, and brings it into view: the
	 * place the donor is looking when it happens. Screen readers hear it through the live region, as
	 * every message. An empty string clears it.
	 *
	 * @param {string}  text
	 * @param {boolean} [progress] 0.9.41 rc2: a wait, shown in the calm box rather than the error box.
	 * @return {void}
	 */
	sayAtPayment( text, progress = false ) {
		this.paymentNote.classList.toggle( 'anudan-checkout__message--progress', progress );
		if ( ! this.paymentNote ) {
			this.say( text );

			return;
		}

		// Announced as every message is, and shown once: here, not also in the form's header, where the
		// same words a screen away read as a second, different message (0.9.2 local:e2e).
		this.liveRegion.textContent = text;

		if ( this.messageEl ) {
			this.messageEl.textContent = '';
			this.messageEl.hidden = true;
		}

		this.paymentNote.textContent = text;
		this.paymentNote.hidden = '' === text;

		if ( text && 'function' === typeof this.paymentNote.scrollIntoView ) {
			this.paymentNote.scrollIntoView( { block: 'nearest' } );
		}
	}

	/**
	 * PayPal's own buttons, for the order the server created. On approval the server is asked to
	 * capture it; the donation completes on PayPal's webhook.
	 *
	 * @param {Object}  paypal      PayPal's own global.
	 * @param {string}  orderId     The order id the server created, or for a recurring gift the subscription id.
	 * @param {string}  donationUid
	 * @param {boolean} recurring   Whether `orderId` is a subscription the donor approves at PayPal. Nothing is
	 *                              captured by this page: PayPal's signed webhooks complete the first payment and
	 *                              every renewal.
	 * @return {void}
	 */
	mountPayPalButtons( paypal, orderId, donationUid, recurring = false ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		if ( ! orderId || ! paypal || 'function' !== typeof paypal.Buttons ) {
			this.offerAlternateGateway( 'paypal', donationUid );

			return;
		}

		this.enterPaymentStep();

		// 0.9.41 rc2 ("donors are never lost"): said before PayPal's window opens, so the donor knows to come back.
		const opens = document.createElement( 'p' );

		opens.className = 'anudan-checkout__payment-hint';
		opens.setAttribute( 'data-anudan-paypal-hint', '' );
		opens.textContent = __( "A PayPal window will open. Finish there, and you'll come back here." );
		this.paymentContainer.appendChild( opens );

		const slot = document.createElement( 'div' );

		slot.className = 'anudan-checkout__paypal-buttons';
		this.paymentContainer.appendChild( slot );

		// D2 (0.9.41-rc3): PayPal's frame on whole screen pixels, so its button's right border is never blended away.
		if ( this.stopPayPalAlign ) {
			this.stopPayPalAlign();
		}

		this.stopPayPalAlign = keepAlignedToScreenPixels( slot );

		/*
		 * B2 (rc2, decided 2026-10-26): one white PayPal button. The script is loaded with PayPal's card, Pay Later, credit and
		 * Venmo buttons switched off (gateway-loader.js), and this names PayPal itself as the one way to pay, in the brand's style.
		 */
		const branded = {
			style: paypalButtonStyle(),
			...( paypal.FUNDING && paypal.FUNDING.PAYPAL ? { fundingSource: paypal.FUNDING.PAYPAL } : {} ),
		};

		if ( recurring ) {
			paypal.Buttons( {
				...branded,
				createSubscription: () => orderId,
				onApprove: () => {
					// 0.9.12: a moved gift takes its first payment on a later date; there is nothing to wait for.
					if ( this.scheduledIntent ) {
						this.showScheduled( this.scheduledIntent );

						return;
					}

					// The donor approved the subscription at PayPal. The first payment is known only from
					// PayPal's webhook, so this waits for it as a one-time capture does.
					this.backButton.disabled = true;
					this.pollStatus( donationUid );
				},
				onCancel: () => {
					this.say( __( 'The PayPal approval was canceled. You have not been charged.' ) );
				},
				onError: () => {
					this.say( __( 'PayPal could not set up this recurring gift. You have not been charged; please try again.' ) );
					api.reportProblem( donationUid, 'gateway_error', 'paypal_subscription' );
				},
			} ).render( slot );

			return;
		}

		paypal.Buttons( {
			...branded,
			createOrder: () => orderId,
			onApprove: async () => {
				// Not Back while the server captures: round 7's LOW-3, on PayPal (round 8, LOW-1).
				this.backButton.disabled = true;
				// 0.9.41 rc2: back from PayPal, the capture is a wait; said, not a still page.
				this.say( __( 'Finishing your payment with PayPal. Please keep this page open.' ), false, true );

				const captured = await api.capturePayPal( donationUid, orderId );

				/*
				 * No clear answer - PayPal did not reply (202), the connection dropped (0), or a
				 * proxy timed out first (a 5xx without this API's own error envelope) - and the
				 * money may have moved. Wait for the webhook rather than inviting a second payment
				 * (round 5, MEDIUM-2; the browser's own share of it, round 6, MEDIUM-2). The
				 * route's own refusal is a 502 with a `code`: that one is definite.
				 */
				const unclear = 202 === captured.status
					|| 0 === captured.status
					|| ( captured.status >= 500 && ! ( captured.body && captured.body.code ) );

				if ( unclear ) {
					this.pollStatus( donationUid );
					api.reportProblem( donationUid, 'no_answer', `paypal_capture_${ captured.status }` );
					this.say(
						( 202 === captured.status && captured.body && captured.body.message )
							|| __( 'We could not hear back about this payment. It may have gone through, so please do not pay again; this page will update when PayPal confirms.' )
					);

					return;
				}

				if ( ! captured.ok ) {
					this.backButton.disabled = false;
					this.say(
						api.describeFailure(
							captured,
							__( 'PayPal approved the payment but it could not be completed. You have not been charged; please try again.' )
						)
					);

					return;
				}

				this.pollStatus( donationUid );
			},
			onCancel: () => {
				this.say( __( 'The PayPal payment was canceled. You have not been charged.' ) );
			},
			onError: () => {
				this.say( __( 'PayPal could not take this payment. You have not been charged; please try again.' ) );
				api.reportProblem( donationUid, 'gateway_error', 'paypal_buttons' );
			},
		} ).render( slot );
	}

	/**
	 * WP-44's own most-emphasised requirement, at the one point a gateway can still fail after a
	 * donor has chosen it: its own JavaScript SDK did not load (a blocked request, an ad blocker,
	 * a network failure — {@see loadGateway}'s own docblock). "The other remains usable" — if
	 * this fund offers another gateway, this donor is offered it here, as a fresh attempt, rather
	 * than being left at a dead end. The already-created donation for the failed gateway is left
	 * exactly as it is (`Donations\AbandonedCheckoutPurge` is what eventually collects it, the
	 * same as any other pending donation nobody completed) — this starts a new one, with its own
	 * idempotency key, because a different gateway is a different intent, not a retry of the same
	 * one.
	 *
	 * @param {string} failedGatewayId The gateway id whose script did not load.
	 * @param {string} [donationUid]   The donation it was for, so the event log records it (0.9.1).
	 * @return {void}
	 */
	offerAlternateGateway( failedGatewayId, donationUid = '' ) {
		if ( donationUid ) {
			api.reportProblem( donationUid, 'gateway_unavailable', failedGatewayId );
		}

		const remaining = this.availableGateways().filter( ( id ) => id !== failedGatewayId );

		if ( 0 === remaining.length ) {
			this.paymentContainer.textContent = window.wp && window.wp.i18n
				? window.wp.i18n.__( 'We could not load the payment form. Please refresh and try again.', 'anudan' )
				: 'We could not load the payment form. Please refresh and try again.';

			return;
		}

		this.paymentContainer.innerHTML = '';

		const message = document.createElement( 'p' );

		message.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'That payment method could not be loaded. You can try another one below.', 'anudan' )
			: 'That payment method could not be loaded. You can try another one below.';
		this.paymentContainer.appendChild( message );

		const labels = this.form.gateway_labels || {};

		remaining.forEach( ( id ) => {
			const retry = document.createElement( 'button' );

			retry.type = 'button';
			retry.className = 'anudan-checkout__button anudan-checkout__button--secondary';
			retry.textContent = labels[ id ] || id;
			retry.addEventListener( 'click', () => {
				this.paymentContainer.querySelectorAll( 'button' ).forEach( ( button ) => {
					button.disabled = true;
				} );
				this.selectedGateway = id;
				saveGatewaySelection( this.fundId, id );
				// The last answer's fee-box totals were for the other gateway's fee.
				this.amountToken += 1;

				// The choice on screen follows, so Back shows the gateway in use (round 13, LOW-4).
				if ( this.gatewaySelector ) {
					this.gatewaySelector.querySelectorAll( 'input' ).forEach( ( input ) => {
						input.checked = id === input.value;
					} );
				}

				this.submit();
			} );

			this.paymentContainer.appendChild( retry );
		} );
	}

	/**
	 * Handles a wallet's `paymentmethod` event, from either slot (WP-19, PRD §1.5.1).
	 *
	 * The wallet always supplies the payment method. Whether it *also* supplies identity is the
	 * one thing that differs between the two slots:
	 *
	 * - **Express** (`isExpress` true, step 1): reaching this callback at all already implies
	 *   `wallets_eligible` was true, since the express slot does not exist in the DOM otherwise —
	 *   so the wallet's own name/email/phone are written over whatever is in the (unseen) step-2
	 *   fields, and the donor never visits step 2.
	 * - **Payment-step** (`isExpress` false): the wallet only fills a field the donor left empty,
	 *   never one they already typed, and any required custom field the fund defines has already
	 *   had its chance to block submission — the wallet supplies a payment method, not an
	 *   exemption from the fund's own required fields.
	 *
	 * Turnstile is still solved either way — the security control is not something a payment
	 * method changes (CLAUDE.md non-negotiable rule 4). **Known gap, recorded rather than
	 * silently assumed away:** soliciting a challenge from `this.turnstileContainer` while step 1
	 * is the active step relies on Turnstile's own invisible/managed mode to complete without the
	 * container being visible; a real end-to-end run against a live Turnstile site key is
	 * assigned to WP-24b (`09-PHASE-4-BUILD.md`), which is where this actually gets exercised
	 * rather than assumed.
	 *
	 * @param {any}     event     Stripe's `PaymentRequest` `paymentmethod` event.
	 * @param {boolean} isExpress True for the step-1 slot.
	 * @return {Promise<void>}
	 */
	async handleWalletPaymentMethod( event, isExpress ) {
		/*
		 * One payment at a time, the wallet included: a wallet tap while Give was in flight, or
		 * Give pressed while the wallet confirmed, made a second intent (release gate 0.5.3,
		 * adversarial review round 7, LOW-2).
		 */
		if ( this.submitting || this.paymentStepActive ) {
			event.complete( 'fail' );

			return;
		}

		this.submitting = true;
		this.submitGeneration = ( this.submitGeneration || 0 ) + 1;
		this.giveButton.disabled = true;

		const generation = this.submitGeneration;

		try {
			await this.walletPayment( event, isExpress );
		} finally {
			// Only its own flag: after Back, a newer Give may hold it now (round 8, LOW-1).
			if ( generation === this.submitGeneration ) {
				this.submitting = false;

				if ( ! this.paymentStepActive ) {
					this.enableGive();
				}
			}
		}
	}

	/**
	 * The body of {@see handleWalletPaymentMethod}, run while it holds the one-payment flag.
	 *
	 * @param {Object}  event     Stripe's `paymentmethod` event.
	 * @param {boolean} isExpress Whether this is the top-of-step-1 express lane.
	 * @return {Promise<void>}
	 */
	async walletPayment( event, isExpress ) {
		if ( this.nothingChosen() ) {
			event.complete( 'fail' );
			this.say(
				window.wp && window.wp.i18n
					? window.wp.i18n.__( 'Please choose an amount first.', 'anudan' )
					: 'Please choose an amount first.'
			);

			return;
		}

		const details = event.paymentMethod.billing_details || {};
		const { firstName, lastName } = splitPayerName( details.name );

		if ( isExpress || ! this.fields.donor_first_name.value ) {
			this.fields.donor_first_name.value = firstName;
		}

		if ( isExpress || ! this.fields.donor_last_name.value ) {
			this.fields.donor_last_name.value = lastName;
		}

		if ( isExpress || ! this.fields.donor_email.value ) {
			this.fields.donor_email.value = details.email || '';
		}

		if ( details.phone && ( isExpress || ! this.fields.donor_phone ) ) {
			this.fields.donor_phone = { value: details.phone };
		}

		const turnstileToken = await getTurnstileToken(
			this.turnstileContainer,
			this.form.turnstile_site_key || '',
			this.sessionId,
			{ status: ( what ) => this.showSecurityStatus( what ) }
		);

		if ( ! turnstileToken ) {
			event.complete( 'fail' );
			this.say( await this.noSecurityCheckMessage() );

			return;
		}

		const payload = {
			fund_id: this.fundId,
			amount_tier_id: this.selectedTierId,
			custom_amount: this.customAmount,
			selected_periods: this.selectedPeriods,
			frequency: this.currentFrequency(),
			cover_fees: this.coverFees,
			gateway: this.form.wallet.gateway,
			monthly_prompt: this.monthlyPromptOutcome || '',
			donor_email: this.fields.donor_email.value,
			donor_first_name: this.fields.donor_first_name.value,
			donor_last_name: this.fields.donor_last_name.value,
			session_id: this.sessionId,
			turnstile_token: turnstileToken,
			idempotency_key: generateSessionId(),
			...this.botSignals(),
			// The fund's own questions are on step 1 for both lanes; the wallet supplies payment
			// (and, on the express lane, identity) — never an answer to one of them.
			fields: this.collectFieldAnswers(),
			is_anonymous: this.isAnonymous,
		};

		const dedicate = this.collectDedicateAnswer();

		if ( undefined !== dedicate ) {
			payload.dedicate = dedicate;
		}

		if ( this.registrationInput && this.registrationInput.value.trim() ) {
			payload.registration_number = this.registrationInput.value.trim();
		}

		Object.assign( payload, this.phonePayload(), this.askedPayload() );

		const response = await api.intent( payload );

		spendTurnstileToken( this.turnstileContainer );

		if ( ! response.ok || ! response.body ) {
			event.complete( 'fail' );
			this.say( api.describeFailure( response ) );

			return;
		}

		/*
		 * The donor authorised the total the wallet sheet showed. That follows the price quote,
		 * which can lag a change of amount or fail; the charge is the intent's. Unless they are the
		 * same, nothing is charged and the donor is asked to try again (release gate 0.5.3,
		 * adversarial review round 9, MEDIUM-1). The intent is left pending, as any abandoned one.
		 */
		const sheet = isExpress ? this.expressWallet : this.paymentWallet;

		if ( ! sheet || sheet.shownTotal !== response.body.total_charged ) {
			event.complete( 'fail' );
			this.say(
				window.wp && window.wp.i18n
					? window.wp.i18n.__( 'The amount changed while the payment sheet was open, so nothing was charged. Please check the amount and try again.', 'anudan' )
					: 'The amount changed while the payment sheet was open, so nothing was charged. Please check the amount and try again.'
			);

			return;
		}

		event.complete( 'success' );
		clearDraft( this.fundId );
		this.adoptIntentTotal( response.body );
		this.trackAddPaymentInfoIfKnown( isExpress ? 'express_wallet' : 'wallet' );

		const controller = isExpress ? this.expressWallet : this.paymentWallet;

		// Not Back while the wallet's payment is confirmed (round 8, LOW-1).
		this.backButton.disabled = true;

		let confirmation = null;

		try {
			confirmation = await controller.stripe.confirmCardPayment( response.body.client_token, {
				payment_method: event.paymentMethod.id,
			} );
		} catch ( error ) {
			confirmation = null;
		} finally {
			// Even if the call rejects rather than resolving with an error (round 9, LOW-1).
			this.backButton.disabled = false;
		}

		/*
		 * No clear answer - the call rejected, or Stripe could not be reached - and the payment
		 * may have gone through. Re-arming Give and the wallet invited a second payment on a new
		 * intent; this waits for Stripe's webhook instead, as the PayPal capture does (release
		 * gate 0.5.3, adversarial review round 10, LOW-1).
		 */
		if ( ! confirmation || ( confirmation.error && isUnclearStripeError( confirmation.error ) ) ) {
			if ( isExpress ) {
				this.showStep( 2 );
			}

			this.pollStatus( response.body.donation_uid );
			this.say(
				window.wp && window.wp.i18n
					? window.wp.i18n.__( 'We could not hear back about this payment. It may have gone through, so please do not pay again; this page will update when it is confirmed.', 'anudan' )
					: 'We could not hear back about this payment. It may have gone through, so please do not pay again; this page will update when it is confirmed.'
			);

			return;
		}

		if ( confirmation.error ) {
			const fallback = window.wp && window.wp.i18n
				? window.wp.i18n.__( 'We could not confirm this payment.', 'anudan' )
				: 'We could not confirm this payment.';

			this.say( confirmation.error.message || fallback );

			return;
		}

		if ( isExpress ) {
			this.showStep( 2 );
		}

		updateStitch( this.stitch, this.liveRegion, SHOWN_STEPS, SHOWN_STEPS, false );
		this.pollStatus( response.body.donation_uid );
	}

	/**
	 * Polls `GET /checkout/status/{uid}` — advisory only; the webhook is authoritative
	 * (CLAUDE.md rule 6). `09-PHASE-4-BUILD.md` WP-22: "honest interim wording — it must never
	 * claim completion before the webhook confirms it," so {@see self.renderConfirming} runs
	 * first and stays up for as many polls as it takes; only a response whose own `status` is
	 * `completed` — the webhook's word, never this call's — reaches
	 * {@see self.renderConfirmation}.
	 *
	 * @param {string} donationUid
	 * @return {void}
	 */
	pollStatus( donationUid ) {
		this.renderConfirming();

		let unanswered = 0;
		let longWait = false;
		let beingConfirmed = false;
		const startedAt = Date.now();

		const poll = async () => {
			const response = await api.status( donationUid );

			unanswered = response.ok ? 0 : unanswered + 1;

			/*
			 * Polling never stops on its own, so a site that stops answering (a 429, a dropped
			 * connection, a 500) used to leave "confirming" on screen for ever with no word that
			 * anything was wrong. After a few misses in a row the donor is told, once, what is
			 * known: the payment may have gone through, and not to pay again.
			 */
			if ( 4 === unanswered ) {
				const waiting = window.wp && window.wp.i18n
					? window.wp.i18n.__( 'We are having trouble confirming your payment. It may already have gone through, so please do not pay again. You will receive an email receipt once it is confirmed; if you do not, please contact us.', 'anudan' )
					: 'We are having trouble confirming your payment. It may already have gone through, so please do not pay again. You will receive an email receipt once it is confirmed; if you do not, please contact us.';

				this.say( waiting );
				// It may not arrive (the site is the thing not answering), but when the site comes back it does.
				api.reportProblem( donationUid, 'status_unreachable', String( response.status ) );
			}

			/*
			 * Staging 0.5.10: a confirmation that took minutes left "This can take a few seconds…" on
			 * screen for ever. After a minute and a half the visible text changes, once, to say
			 * plainly that the wait is longer than usual and a receipt will follow by email.
			 * Polling carries on, so a confirmation that does arrive is still shown.
			 */
			if ( ! longWait && Date.now() - startedAt >= CONFIRMING_PATIENCE_MS ) {
				longWait = true;
				this.renderConfirmingLongWait();
				api.reportProblem( donationUid, 'slow_confirmation', 'webhook_wait' );
			}

			/*
			 * rc6's local:e2e: a gift completed and refunded (Stripe's dispute card) in the same cron pass was never seen as
			 * "completed", and the page waited for ever. Only a completed gift can become refunded (Donations\StatusMachine), so
			 * these say, on the server's word, that the payment went through.
			 */
			if ( response.ok && response.body && [ 'completed', 'refunded', 'partially_refunded' ].includes( response.body.status ) ) {
				updateStitch( this.stitch, this.liveRegion, SHOWN_STEPS, SHOWN_STEPS, true );
				this.renderConfirmation( response.body );

				return;
			}

			if ( response.ok && response.body && [ 'failed', 'cancelled', 'abandoned' ].includes( response.body.status ) ) {
				// The gateway said no. Waiting on would show "confirming" for ever (round 5, LOW-1).
				this.renderPaymentFailed();

				return;
			}

			/*
			 * F11 (rc4): the server asked Stripe itself, and Stripe says the payment is still processing (a bank can take a
			 * while). The donor is told, once, that it is being confirmed and the receipt follows by email, so they may leave.
			 * Polling carries on, so a confirmation that arrives while they stay is still shown.
			 */
			if ( ! beingConfirmed && response.ok && response.body && 'processing' === response.body.confirmation ) {
				beingConfirmed = true;
				this.renderBeingConfirmed();
			}

			window.setTimeout( poll, 3000 );
		};

		poll();
	}

	/**
	 * 0.9.41 (audit T1): the thank-you page's receipt button, made the download.
	 *
	 * @param {HTMLAnchorElement} link
	 * @param {string} url
	 * @return {void}
	 */
	receiptReady( link, url ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

		link.href = url;
		link.removeAttribute( 'aria-disabled' );
		link.removeAttribute( 'role' );
		link.classList.remove( 'anudan-checkout__receipt-download--pending' );
		link.textContent = __( 'Download your receipt' );
	}

	/**
	 * 0.9.41 (audit T1): asks again until the receipt's PDF exists, every 3 seconds for two minutes, then every 15 seconds while
	 * the page is open, and turns the button into the download as soon as it does.
	 *
	 * @param {HTMLAnchorElement} link
	 * @param {string} donationUid
	 * @return {void}
	 */
	awaitReceipt( link, donationUid ) {
		if ( ! donationUid ) {
			return;
		}

		const startedAt = Date.now();
		const ask = async () => {
			if ( ! link.isConnected ) {
				return;
			}

			const response = await api.status( donationUid );

			if ( response.ok && response.body && response.body.receipt_url ) {
				this.receiptReady( link, response.body.receipt_url );

				return;
			}

			window.setTimeout( ask, Date.now() - startedAt < 120000 ? 3000 : 15000 );
		};

		window.setTimeout( ask, 2000 );
	}

	/**
	 * 0.9.41 (replaces R3-5's visible status line): the security check shows only while Cloudflare asks the donor to act, with
	 * one line above its box; it disappears once done. While it works or once it passes, nothing is shown. When it gives up,
	 * the donor is told in plain words below (submitOnce, `noSecurityCheckMessage`) and the next press tries again.
	 *
	 * @param {string} what One of checking, passed, challenge, failed.
	 * @return {void}
	 */
	showSecurityStatus( what ) {
		if ( ! this.securityBox ) {
			return;
		}

		const asking = 'challenge' === what;

		this.securityBox.classList.toggle( 'is-asking', asking );
		this.securityStatus.hidden = ! asking;

		// 0.9.41 rc2: while Cloudflare asks the donor to act, "Getting your secure payment ready." is not what is happening; the box's
		// own line says what to do. It comes back once the box is done and the payment is still being made ready.
		if ( this.preparing ) {
			const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

			this.say( asking ? '' : __( 'Getting your secure payment ready.' ), false, ! asking );
		}

		this.syncDock();
	}

	/**
	 * F11 (rc4): Stripe says the payment is still processing. Never claims completion (CLAUDE.md rule 6).
	 *
	 * @return {void}
	 */
	renderBeingConfirmed() {
		const text = window.wp && window.wp.i18n
			? window.wp.i18n.__( "Your gift is being confirmed. We'll email your receipt shortly.", 'anudan' )
			: "Your gift is being confirmed. We'll email your receipt shortly.";

		const confirming = this.container.querySelector( '.anudan-checkout__confirming' );

		if ( confirming ) {
			confirming.textContent = text;
		}

		this.say( text );
	}

	/**
	 * Replaces the "few seconds" wording once the confirmation has taken longer than that.
	 *
	 * Still never claims completion (CLAUDE.md rule 6): it says the gift is being confirmed, that
	 * nothing more is needed, and that a receipt follows by email once it is.
	 *
	 * @return {void}
	 */
	renderConfirmingLongWait() {
		const text = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'Your gift is still being confirmed. This is taking longer than usual, but there is nothing more you need to do, and please do not pay again. We will email your receipt as soon as it is confirmed.', 'anudan' )
			: 'Your gift is still being confirmed. This is taking longer than usual, but there is nothing more you need to do, and please do not pay again. We will email your receipt as soon as it is confirmed.';

		const confirming = this.container.querySelector( '.anudan-checkout__confirming' );

		if ( confirming ) {
			confirming.textContent = text;
		}

		this.say( text );
	}

	/**
	 * Replaces the "confirming" message once the gateway has refused the payment.
	 *
	 * @return {void}
	 */
	renderPaymentFailed() {
		const text = window.wp && window.wp.i18n
			? window.wp.i18n.__( 'This payment did not go through, and you have not been charged. Please reload the page to try again, or use another way to pay.', 'anudan' )
			: 'This payment did not go through, and you have not been charged. Please reload the page to try again, or use another way to pay.';

		const confirming = this.container.querySelector( '.anudan-checkout__confirming' );

		if ( confirming ) {
			confirming.textContent = text;
			confirming.classList.add( 'anudan-checkout__confirming--failed' );
		}

		this.say( text );
	}

	/**
	 * The honest interim state, shown from the moment a payment is handed to the gateway until
	 * the webhook confirms it. Replaces whatever `paymentContainer` was last showing
	 * ("Loading payment…", or the mounted card element) — the gift has been submitted and there
	 * is nothing left for the donor to do, so this is not another loading spinner beside a form
	 * that no longer does anything.
	 *
	 * **Never says "thank you," "confirmed," or anything a donor could read as done.** That
	 * sentence is `renderConfirmation()`'s alone, and only once the webhook has actually said so
	 * (CLAUDE.md rule 6) — this package's whole reason for existing is not saying it early.
	 *
	 * @return {void}
	 */
	renderConfirming() {
		this.paymentContainer.innerHTML = '';
		this.hidePaymentWallet();

		const message = document.createElement( 'p' );

		message.className = 'anudan-checkout__confirming';
		message.textContent = window.wp && window.wp.i18n
			? window.wp.i18n.__( "We're confirming your gift with your bank. This can take a few seconds…", 'anudan' )
			: "We're confirming your gift with your bank. This can take a few seconds…";

		this.paymentContainer.appendChild( message );

		// Q1 (0.9.40): the payment part is where this is said, also after a wallet payment that never opened it; and nothing that
		// would start a second payment stays on screen: not Back (at the top of the step now), not the other method.
		this.paymentContainer.hidden = false;
		this.paymentArea.hidden = false;
		this.switchMethod.hidden = true;

		if ( this.backButton ) {
			this.backButton.hidden = true;
		}

		const actions = this.stepTwoEl.querySelector( '.anudan-checkout__actions' );

		if ( actions ) {
			actions.hidden = true;
		}
	}

	/**
	 * The confirmation screen itself (`09-PHASE-4-BUILD.md` WP-22) — reached only once
	 * `pollStatus()` has seen `status: "completed"` from the webhook.
	 *
	 * Mirrors the receipt on screen, including the fund name (**G-07**), personalised with the
	 * donor's own first name when the session matched (`status_body()`'s own gate — the same
	 * one that already decided whether a name was in this response at all). Registration
	 * Number and the receipt/portal links are each built only when their key is present in the
	 * response, never rendered as an empty row (**G-08**).
	 *
	 * @param {object} body The completed `/checkout/status` response body.
	 * @return {void}
	 */
	renderConfirmation( body ) {
		this.paymentContainer.innerHTML = '';
		this.hidePaymentWallet();

		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const panel = document.createElement( 'div' );

		panel.className = 'anudan-checkout__confirmation';
		panel.setAttribute( 'data-anudan-confirmation', '' );

		// Q1 (0.9.40), on the thank-you design: a check, "Thank you, Shahid.", what was confirmed, then the gift's facts.
		const mark = document.createElement( 'span' );

		mark.className = 'anudan-checkout__confirmation-mark';
		mark.setAttribute( 'aria-hidden', 'true' );
		mark.textContent = '✓';
		panel.appendChild( mark );

		const [ thanks, sentence ] = this.confirmationParts( body );
		const heading = document.createElement( 'h2' );

		heading.className = 'anudan-checkout__confirmation-heading';
		heading.textContent = thanks;
		panel.appendChild( heading );

		if ( '' !== sentence ) {
			const said = document.createElement( 'p' );

			said.className = 'anudan-checkout__confirmation-sentence';
			// D1 (0.9.41-rc3): the text between the two markers is the "Monthly" badge.
			` ${ sentence }`.split( BADGE_MARK ).forEach( ( part, index ) => {
				said.appendChild( 1 === index % 2 ? frequencyBadge( part ) : document.createTextNode( part ) );
			} );
			panel.appendChild( said );
		}

		const facts = document.createElement( 'dl' );
		const fact = ( label, value ) => {
			if ( ! value ) {
				return;
			}

			const row = document.createElement( 'div' );
			const term = document.createElement( 'dt' );
			const detail = document.createElement( 'dd' );

			term.textContent = label;
			detail.textContent = value;
			row.appendChild( term );
			row.appendChild( detail );
			facts.appendChild( row );
		};

		facts.className = 'anudan-checkout__confirmation-facts';
		fact( __( 'Amount' ), 'number' === typeof body.amount_minor ? this.formatMinorUnits( body.amount_minor, body.currency || 'USD' ) : '' );
		fact( __( 'Fund' ), body.fund_name || '' );
		fact( __( 'Date' ), body.completed_on || '' );
		fact( __( 'How often' ), body.frequency_label || __( 'One-time' ) );
		fact( __( 'Paid with' ), body.paid_with ? body.paid_with.charAt( 0 ).toUpperCase() + body.paid_with.slice( 1 ) : '' );
		fact( __( 'Registration Number' ), body.registration_number || '' );

		if ( facts.childNodes.length > 0 ) {
			panel.appendChild( facts );
		}

		if ( body.frequency_label ) {
			const repeats = document.createElement( 'div' );
			const lead = document.createElement( 'strong' );
			const more = document.createElement( 'span' );

			repeats.className = 'anudan-checkout__note anudan-checkout__note--info anudan-checkout__confirmation-recurring';
			// 0.9.41 (the owner's decision on gift items): a payment to a fund that is not tax-deductible is a payment.
			if ( body.is_payment ) {
				lead.textContent = body.next_payment_on
					? __( 'Your next payment is on %s.' ).replace( '%s', body.next_payment_on )
					: __( 'This payment repeats until you cancel it.' );
			} else {
				lead.textContent = body.next_payment_on
					? __( 'Your next gift is on %s.' ).replace( '%s', body.next_payment_on )
					: __( 'This gift repeats until you cancel it.' );
			}
			more.textContent = __( 'A receipt is emailed for every payment. Change or cancel anytime from your account.' );
			repeats.appendChild( lead );
			repeats.appendChild( more );
			panel.appendChild( repeats );
		}

		/*
		 * 0.9.41 (audit T1): the button is always there. Its PDF is written by the request that completed the gift, just after
		 * that request answers, so the first "completed" answer often comes before it: since F11 (rc4) that request is often this
		 * page's own status poll, which used to stop polling there and leave no button at all. Until the PDF exists the button
		 * says "Preparing your receipt…" and cannot be pressed, and the page keeps asking; it becomes the download once it can.
		 */
		const link = document.createElement( 'a' );

		link.className = 'anudan-checkout__button anudan-checkout__receipt-download';
		panel.appendChild( link );

		if ( body.receipt_url ) {
			this.receiptReady( link, body.receipt_url );
		} else {
			link.classList.add( 'anudan-checkout__receipt-download--pending' );
			link.setAttribute( 'aria-disabled', 'true' );
			link.setAttribute( 'role', 'link' );
			link.textContent = __( 'Preparing your receipt…' );
			this.awaitReceipt( link, body.donation_uid );
		}

		const pending = document.createElement( 'p' );

		pending.className = 'anudan-checkout__receipt-pending';
		pending.textContent = body.donor_email
			? __( 'A receipt is on its way to your email, %s.' ).replace( '%s', body.donor_email )
			: __( 'Your receipt is being prepared and will be emailed to you shortly.' );
		panel.appendChild( pending );

		if ( body.portal_url ) {
			const portalLink = document.createElement( 'a' );

			portalLink.className = 'anudan-checkout__portal-link';
			portalLink.href = body.portal_url;
			portalLink.textContent = body.is_payment ? __( 'See your payments in your account' ) : __( 'See your gifts in your account' );
			panel.appendChild( portalLink );
		}

		// Share this fund: by email, or the page's address copied. Nothing is sent from here.
		if ( ! this.tablet ) {
			const share = document.createElement( 'div' );
			const title = document.createElement( 'p' );
			const row = document.createElement( 'div' );
			const address = `${ window.location.origin }${ window.location.pathname }`;
			const byEmail = document.createElement( 'a' );
			const copy = document.createElement( 'button' );

			share.className = 'anudan-checkout__share';
			title.className = 'anudan-checkout__field-label';
			title.textContent = __( 'Share this fund' );
			row.className = 'anudan-checkout__share-row';
			byEmail.className = 'anudan-checkout__button anudan-checkout__button--secondary';
			byEmail.href = `mailto:?subject=${ encodeURIComponent( body.fund_name || '' ) }&body=${ encodeURIComponent( address ) }`;
			byEmail.textContent = __( 'Share by email' );
			copy.type = 'button';
			copy.className = 'anudan-checkout__button anudan-checkout__button--secondary';
			copy.textContent = __( 'Copy link' );
			copy.addEventListener( 'click', async () => {
				try {
					await window.navigator.clipboard.writeText( address );
					copy.textContent = __( 'Link copied' );
				} catch ( error ) {
					copy.textContent = address;
				}
			} );
			row.appendChild( byEmail );
			row.appendChild( copy );
			share.appendChild( title );
			share.appendChild( row );
			panel.appendChild( share );
		}

		this.paymentContainer.appendChild( panel );

		// Q1 (0.9.40): the thank-you is the whole page: the steps, the summary and the payment's frame fold away (the stylesheet).
		this.container.classList.add( 'is-confirmed' );
		this.syncDock();

		if ( 'function' === typeof panel.scrollIntoView ) {
			panel.scrollIntoView( { block: 'start' } );
		}

		this.liveRegion.textContent = `${ thanks } ${ sentence.split( BADGE_MARK ).join( '' ) }`.trim();
	}

	/**
	 * The thank-you page's two parts: "Thank you, Shahid." and "Your $25.00 monthly gift to the General Fund is confirmed."
	 *
	 * @param {object} body The completed `/checkout/status` response body.
	 * @return {string[]}
	 */
	confirmationParts( body ) {
		const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
		const amount = 'number' === typeof body.amount_minor ? this.formatMinorUnits( body.amount_minor, body.currency || 'USD' ) : '';
		const fund = body.fund_name || '';
		// D1 (0.9.41-rc3): "Monthly" stands beside the amount as a badge; the marker is where {@see confirmationSentence} puts it.
		const often = body.frequency_label ? ` ${ BADGE_MARK }${ String( body.frequency_label ) }${ BADGE_MARK }` : '';
		const thanks = body.donor_first_name ? __( 'Thank you, {name}.' ).replace( '{name}', body.donor_first_name ) : __( 'Thank you.' );

		// 0.9.41 (the owner's decision on gift items): a payment to a fund that is not tax-deductible is a payment, never a gift.
		if ( body.is_payment ) {
			return amount && fund
				? [ thanks, __( 'Your {amount}{often} payment to {fund} is confirmed.' ).replace( '{amount}', amount ).replace( '{often}', often ).replace( '{fund}', fund ) ]
				: [ thanks, __( 'Your payment is confirmed.' ) ];
		}

		if ( amount && fund ) {
			return [ thanks, __( 'Your {amount}{often} gift to {fund} is confirmed.' ).replace( '{amount}', amount ).replace( '{often}', often ).replace( '{fund}', fund ) ];
		}

		return [ thanks, __( 'Your gift is confirmed.' ) ];
	}

	/**
	 * Opens the modal — focus trap engaged, backdrop shown, body scroll locked.
	 *
	 * @param {HTMLElement|null} trigger The button that opened it; focus returns there on close.
	 * @return {void}
	 */
	open( trigger = null ) {
		if ( this.backdrop ) {
			this.backdrop.hidden = false;
		}

		this.container.hidden = false;

		if ( ! this.focusTrap ) {
			this.focusTrap = createFocusTrap( this.container );
		}

		this.focusTrap.activate( trigger );

		this._onKeydown = ( event ) => {
			if ( 'Escape' === event.key ) {
				this.close();
			}
		};

		document.addEventListener( 'keydown', this._onKeydown );
	}

	/**
	 * Closes the modal and returns focus to the trigger.
	 *
	 * @return {void}
	 */
	close() {
		this.container.hidden = true;

		if ( this.backdrop ) {
			this.backdrop.hidden = true;
		}

		if ( this.focusTrap ) {
			this.focusTrap.release();
		}

		if ( this._onKeydown ) {
			document.removeEventListener( 'keydown', this._onKeydown );
		}
	}
}
